import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Check, Info, Clock, Gift, Landmark, Banknote, CreditCard, FileText, Wallet, Home, Users, Building2, MoreHorizontal, Truck, MapPin, Package, Leaf, Construction, Mountain, Trees, Square, MessageCircle, Mail as MailIcon, Facebook, Megaphone, School, UserCheck, Newspaper, Wrench, Palette } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { resolveImageUrl } from '../context/SettingsContext';
import { useCart } from '../context/CartContext';
import AccessoriesPicker from './AccessoriesPicker';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import DatePicker from './DatePicker';
import TimeWindowPicker from './TimeWindowPicker';
import { berlinTodayIso } from '../utils/berlinTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_TYPES = [
  { v: 'private', l: 'private Veranstaltung' },
  { v: 'oeffentliche', l: 'öffentliche Veranstaltung' },
  { v: 'gewerbliche', l: 'gewerbliche Veranstaltung' },
  { v: 'sonstiges', l: 'sonstiges' },
];
const PAYMENT_OPTIONS = [
  { v: 'vorkasse', l: 'Vorkasse per PayPal oder Überweisung' },
  { v: 'bar', l: 'Barzahlung am Anmiettag' },
  { v: 'karte', l: 'Kartenzahlung am Anmiettag (nur bei Selbstabholung)' },
  { v: 'paypal', l: 'PayPal-Zahlung am Anmiettag' },
  { v: 'rechnung', l: 'Rechnungszahlung (NICHT für Privatkunden)' },
];
// Phase 133.83 — Kompakte Icon-Karten für die Zahlungsart. Jedes Item kommt
// mit einem Haupt-Label und einem schmalen Untertext; Zustand per Mehrfach-
// auswahl bleibt wie vor dem Redesign (`form.payment_options` Array).
const PAYMENT_OPTION_CARDS = [
  { v: 'bar', label: 'Barzahlung', sub: 'am Anmiettag', icon: 'cash' },
  { v: 'vorkasse', label: 'Vorkasse', sub: 'Überweisung oder PayPal', icon: 'bank' },
  { v: 'paypal', label: 'PayPal', sub: 'am Anmiettag', icon: 'paypal' },
  { v: 'karte', label: 'Kartenzahlung', sub: 'bei Selbstabholung', icon: 'card' },
  { v: 'rechnung', label: 'Rechnungszahlung', sub: 'nur gewerblich', icon: 'invoice' },
];

const AWARE_OPTIONS = [
  'Google',
  'Facebook/Instagram/Social Media',
  'Empfehlung',
  'Werbung auf Veranstaltungen',
  'Kita/Schule',
  'Bestandskunde',
  'Flyer',
  'Anhänger-Werbung',
  'Sonstiges',
];

const initialForm = {
  customer_name: '',
  company_name: '',
  customer_email: '',
  customer_phone: '',
  customer_street: '',
  customer_zip: '',
  customer_city: '',
  event_date_from: '',
  event_date_to: '',
  event_location_street: '',
  event_location_zip: '',
  event_location_city: '',
  event_location_same_as_billing: true,
  ground_type: '',
  event_type: 'private',
  service_bundle: false,
  // Phase 122S — Admin-Modus: Auf-/Abbau kann geteilt werden.
  // Werte: 'both' (Standard bei service_bundle=true), 'setup_only', 'teardown_only', 'none'.
  bundle_mode: 'none',
  // Admin-Override für den finalen Auf-/Abbau-Preis (auch 0€ möglich).
  bundle_price_override_manual: null,
  // Phase 122S — Individuelle Preise für Aufbau und Abbau (Admin editierbar)
  bundle_setup_price: null,
  bundle_teardown_price: null,
  service_betreuung: false,
  betreuung_hours: 2,
  betreuung_price_override: '',       // Phase 122A — Admin-Override
  service_kinderschminken: false,
  kinderschminken_price_override: null,
  kinderschminken_notes: '',
  delivery_option: 'pickup_lankow',
  cleaning_option: 'kidsevent',
  contact_methods: [],
  payment_options: [],
  aware_through: '',
  notes: '',
  privacy_accepted: false,
  create_account: false,
  preferred_time_window: '',
  return_time_window: '',
  booking_intent: '', // 'binding' | 'inquiry' — Kunde muss aktiv wählen
  referral_code: '',
  // Admin-only: freie Positionen / Rabatte / eigene Produkte
  custom_items: [],
  // Admin-only: Overrides
  delivery_price_override: '',
  total_price_override: '',
  // Phase 125.13 — Admin-Override: Gesamtpreis für die Endreinigung (pauschal).
  cleaning_price_total_override: '',
  // Admin-only: nach Buchungserstellung Bestätigungs-Mail an Kunden versenden?
  notify_customer: false,
  // Phase 128.82 — Fun-Food-Auswahl je Warenkorb-Item, keyed nach product_id
  // `{ [product_id]: { upgrade?: {label, choice, price}, extras: { [label]: qty } } }`
  funfood_selections: {},
  // Phase 125.14 — Admin-only: Buchung sofort als bezahlt markieren
  already_paid: false,
  already_paid_method: 'bar',
};

const fmt = (n) => `${(n || 0).toFixed(2).replace('.', ',')} €`;

// Wochenende/Zusatztag-Zerlegung des inklusiven Zeitraums – spiegelt das Backend.
// Rückgabe: { weekends, outsideDays }. Tage zwischen erstem & letztem WE sind inklusive,
// Tage davor/danach = Zusatztage.
const weekendBreakdown = (from, to) => {
  if (!from) return { weekends: 0, outsideDays: 0 };
  const d1 = new Date(from); d1.setHours(0, 0, 0, 0);
  const d2 = to ? new Date(to) : new Date(from); d2.setHours(0, 0, 0, 0);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return { weekends: 0, outsideDays: 0 };
  let [a, b] = d1 <= d2 ? [d1, d2] : [d2, d1];
  const sats = [];
  const cur = new Date(a);
  while (cur <= b) { if (cur.getDay() === 6) sats.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  const w = sats.length;
  if (w === 0) return { weekends: 0, outsideDays: 0 };
  const DAY = 86400000;
  const firstFri = new Date(sats[0].getTime() - DAY);
  const lastSun = new Date(sats[w - 1].getTime() + DAY);
  const spanStart = a > firstFri ? a : firstFri;
  const spanEnd = b < lastSun ? b : lastSun;
  const inside = Math.round((spanEnd - spanStart) / DAY) + 1;
  const total = Math.round((b - a) / DAY) + 1;
  return { weekends: w, outsideDays: Math.max(0, total - inside) };
};

const countWeekends = (from, to) => weekendBreakdown(from, to).weekends;

// Einzelpreis je Produkt & Mietdauer – spiegelt die Backend-Logik (Staffelpreise / Zusatztag / Wochenenden).
// Phase 133.86 — Preisvariante (price_option) überschreibt die Basis-Preise, nicht aber Staffel-Preise oder
// „ohne Betreuung" (die bleiben Produkt-weit, parallel zur Backend-Logik in `compute_unit_price`).
const computeUnitPrice = (product, days, from = null, to = null, withSupervision = undefined, priceOption = null) => {
  if (!product) return 0;
  const d = Math.max(1, parseInt(days || 1, 10));
  // Betreuungs-Produkt ohne Betreuung: eigenes Staffel-Set bzw. fester Preis (spiegelt Backend).
  const useWithout = product.requires_supervision && withSupervision === false && product.price_without_supervision != null;
  const tiersRaw = useWithout
    ? (Array.isArray(product.tiered_prices_without_supervision) ? product.tiered_prices_without_supervision : [])
    : (Array.isArray(product.tiered_prices) ? product.tiered_prices : []);
  const tieredOn = product.tiered_pricing_enabled && tiersRaw.length > 0;
  const extraOn = product.extra_day_enabled && product.price_per_extra_day != null && product.price_per_extra_day !== '';
  const extra = parseFloat(product.price_per_extra_day) || 0;
  if (tieredOn) {
    const tiers = tiersRaw
      .map((t) => ({ days: parseInt(t.days, 10), price: parseFloat(t.price) }))
      .filter((t) => t.days > 0 && !Number.isNaN(t.price))
      .sort((a, b) => a.days - b.days);
    if (tiers.length) {
      const exact = tiers.find((t) => t.days === d);
      if (exact) return exact.price;
      const lowers = tiers.filter((t) => t.days <= d);
      if (lowers.length) {
        const base = lowers[lowers.length - 1];
        return extraOn ? base.price + (d - base.days) * extra : base.price;
      }
      return tiers[0].price;
    }
  }
  // „ohne Betreuung" ohne eigene Staffel → fester Preis, unabhängig von der Mietdauer.
  if (useWithout) return parseFloat(product.price_without_supervision) || 0;
  // Phase 133.86 — Variante überschreibt price_1_day/price_3_days (nicht useWithout und nicht Tiered)
  const poP1 = priceOption ? parseFloat(priceOption.price_1_day) || 0 : 0;
  const poP3 = priceOption ? parseFloat(priceOption.price_3_days) || poP1 : 0;
  if (extraOn) {
    const p1 = priceOption ? poP1 : (parseFloat(product.price_1_day) || 0);
    const p3 = priceOption ? poP3 : (parseFloat(product.price_3_days) || 0);
    const base3 = p3 || p1;
    // Wochenend-basiert: jedes Kalender-Wochenende = 1× Wochenendpreis, Resttage zum Zusatztag-Preis.
    const weekends = countWeekends(from, to);
    if (weekends >= 2) {
      // Mehrere Wochenenden: je WE 1× Wochenendpreis + Zusatztage vor/nach den Wochenenden.
      const { outsideDays } = weekendBreakdown(from, to);
      return weekends * base3 + outsideDays * extra;
    }
    if (d <= 1) return p1;
    if (d <= 3) return base3;
    return base3 + (d - 3) * extra;
  }
  if (product.single_price) return priceOption ? poP1 : (parseFloat(product.price_1_day) || 0);
  // Phase 122v — mehrtägig (auch 2, 4, 5 Tage) nutzt den 3-Tages-Preis wenn gesetzt.
  if (d === 1) return priceOption ? poP1 : (parseFloat(product.price_1_day) || 0);
  if (priceOption) return poP3 > 0 ? poP3 : poP1;
  const p3 = parseFloat(product.price_3_days) || 0;
  const p1 = parseFloat(product.price_1_day) || 0;
  return p3 > 0 ? p3 : p1;
};

// Standard-Mietdauer beim Hinzufügen (kleinste Staffel, sonst 1 Tag).
const defaultDuration = (product) => {
  if (product?.tiered_pricing_enabled && Array.isArray(product.tiered_prices) && product.tiered_prices.length) {
    const days = product.tiered_prices.map((t) => parseInt(t.days, 10)).filter((n) => n > 0).sort((a, b) => a - b);
    if (days.length) return days[0];
  }
  return 1;
};

// Berechnet Miettage aus dem Datumsbereich (24h-basiert: Rückgabe − Anmiettag
// in Tagen). Konvention: Fr morgens → Mo morgens = 3 Tage (3× 24 h).
const computeDaysFromRange = (from, to) => {
  if (!from) return 1;
  const a = new Date(`${from}T12:00:00`);
  const b = new Date(`${to || from}T12:00:00`);
  const diff = Math.round((b - a) / (1000 * 60 * 60 * 24));
  return Math.max(1, diff || 1);
};

// Passt die berechnete Anzahl Tage an die Preis-Staffelung eines Produkts an.
// Kunden-Konvention: es gibt KEINEN „2-Tage-Preis". 2× 24 h springen immer auf
// die 3-Tage-Staffel hoch (Wochenendpreis). 1 Tag = 1-Tages-Preis, ab 3 Tage
// aufsteigend die jeweils höhere Staffel bzw. Zusatztag-Preis.
const snapDurationForProduct = (product, days) => {
  const d = Math.max(1, Math.floor(days || 1));
  // Regel: nie 2 Tage — springt auf 3 hoch. Gilt für ALLE Preismodelle.
  if (d === 2) return 3;
  if (!product) return d;
  const extraOn = product.extra_day_enabled && product.price_per_extra_day != null && product.price_per_extra_day !== '';
  const tiersRaw = Array.isArray(product.tiered_prices) ? product.tiered_prices : [];
  const tieredOn = product.tiered_pricing_enabled && tiersRaw.length > 0;
  if (extraOn) return d;
  if (tieredOn) {
    const tiers = tiersRaw
      .map((t) => parseInt(t.days, 10))
      .filter((n) => n > 0)
      .sort((a, b) => a - b);
    if (!tiers.length) return d;
    const upper = tiers.find((t) => t >= d);
    return upper || tiers[tiers.length - 1];
  }
  return d;
};

// Stabile Warenkorb-Session-ID (für 30-Min-Hold / Verfügbarkeitsausschluss der eigenen Auswahl)
const ensureCartSessionId = () => {
  let sid = localStorage.getItem('cartSessionId');
  if (!sid) {
    sid = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('cartSessionId', sid);
  }
  return sid;
};

// Multi-Select Dropdown for products (each product max once, no quantity)
const ProductMultiSelect = ({ allProducts, selectedIds, onToggle, unavailableIds = new Set(), heldIds = new Set(), waitlistJoined = {}, onJoinWaitlist, hasDateChosen = false, categoryOrder = {}, adminMode = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  const selectedSet = new Set(selectedIds);
  const count = selectedIds.length;
  const normalizedQuery = query.trim().toLowerCase();
  const filteredProducts = normalizedQuery
    ? allProducts.filter((p) => {
        const haystack = `${p.name || ''} ${p.short_description || ''} ${(p.category_names || []).join(' ')}`.toLowerCase();
        return haystack.includes(normalizedQuery);
      })
    : allProducts;

  // Group products by their (first) category for the accordion view.
  // Category order matches the rest of the site (categories `order` field);
  // unknown categories ("Weitere Produkte") go last. Products inside a group
  // keep the API order (oldest first), so it stays identical everywhere.
  const grouped = useMemo(() => {
    const map = new Map();
    allProducts.forEach((p) => {
      const cat = (p.category_names && p.category_names[0]) || 'Weitere Produkte';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(p);
    });
    const rank = (name) => (categoryOrder[name] !== undefined ? categoryOrder[name] : 9999);
    return Array.from(map.entries()).sort((a, b) => {
      const ra = rank(a[0]);
      const rb = rank(b[0]);
      if (ra !== rb) return ra - rb;
      return a[0].localeCompare(b[0], 'de');
    });
  }, [allProducts, categoryOrder]);
  const [expandedCats, setExpandedCats] = useState(() => new Set());
  const toggleCat = (cat) =>
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });

  const renderRow = (p) => {
    const rawUnavailable = unavailableIds.has(p.id);
    const isHeld = heldIds.has(p.id);
    // Phase 122Y — Im Admin-Modus können auch nicht verfügbare Produkte gebucht
    // werden (Admin hat sich das Produkt evtl. selbst blockiert und will es
    // trotzdem manuell eintragen). Die Warn-Badge bleibt aber sichtbar.
    const isUnavailable = rawUnavailable && !adminMode;
    const showHeldMsg = () => toast.info(
      `„${p.name}" wird gerade von einem anderen Kunden angefragt und ist für kurze Zeit reserviert. Bitte wähle ein anderes Produkt oder einen anderen Termin – falls der Termin wieder frei wird, kannst Du Dich auch auf die Warteliste setzen.`,
      { duration: 7000 }
    );
    return (
      <label
        key={p.id}
        className={`flex items-center gap-3 px-3 py-2.5 border-b border-sky-50 last:border-b-0 ${isUnavailable ? `opacity-70 cursor-not-allowed ${isHeld ? 'bg-amber-50/60' : 'bg-red-50/40'}` : 'hover:bg-sky-50 cursor-pointer'}`}
        data-testid={`bf-product-opt-${p.id}`}
        title={isHeld ? 'Gerade von einem anderen Kunden reserviert' : (isUnavailable ? 'Im gewählten Zeitraum nicht verfügbar' : '')}
        onClick={(e) => {
          if (isUnavailable && !selectedSet.has(p.id)) {
            e.preventDefault();
            if (isHeld) showHeldMsg();
          }
        }}
      >
        <Checkbox
          checked={selectedSet.has(p.id)}
          onCheckedChange={() => {
            if (isUnavailable && !selectedSet.has(p.id)) return;
            onToggle(p);
          }}
          disabled={isUnavailable && !selectedSet.has(p.id)}
        />
        <img
          src={resolveImageUrl(p.image_url, 64)}
          alt={p.name}
          loading="lazy"
          className={`h-10 w-10 rounded-lg object-cover flex-shrink-0 bg-neutral-100 ${isUnavailable ? 'opacity-50' : ''}`}
          data-testid={`bf-product-thumb-${p.id}`}
        />
        <span className={`flex-1 text-sm ${isUnavailable ? 'line-through text-neutral-500' : ''}`}>
          {p.series_variant_name ? (
            <>
              <span className="font-semibold">{p.series_variant_name}</span>
              {p.series_name && (
                <span className="ml-1.5 inline-block text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 rounded-full px-1.5 py-0.5 align-middle">
                  {p.series_name}
                </span>
              )}
            </>
          ) : p.name}
        </span>
        {isHeld ? (
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="text-[10px] uppercase font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid={`bf-product-held-${p.id}`}>Gerade reserviert</span>
            {waitlistJoined[p.id] ? (
              <span className="text-[10px] font-bold text-emerald-700" data-testid={`bf-waitlist-joined-${p.id}`}>✓ Auf Warteliste</span>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onJoinWaitlist && onJoinWaitlist(p); }}
                className="text-[10px] font-bold text-sky-700 underline underline-offset-2 hover:text-sky-900"
                data-testid={`bf-waitlist-btn-${p.id}`}
              >
                Auf Warteliste setzen
              </button>
            )}
          </div>
        ) : rawUnavailable ? (
          // Auch im Admin-Modus die Warn-Badge zeigen, damit klar ist, dass
          // das Produkt eigentlich nicht verfügbar wäre.
          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${adminMode ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-testid={`bf-product-unavailable-${p.id}`}>
            {adminMode ? 'Blockiert · trotzdem buchen möglich' : 'Nicht verfügbar'}
          </span>
        ) : (
          <span className="text-xs text-neutral-500">ab {fmt(p.price_1_day)}</span>
        )}
      </label>
    );
  };

  return (
    <div className="relative" ref={ref} data-testid="bf-products-multiselect">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-white border border-sky-200 hover:border-sky-400 text-left"
        data-testid="bf-products-dropdown"
      >
        <span className="text-sm">
          {count === 0 ? 'Produkte auswählen ...' : `${count} Produkt${count === 1 ? '' : 'e'} ausgewählt`}
        </span>
        <span className={`text-sky-500 transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-sky-200 rounded-xl shadow-xl max-h-80 overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-sky-100 p-2 z-10">
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Produkt suchen..."
                className="w-full text-sm pl-8 pr-8 py-2 rounded-lg border border-sky-200 focus:border-sky-400 focus:outline-none"
                data-testid="bf-products-search"
                autoFocus
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sky-400 text-sm pointer-events-none">🔍</span>
              {query && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setQuery(''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 text-sm"
                  aria-label="Suche leeren"
                  data-testid="bf-products-search-clear"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          {allProducts.length === 0 && (
            <p className="text-sm text-neutral-500 p-3">Keine Produkte verfügbar.</p>
          )}
          {/* Search active → flat results; otherwise category accordion (collapsed) */}
          {allProducts.length > 0 && normalizedQuery ? (
            filteredProducts.length === 0 ? (
              <p className="text-sm text-neutral-500 p-3" data-testid="bf-products-no-results">
                Keine Treffer für „{query}&ldquo;.
              </p>
            ) : (
              filteredProducts.map(renderRow)
            )
          ) : (
            allProducts.length > 0 &&
            grouped.map(([cat, prods]) => {
              const isExp = expandedCats.has(cat);
              const selInCat = prods.filter((p) => selectedSet.has(p.id)).length;
              return (
                <div key={cat} className="border-b border-sky-100 last:border-b-0" data-testid={`bf-cat-${cat}`}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleCat(cat); }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-sky-50/60 hover:bg-sky-100/70 text-left font-semibold text-sm text-sky-800"
                    data-testid={`bf-cat-toggle-${cat}`}
                  >
                    <span className="flex items-center gap-2">
                      {cat}
                      {selInCat > 0 && (
                        <span className="text-[10px] font-bold bg-sky-600 text-white px-1.5 py-0.5 rounded-full">{selInCat}</span>
                      )}
                    </span>
                    <span className="flex items-center gap-2 text-neutral-500 font-normal text-xs">
                      {prods.length}
                      <span className={`transition-transform ${isExp ? 'rotate-180' : ''}`}>▾</span>
                    </span>
                  </button>
                  {isExp && prods.map(renderRow)}
                </div>
              );
            })
          )}
          {!hasDateChosen && (
            <p className="text-[11px] text-neutral-500 italic p-3 bg-sky-50/40 border-t border-sky-50">
              Tipp: Bitte zuerst Mietzeitraum oben wählen, damit wir die Verfügbarkeit prüfen können.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Phase 133.83 — Kompakter, nummerierter Section-Wrapper. Ersetzt die alten
// großen Section-Überschriften mit einem schlanken Badge + Titel in einer Zeile.
// Die Nummer wird automatisch aus dem Titel geparst, wenn er mit "N." beginnt
// (z. B. "1. Deine Kontaktdaten"); sonst kann via `number`-Prop überschrieben
// werden. Weniger vertikale Höhe, modernes Checkout-Feeling.
const Section = ({ title, required, children, testId, number }) => {
  let n = number;
  let cleanTitle = title;
  if (n == null && typeof title === 'string') {
    const m = title.match(/^(\d+)\.\s*(.+)$/);
    if (m) { n = Number(m[1]); cleanTitle = m[2]; }
  }
  return (
    <section data-testid={testId} className="space-y-2.5">
      <div className="flex items-center gap-2.5 pt-1">
        {n != null && (
          <div className="shrink-0 w-7 h-7 rounded-full bg-sky-100 text-sky-700 font-bold text-sm flex items-center justify-center">
            {n}
          </div>
        )}
        <h3 className="text-base md:text-lg font-bold text-sky-800 leading-tight">
          {cleanTitle}{required && <span className="text-red-500 ml-1">*</span>}
        </h3>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
};

// Phase 128.82/84 — Konvertiert die Fun-Food-Auswahlen in `custom_items`, damit
// Upgrade-Aufpreis + Extra-Beträge automatisch in Preisberechnung, Rechnung,
// Bestätigungsmail und Statistik einfließen. Präfix „🍭" macht die Zeilen in
// Rechnung/Mail/Vertrag klar als Fun-Food-Zutat kenntlich.
const funfoodToCustomItems = (selections, itemsList) => {
  const out = [];
  for (const it of itemsList || []) {
    const p = it.product;
    const pid = p?.id;
    if (!pid) continue;
    const sel = selections?.[pid];
    if (!sel) continue;
    const qtyItem = Number(it.quantity) || 1;
    if (sel.upgrade && Number(sel.upgrade.price) > 0) {
      out.push({
        label: `🍭 ${p.name}: ${sel.upgrade.label}${sel.upgrade.choice ? ` (${sel.upgrade.choice})` : ''}`,
        kind: 'custom_extra',
        amount: Number(sel.upgrade.price) * qtyItem,
      });
    }
    const extrasCfg = Array.isArray(p.funfood_extras) ? p.funfood_extras : [];
    for (const [label, qty] of Object.entries(sel.extras || {})) {
      const q = Number(qty) || 0;
      if (q <= 0) continue;
      const cfg = extrasCfg.find((e) => e.label === label);
      const price = Number(cfg?.price || 0);
      if (price <= 0) continue;
      const unit = cfg?.unit ? ` ${cfg.unit}` : '';
      out.push({
        label: `🍭 ${p.name}: ${label} · ${q}×${unit}`,
        kind: 'custom_extra',
        amount: price * q,
      });
    }
  }
  return out;
};

const FunFoodSelector = ({ items, selections, onChange, testId = 'funfood-selector' }) => {
  // Nur Items zeigen, die funfood-Konfiguration haben.
  const enriched = (items || [])
    .map((it) => ({
      it,
      hasUp: Array.isArray(it.product?.funfood_upgrades) && it.product.funfood_upgrades.length > 0,
      hasEx: Array.isArray(it.product?.funfood_extras) && it.product.funfood_extras.length > 0,
      hasInc: Array.isArray(it.product?.funfood_included) && it.product.funfood_included.length > 0,
    }))
    .filter((r) => r.hasUp || r.hasEx || r.hasInc);
  if (enriched.length === 0) return null;
  const set = (pid, patch) => {
    const cur = selections?.[pid] || { upgrade: null, extras: {} };
    onChange({ ...selections, [pid]: { ...cur, ...patch } });
  };
  return (
    <Section title="Zutaten & Extras" testId={testId}>
      <p className="text-xs text-neutral-600 -mt-2">Für Fun-Food-Maschinen: passe die inklusiven Zutaten an oder buche Extras dazu.</p>
      <div className="space-y-3">
        {enriched.map(({ it, hasUp, hasEx, hasInc }) => {
          const p = it.product;
          const sel = selections?.[p.id] || { upgrade: null, extras: {} };
          return (
            <div key={p.id} className="rounded-2xl bg-gradient-to-br from-pink-50/60 to-amber-50/60 border border-pink-200 p-2.5 space-y-2" data-testid={`ff-item-${p.id}`}>
              <div className="text-sm font-bold text-neutral-900 flex items-center gap-1.5">🍭 {p.name}</div>

              {hasInc && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-2.5 py-1.5 text-[12px] text-emerald-900">
                  <span className="font-bold text-emerald-700 uppercase text-[10px] tracking-wider">Inklusive:</span> {p.funfood_included.map((x) => x.label).join(' · ')}
                </div>
              )}

              {hasUp && (
                <div className="space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Zutaten wählen</div>
                  <label className={`flex items-center gap-2 rounded-lg border cursor-pointer px-2.5 py-1.5 ${!sel.upgrade ? 'border-sky-500 bg-sky-50' : 'border-neutral-200 bg-white hover:border-neutral-300'}`}>
                    <input
                      type="radio"
                      name={`ff-upgrade-${p.id}`}
                      className="accent-sky-600"
                      checked={!sel.upgrade}
                      onChange={() => set(p.id, { upgrade: null })}
                      data-testid={`ff-upgrade-radio-none-${p.id}`}
                    />
                    <span className="text-[13px] font-semibold flex-1">Normal (inklusive)</span>
                    <span className="text-[11px] text-emerald-700 font-bold">0,00 €</span>
                  </label>
                  {p.funfood_upgrades.map((up, idx) => {
                    const active = sel.upgrade?.label === up.label;
                    return (
                      <div key={idx} className={`rounded-lg border ${active ? 'border-amber-500 bg-amber-50' : 'border-neutral-200 bg-white'}`}>
                        <label className="flex items-center gap-2 cursor-pointer px-2.5 py-1.5">
                          <input
                            type="radio"
                            name={`ff-upgrade-${p.id}`}
                            className="accent-amber-600"
                            checked={active}
                            onChange={() => set(p.id, { upgrade: { label: up.label, price: Number(up.price) || 0, choice: (up.choices && up.choices[0]) || null } })}
                            data-testid={`ff-upgrade-radio-${p.id}-${idx}`}
                          />
                          <span className="text-[13px] font-semibold flex-1">⬆ {up.label}</span>
                          <span className="text-[11px] text-amber-700 font-bold">+{Number(up.price || 0).toFixed(2)} €</span>
                        </label>
                        {active && Array.isArray(up.choices) && up.choices.length > 0 && (
                          <div className="px-2.5 pb-1.5">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-amber-700 block mb-0.5">Geschmack wählen</label>
                            <select
                              value={sel.upgrade.choice || up.choices[0]}
                              onChange={(e) => set(p.id, { upgrade: { ...sel.upgrade, choice: e.target.value } })}
                              className="w-full rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-[13px]"
                              data-testid={`ff-upgrade-choice-${p.id}`}
                            >
                              {up.choices.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {hasEx && (
                <div className="space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-sky-800">Extras dazubuchen</div>
                  <div className={`grid gap-1 ${p.funfood_extras.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
                  {p.funfood_extras.map((ex, idx) => {
                    const qty = Number(sel.extras?.[ex.label] || 0);
                    const max = Number(ex.max_qty || 99);
                    const setQty = (q) => {
                      const clamped = Math.max(0, Math.min(max, q));
                      const nextExtras = { ...(sel.extras || {}) };
                      if (clamped <= 0) delete nextExtras[ex.label];
                      else nextExtras[ex.label] = clamped;
                      set(p.id, { extras: nextExtras });
                    };
                    const isActive = qty > 0;
                    return (
                      <div key={idx} className={`rounded-lg border px-2.5 py-1.5 ${isActive ? 'border-sky-400 bg-sky-50/60' : 'border-neutral-200 bg-white'}`} data-testid={`ff-extra-${p.id}-${idx}`}>
                        <div className="text-[13px] font-semibold text-neutral-900 leading-tight break-words">＋ {ex.label}</div>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <div className="text-[11px] text-neutral-500 leading-tight">
                            {Number(ex.price || 0).toFixed(2)} €{ex.unit ? ` / ${ex.unit}` : ''}
                            {isActive && (
                              <span className="ml-1 font-bold text-sky-800 tabular-nums">
                                = {(qty * Number(ex.price || 0)).toFixed(2)} €
                              </span>
                            )}
                          </div>
                          <div className="inline-flex items-center rounded-full bg-white border border-sky-300 shadow-sm shrink-0">
                            <button type="button" onClick={() => setQty(qty - 1)} disabled={qty <= 0}
                              className="h-7 w-7 rounded-full text-sky-700 hover:bg-sky-50 disabled:opacity-30 text-sm leading-none"
                              data-testid={`ff-extra-dec-${p.id}-${idx}`}>−</button>
                            <span className="min-w-[1.1rem] text-center text-sm font-bold tabular-nums" data-testid={`ff-extra-qty-${p.id}-${idx}`}>{qty}</span>
                            <button type="button" onClick={() => setQty(qty + 1)} disabled={qty >= max}
                              className="h-7 w-7 rounded-full text-sky-700 hover:bg-sky-50 disabled:opacity-30 text-sm leading-none"
                              data-testid={`ff-extra-inc-${p.id}-${idx}`}>+</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const RadioRow = ({ name, value, current, onChange, label, price, testId, badge }) => (
  <label className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-sky-100 hover:bg-sky-50 hover:border-sky-200 cursor-pointer transition-colors" data-testid={testId}>
    <input
      type="radio"
      name={name}
      value={value}
      checked={current === value}
      onChange={() => onChange(value)}
      className="h-4 w-4 accent-sky-500"
    />
    <span className="flex-1 text-sm">
      {label}
      {badge && (
        <span className="ml-2 inline-block text-[10px] font-bold uppercase bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full align-middle">{badge}</span>
      )}
    </span>
    {typeof price === 'number' && price > 0 && (
      <span className="font-bold text-sky-700 text-sm">+{fmt(price)}</span>
    )}
    {price === 0 && <span className="text-xs text-emerald-600 font-semibold">kostenlos</span>}
  </label>
);

const BookingForm = ({
  initialItems = [],
  initialDate = null,
  initialDateTo = null,
  initialFunfoodSelections = null,
  // Phase 133.82 — Zusatzleistungen aus der Produktseite (Lieferung, Auf-/Abbau)
  // werden hier vorbelegt. Shape: { hasDelivery, deliveryAddress, deliveryPriceOverride, hasSetup }.
  initialAddons = null,
  onSuccess,
  // Phase 122z — Admin-Modus: das öffentliche BookingForm wird auch in der
  // manuellen Buchung des Admin-Panels eingesetzt.
  adminMode: adminModeProp = false,
  prefillCustomer = null,
  // Phase 122M — Edit-Modus: bestehende Buchung hydratisieren + PUT statt POST
  editBooking = null,
  // Phase 128.52 — Offer-Modus: das gleiche Formular wie eine Buchung, aber
  // Absenden legt ein Angebot an (kein Kalender-/Bestands-Check, keine Kunden-
  // Bestätigungsmail). `offerFields` enthaelt Angebots-spezifische Felder
  // (intro_text, offer_notes, valid_days), `onPayloadChange` erhaelt bei jeder
  // Formular-Aenderung das aktuelle Payload-Dict (fuer Live-PDF-Vorschau).
  offerMode = false,
  offerInitial = null,
  onPayloadChange = null,
}) => {
  // Offer-Modus wird intern wie Admin-Modus behandelt (Overrides, keine
  // Kalender-/Bestands-Blockaden, kein Cart-Hold …). Nur beim Submit unterscheiden
  // wir: adminMode → Buchung anlegen, offerMode → Angebot anlegen.
  const adminMode = adminModeProp || offerMode;
  const { settings } = useSettings();
  const { clearCart, accessories, getAccessoriesTotal, removeAllByProduct, addToCart, replaceAccessories, snapshotCart, restoreCartSnapshot } = useCart();

  // Phase 122S — Admin-Modus: Warenkorb des öffentlichen Nutzers vor dem
  // Öffnen des Editors sichern und beim Verlassen wiederherstellen. Sonst
  // hydratisieren wir editBooking-Items via `addToCart` in den globalen
  // Cart (localStorage-Persist!) und der Kunde/Admin sieht diese Items
  // hinterher im öffentlichen Warenkorb.
  const adminCartSnapshotRef = React.useRef(null);
  useEffect(() => {
    if (!adminMode) return undefined;
    adminCartSnapshotRef.current = snapshotCart();
    return () => {
      if (adminCartSnapshotRef.current) {
        restoreCartSnapshot(adminCartSnapshotRef.current);
        adminCartSnapshotRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMode]);
  const { user: customerUser } = useCustomerAuth();
  const navigate = useNavigate();
  const pricing = settings?.pricing || {};
  // Admin-configurable required/optional state per form field (Pflicht/Optional)
  const reqCfg = settings?.inquiry_form?.required || {};
  const FIELD_REQ_DEFAULTS = {
    customer_phone: true,
    customer_street: true, customer_zip: true, customer_city: true,
    event_location_street: true, event_location_zip: true, event_location_city: true,
    ground_type: true, event_type: true,
    aware_through: false, notes: false,
  };
  const isReq = (k) => (reqCfg[k] !== undefined ? !!reqCfg[k] : (FIELD_REQ_DEFAULTS[k] ?? true));
  const star = (k) => (isReq(k) ? ' *' : '');
  const [bonusCard, setBonusCard] = useState(null);
  const [referralValidation, setReferralValidation] = useState(null); // {valid, reason, discount_eur, referrer_name}

  // Sync form fields from customerUser whenever it becomes available or changes
  // (e.g. after profile edit / login during navigation).
  useEffect(() => {
    if (adminMode) return;                              // im Admin-Modus keine Login-Sync
    if (!customerUser) return;
    setForm((prev) => ({
      ...prev,
      customer_name: prev.customer_name || customerUser.name || '',
      customer_email: prev.customer_email || customerUser.email || '',
      customer_phone: prev.customer_phone || customerUser.phone || '',
      customer_street: prev.customer_street || customerUser.street || '',
      customer_zip: prev.customer_zip || customerUser.zip || '',
      customer_city: prev.customer_city || customerUser.city || '',
    }));
  }, [customerUser, adminMode]);

  // Phase 122z — Prefill aus einem Admin-Kundenkonto (bei manueller Buchung)
  useEffect(() => {
    if (!adminMode || !prefillCustomer) return;
    setForm((prev) => ({
      ...prev,
      customer_name: prefillCustomer.name || prefillCustomer.customer_name || prev.customer_name || '',
      customer_email: prefillCustomer.email || prefillCustomer.customer_email || prev.customer_email || '',
      customer_phone: prefillCustomer.phone || prefillCustomer.customer_phone || prev.customer_phone || '',
      customer_street: prefillCustomer.street || prefillCustomer.customer_street || prev.customer_street || '',
      customer_zip: prefillCustomer.zip || prefillCustomer.customer_zip || prev.customer_zip || '',
      customer_city: prefillCustomer.city || prefillCustomer.customer_city || prev.customer_city || '',
      company_name: prefillCustomer.company_name || prev.company_name || '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMode, prefillCustomer]);

  useEffect(() => {
    if (!customerUser) {
      setBonusCard(null);
      return;
    }
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/bonus-card-v2`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setBonusCard(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [customerUser]);
  const [form, setForm] = useState(() => {
    // Phase 128.52 — Offer-Modus: aus `offerInitial.booking_snapshot` hydratisieren.
    const snap = offerInitial?.booking_snapshot || null;
    const hydrateSrc = editBooking || snap || null;
    const billingStreetFromOffer = offerInitial?.billing_street || snap?.customer_street || '';
    const billingZipFromOffer = offerInitial?.billing_zip || snap?.customer_zip || '';
    const billingCityFromOffer = offerInitial?.billing_city || snap?.customer_city || '';
    return {
    ...initialForm,
    // Fun-Food-Auswahl (auf der Produktseite gewählt) vorbelegen.
    funfood_selections: initialFunfoodSelections && Object.keys(initialFunfoodSelections).length > 0
      ? initialFunfoodSelections
      : (initialForm.funfood_selections || {}),
    customer_name: editBooking?.customer_name || offerInitial?.customer_name || customerUser?.name || '',
    customer_email: editBooking?.customer_email || offerInitial?.customer_email || customerUser?.email || '',
    customer_phone: editBooking?.customer_phone || offerInitial?.customer_phone || customerUser?.phone || '',
    customer_street: editBooking?.customer_street || billingStreetFromOffer || customerUser?.street || '',
    customer_zip: editBooking?.customer_zip || billingZipFromOffer || customerUser?.zip || '',
    customer_city: editBooking?.customer_city || billingCityFromOffer || customerUser?.city || '',
    event_date_from: editBooking?.event_date_from || offerInitial?.event_date_from || initialDate || '',
    event_date_to: editBooking?.event_date_to || offerInitial?.event_date_to || initialDateTo || initialDate || '',
    // Phase 122M — hydratisiere weitere Booking-Felder im Edit-Modus (bzw. Offer-Snapshot)
    ...(hydrateSrc ? {
      company_name: hydrateSrc.company_name || '',
      preferred_time_window: hydrateSrc.preferred_time_window || '',
      ground_type: hydrateSrc.ground_type || '',
      event_location_street: hydrateSrc.event_location_street || '',
      event_location_zip: hydrateSrc.event_location_zip || '',
      event_location_city: hydrateSrc.event_location_city || '',
      event_location_same_as_billing: hydrateSrc.event_location_same_as_billing ?? true,
      event_type: hydrateSrc.event_type || '',
      service_bundle: !!hydrateSrc.service_bundle,
      // Phase 122S — bundle_mode aus editBooking übernehmen; legacy: falls
      // service_bundle=true aber kein bundle_mode gespeichert → 'both'.
      bundle_mode: hydrateSrc.bundle_mode || (hydrateSrc.service_bundle ? 'both' : 'none'),
      bundle_price_override_manual: hydrateSrc.bundle_price_override_manual ?? null,
      bundle_setup_price: hydrateSrc.bundle_setup_price ?? null,
      bundle_teardown_price: hydrateSrc.bundle_teardown_price ?? null,
      service_betreuung: !!hydrateSrc.service_betreuung,
      betreuung_hours: hydrateSrc.betreuung_hours || 0,
      service_kinderschminken: !!hydrateSrc.service_kinderschminken,
      kinderschminken_price_override: hydrateSrc.kinderschminken_price_override ?? null,
      kinderschminken_notes: hydrateSrc.kinderschminken_notes ?? '',
      service_stempelpass: !!hydrateSrc.service_stempelpass,
      cleaning_option: hydrateSrc.cleaning_option || 'eigenreinigung',
      delivery_option: hydrateSrc.delivery_option || 'pickup_lankow',
      delivery_distance_km: hydrateSrc.delivery_distance_km || 0,
      contact_methods: hydrateSrc.contact_methods || (hydrateSrc.contact_method ? [hydrateSrc.contact_method] : []),
      contact_method: hydrateSrc.contact_method || '',
      payment_options: hydrateSrc.payment_options || [],
      custom_items: hydrateSrc.custom_items || [],
      delivery_price_override: hydrateSrc.delivery_price_override ?? '',
      total_price_override: hydrateSrc.total_price_override ?? '',
      cleaning_price_total_override: hydrateSrc.cleaning_price_total_override ?? '',
      // Datenschutz + Intent im Edit-Modus vorausgefüllt (werden im adminMode ausgeblendet)
      privacy_accepted: true,
      booking_intent: hydrateSrc.booking_intent || 'binding',
      notify_customer: false,
    } : {}),
  };
  });
  // Phase 128.52 — Angebots-spezifische Felder (intro, notes, valid_days)
  const [offerFields, setOfferFields] = useState({
    intro_text: offerInitial?.intro_text || '',
    offer_notes: offerInitial?.notes || '',
    valid_days: offerInitial?.valid_days || 14,
  });
  // Lieferung durch uns? → Veranstaltungsort (PLZ+Ort) erforderlich. Straße bleibt immer optional.
  const deliveryByUs = (form.delivery_option || '').startsWith('delivery');
  const locStar = (k) => (deliveryByUs && (k === 'event_location_zip' || k === 'event_location_city') ? ' *' : '');
  const [items, setItems] = useState(() => {
    // Phase 128.52 — Offer-Modus: aus `offerInitial.booking_snapshot.items`
    const snapItems = offerInitial?.booking_snapshot?.items;
    const src = editBooking?.items?.length ? editBooking.items : (snapItems?.length ? snapItems : null);
    if (src) {
      return src.map((it) => ({
        product: null,  // resolved from allProducts once loaded
        product_id: it.product_id,
        product_name: it.product_name,
        quantity: it.quantity || 1,
        rental_duration: it.rental_duration || 1,
        with_supervision: it.with_supervision,
        supervision_hours: it.supervision_hours,
      }));
    }
    return initialItems;
  });
  const [allProducts, setAllProducts] = useState([]);
  const [categoryOrder, setCategoryOrder] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState({});
  const [specialDates, setSpecialDates] = useState([]);
  const [showReferralInput, setShowReferralInput] = useState(false);
  // Phase 133.84 — Firmenname-Toggle. True wenn der User bewusst auf
  // "Buchung für Firma" tippt ODER wenn beim Hydrieren bereits ein Firmen-
  // name im Formular steht (Edit, Prefill, Admin-Buchung).
  const [companyEnabled, setCompanyEnabled] = useState(false);
  useEffect(() => {
    if (form.company_name && !companyEnabled) setCompanyEnabled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.company_name]);
  // Phase 90 — gesammelte fehlende Felder für den Kunden sichtbar machen (statt nur einem Toast).
  const [missingFields, setMissingFields] = useState([]);
  const missingPanelRef = React.useRef(null);

  // ============ WIZARD (Phase 126) ============
  // Interaktiver Schritt-für-Schritt-Modus für öffentliche Mietanfrage.
  // Deaktiviert im Admin/Edit-Modus — dort wird das Formular komplett offen gezeigt.
  const wizardCapable = !adminMode && !editBooking;
  const WIZARD_PREF_KEY = 'ke_booking_wizard_pref_v1';
  const [useWizard, setUseWizard] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const v = window.localStorage.getItem(WIZARD_PREF_KEY);
      return v === null ? true : v === '1';
    } catch { return true; }
  });
  const wizardMode = wizardCapable && useWizard;
  const toggleWizard = () => {
    const next = !useWizard;
    setUseWizard(next);
    try { window.localStorage.setItem(WIZARD_PREF_KEY, next ? '1' : '0'); } catch { /* noop */ }
  };
  const WIZARD_STEPS = [
    { key: 'contact',    title: 'Kontakt' },
    { key: 'schedule',   title: 'Termin & Ort' },
    { key: 'products',   title: 'Produkte' },
    { key: 'details',    title: 'Details' },
    { key: 'summary',    title: 'Absenden' },
  ];
  const WIZARD_DRAFT_KEY = 'ke_booking_wizard_draft_v1';
  const [currentStep, setCurrentStep] = useState(0);
  const [stepError, setStepError] = useState('');
  const [draftRestorePrompt, setDraftRestorePrompt] = useState(null); // {snapshot, timestamp}
  // Phase 128.17 — Confirm-Modal für Mietvertrags-Behandlung beim Bearbeiten
  const [contractDecisionPending, setContractDecisionPending] = useState(null); // { payload, event }
  // Phase 128.8 — Verhindert, dass der Save-Effect nach erfolgreichem Absenden
  // erneut feuert (currentStep > 0 macht hasContent sonst weiter truthy und der
  // gerade gelöschte Entwurf wird sofort wieder geschrieben).
  const wizardSubmittedRef = useRef(false);

  // ============ WIZARD LOCAL STORAGE PERSISTENCE ============
  // Auf öffentlicher Mietanfrage: Formulardaten in localStorage sichern,
  // damit der Nutzer später fortfahren kann.
  useEffect(() => {
    if (!wizardMode) return;
    try {
      if (sessionStorage.getItem('kidsevent_ai_handover')) {
        window.localStorage.removeItem(WIZARD_DRAFT_KEY);
        return;
      }
      const raw = window.localStorage.getItem(WIZARD_DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.form && parsed.timestamp) {
        const ageDays = (Date.now() - parsed.timestamp) / (1000 * 60 * 60 * 24);
        if (ageDays < 14) {
          setDraftRestorePrompt(parsed);
        } else {
          window.localStorage.removeItem(WIZARD_DRAFT_KEY);
        }
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardMode]);

  // Speichert bei Änderungen automatisch (nur öffentlich, nach Interaktion mit Schritt > 0 oder Feld gefüllt)
  useEffect(() => {
    if (!wizardMode) return;
    // Phase 128.8 — Nach erfolgreichem Absenden nicht mehr speichern (sonst
    // erscheint der "Entwurf fortsetzen"-Prompt beim nächsten Besuch).
    if (wizardSubmittedRef.current) return;
    const hasContent = form.customer_name || form.customer_email || form.event_date_from || currentStep > 0;
    if (!hasContent) return;
    try {
      const payload = { form, currentStep, timestamp: Date.now() };
      window.localStorage.setItem(WIZARD_DRAFT_KEY, JSON.stringify(payload));
    } catch { /* ignore quota errors */ }
  }, [wizardMode, form, currentStep]);

  const restoreDraft = () => {
    if (!draftRestorePrompt) return;
    setForm((prev) => ({ ...prev, ...draftRestorePrompt.form }));
    setCurrentStep(Math.min(draftRestorePrompt.currentStep || 0, WIZARD_STEPS.length - 1));
    setDraftRestorePrompt(null);
    toast.success('Entwurf wiederhergestellt — du kannst dort weitermachen, wo du aufgehört hast.');
  };
  const discardDraft = () => {
    try { window.localStorage.removeItem(WIZARD_DRAFT_KEY); } catch { /* ignore */ }
    setDraftRestorePrompt(null);
  };

  // ============ WIZARD STEP VALIDATION ============
  const validateStep = (stepIdx) => {
    if (!wizardMode) return { ok: true };
    const missing = [];
    if (stepIdx === 0) {
      if (!form.customer_name?.trim()) missing.push('Name');
      if (!form.customer_email?.trim()) missing.push('E-Mail');
      if (isReq('customer_phone') && !form.customer_phone?.trim()) missing.push('Telefon');
      if (isReq('customer_street') && !form.customer_street?.trim()) missing.push('Straße & Hausnummer');
      if (isReq('customer_zip') && !form.customer_zip?.trim()) missing.push('PLZ');
      if (isReq('customer_city') && !form.customer_city?.trim()) missing.push('Ort');
    } else if (stepIdx === 1) {
      if (!form.event_date_from) missing.push('Anmiettag');
      if (!form.event_date_to) missing.push('Rückgabe-Datum');
      if (isReq('event_type') && !form.event_type) missing.push('Veranstaltungsart');
      if (!form.event_location_same_as_billing) {
        if (isReq('event_location_zip') && !form.event_location_zip?.trim()) missing.push('Event-PLZ');
        if (isReq('event_location_city') && !form.event_location_city?.trim()) missing.push('Event-Ort');
      }
    } else if (stepIdx === 2) {
      const hasAnyProduct = items.some((it) => it.product);
      if (!hasAnyProduct) missing.push('Mindestens 1 Produkt');
      if (isReq('ground_type') && !form.ground_type?.trim()) missing.push('Untergrund');
    } else if (stepIdx === 3) {
      if (!form.delivery_option) missing.push('Lieferung / Abholung');
      if (!form.cleaning_option) missing.push('Reinigungs-Option');
      const contactAny = (form.contact_methods || []).length > 0;
      if (!contactAny) missing.push('Kontaktaufnahme (WhatsApp oder E-Mail)');
    }
    return missing.length === 0 ? { ok: true } : { ok: false, missing };
  };

  const goNext = () => {
    const res = validateStep(currentStep);
    if (!res.ok) {
      setStepError(`Bitte ergänze noch: ${res.missing.join(', ')}`);
      toast.error(`Bitte ergänze noch: ${res.missing.join(', ')}`);
      return;
    }
    setStepError('');
    setCurrentStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goPrev = () => {
    setStepError('');
    setCurrentStep((s) => Math.max(s - 1, 0));
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const jumpToStep = (idx) => {
    if (idx < currentStep) {
      setStepError('');
      setCurrentStep(idx);
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Auto-fill referral code from URL ?ref=KIDS-XXXX
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) {
        setForm((prev) => (prev.referral_code ? prev : { ...prev, referral_code: ref.toUpperCase() }));
        setShowReferralInput(true);
      }
    } catch { /* ignore */ }
  }, []);

  // Debounced referral code validation
  useEffect(() => {
    const code = (form?.referral_code || '').trim().toUpperCase();
    if (!code || code.length < 4) {
      setReferralValidation(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/referral/validate`, {
          code,
          email: form.customer_email || customerUser?.email || '',
        });
        if (!cancelled) setReferralValidation(res.data);
      } catch {
        if (!cancelled) setReferralValidation({ valid: false, reason: 'Validierung fehlgeschlagen' });
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [form?.referral_code, form?.customer_email, customerUser?.email]);

  useEffect(() => {
    axios.get(`${API}/products`).then((res) => {
      // Coming-Soon-Produkte dürfen im Mietanfrage/Buchungs-Auswahllayout NICHT
      // auftauchen (Teaser-Karte, kein echter Preis, nicht buchbar).
      const list = (res.data || []).filter((p) => p.coming_soon !== true);
      setAllProducts(list);
    }).catch(() => {});
    axios.get(`${API}/special-dates`).then((res) => setSpecialDates(res.data?.items || [])).catch(() => {});
    axios.get(`${API}/categories`).then((res) => {
      const map = {};
      (res.data || []).forEach((c, i) => {
        if (c && c.name) map[c.name] = (c.order !== undefined && c.order !== null) ? c.order : i;
      });
      setCategoryOrder(map);
    }).catch(() => {});
  }, []);

  // Phase 122M — Edit-Modus: sobald allProducts geladen ist, `product`-Referenz für alle Items auflösen
  // Phase 128.52-fix — auch im Offer-Modus (`offerInitial`), sonst bleiben Items nach Wieder-Öffnen leer
  useEffect(() => {
    if (!(editBooking || offerInitial) || allProducts.length === 0) return;
    setItems((prev) => prev.map((it) => {
      if (it.product) return it;
      const p = allProducts.find((x) => x.id === it.product_id);
      return p ? { ...it, product: p } : it;
    }));
  }, [allProducts, editBooking, offerInitial]);

  // Phase 122S-fix — Edit-Modus: die auf der Buchung gespeicherten Zubehör-
  // Positionen einmalig in den Cart-Context hydratisieren, damit der Admin
  // sie im `AccessoriesPicker` sehen UND entfernen/ändern kann. Ohne diese
  // Hydration blieb `cart.accessories = []`, der Admin konnte nichts
  // entfernen, und beim Speichern wurde stur die alte Liste zurückgesendet.
  const accHydratedRef = React.useRef(false);
  useEffect(() => {
    if (accHydratedRef.current) return;
    // Phase 128.52 — Offer-Modus: aus booking_snapshot.accessories hydratisieren
    const rawSrc = editBooking?.accessories
      || offerInitial?.booking_snapshot?.accessories
      || (offerInitial ? [] : null);
    if (!editBooking && !offerInitial) return;
    accHydratedRef.current = true;
    const src = rawSrc || [];
    if (src.length === 0) {
      // Auch leere Buchungs-Zubehör-Liste bewusst setzen, um evtl. persistiertes
      // Cart-Zubehör aus einer früheren Session nicht in die Buchung zu leaken.
      replaceAccessories([]);
      return;
    }
    // Vollständige Accessory-Docs vom Server holen und über ID matchen —
    // damit Preise / max_quantity / Kategorien stimmen (wichtig für Anzeige
    // und Mengen-Clamping).
    axios.get(`${API}/accessories`)
      .then((r) => {
        const allAcc = r.data?.items || [];
        const hydrated = src.map((a) => {
          const doc = allAcc.find((x) => x.id === a.accessory_id);
          const fallback = {
            id: a.accessory_id,
            name: a.name || 'Zubehör',
            price: Number(a.unit_price) || 0,
            max_quantity: 10,
            image_url: a.image_url || '',
          };
          return { accessory: doc || fallback, quantity: Number(a.quantity) || 1 };
        });
        replaceAccessories(hydrated);
      })
      .catch(() => {
        // Fallback: synthetisierte Accessory-Objekte, falls Fetch fehlschlägt.
        const hydrated = src.map((a) => ({
          accessory: {
            id: a.accessory_id,
            name: a.name || 'Zubehör',
            price: Number(a.unit_price) || 0,
            max_quantity: 10,
            image_url: a.image_url || '',
          },
          quantity: Number(a.quantity) || 1,
        }));
        replaceAccessories(hydrated);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editBooking, offerInitial]);

  // Compute matching special-date rules based on current event dates
  const matchedSpecialDates = useMemo(() => {
    const df = form.event_date_from;
    const dt = form.event_date_to || df;
    if (!df) return [];
    return specialDates.filter((sd) => sd.enabled && sd.date_from <= dt && sd.date_to >= df);
  }, [specialDates, form.event_date_from, form.event_date_to]);

  const minRentalDaysRequired = matchedSpecialDates.reduce((m, sd) => Math.max(m, sd.min_rental_days || 1), 1);
  const forceCleaning = matchedSpecialDates.some((sd) => sd.force_cleaning);
  const onlySelfPickup = matchedSpecialDates.some((sd) => sd.only_self_pickup);
  const deliveryOnRequest = matchedSpecialDates.some((sd) => sd.delivery_on_request);
  // Zahlungsart-Whitelist: Schnittmenge aller aktiven Regeln (leere Listen zählen nicht).
  const allowedPaymentOptions = useMemo(() => {
    const lists = matchedSpecialDates
      .map((sd) => (sd.allowed_payment_options || []))
      .filter((l) => l.length > 0);
    if (!lists.length) return null; // null = keine Beschränkung
    return lists.reduce((acc, l) => acc.filter((v) => l.includes(v)));
  }, [matchedSpecialDates]);
  // Reservierung-Sperre: zeigt Termin nicht buchbar, wenn heutiges Datum < bookable_from
  const todayIso = berlinTodayIso();
  const notYetBookable = matchedSpecialDates.find((sd) => sd.bookable_from && todayIso < sd.bookable_from);

  // Auto-correct cleaning + delivery option when special dates apply
  useEffect(() => {
    if (forceCleaning && form.cleaning_option !== 'kidsevent') {
      setForm((f) => ({ ...f, cleaning_option: 'kidsevent' }));
    }
    if (onlySelfPickup && form.delivery_option && !['pickup_lankow', 'pickup_anhaenger'].includes(form.delivery_option)) {
      setForm((f) => ({ ...f, delivery_option: 'pickup_lankow' }));
    }
    // Falls bisher gewählte Zahlungsart nicht mehr erlaubt ist → aus payment_options entfernen
    if (allowedPaymentOptions) {
      setForm((f) => {
        const filtered = (f.payment_options || []).filter((v) => allowedPaymentOptions.includes(v));
        if (filtered.length === (f.payment_options || []).length) return f;
        return { ...f, payment_options: filtered };
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceCleaning, onlySelfPickup, allowedPaymentOptions, form.cleaning_option]);

  // Rechnungsanschrift = Veranstaltungsort/Lieferadresse: spiegelt die Kundendaten in
  // die Event-Location-Felder, solange die Checkbox aktiv ist.
  useEffect(() => {
    if (!form.event_location_same_as_billing) return;
    setForm((f) => {
      if (
        f.event_location_street === f.customer_street &&
        f.event_location_zip === f.customer_zip &&
        f.event_location_city === f.customer_city
      ) return f;
      return {
        ...f,
        event_location_street: f.customer_street,
        event_location_zip: f.customer_zip,
        event_location_city: f.customer_city,
      };
    });
  }, [form.event_location_same_as_billing, form.customer_street, form.customer_zip, form.customer_city]);

  useEffect(() => {
    if (initialItems && initialItems.length > 0) setItems(initialItems);
  }, [initialItems]);

  // Phase 133.82 — Zusatzleistungen aus dem Warenkorb hydratisieren.
  // Nur einmalig beim ersten Mount und nur im Public-Modus (kein adminMode /
  // editBooking) — in bestehenden Buchungen würden wir sonst echte Daten
  // überschreiben. Wir berühren die bestehende Endreinigungs-Logik NICHT:
  // Auf-/Abbau beinhaltet die Endreinigung bereits über die bundle-Logik;
  // service_cleaning bleibt davon unberührt.
  const addonsHydratedRef = React.useRef(false);
  useEffect(() => {
    if (addonsHydratedRef.current) return;
    if (adminMode || editBooking) return;
    if (!initialAddons) return;
    if (!initialAddons.hasDelivery && !initialAddons.hasSetup && !initialAddons.cleaningChoice) return;
    addonsHydratedRef.current = true;
    setForm((f) => {
      const next = { ...f };
      if (initialAddons.hasDelivery && initialAddons.deliveryAddress) {
        next.delivery_option = 'delivery_other';
        next.event_location_street = initialAddons.deliveryAddress.street || f.event_location_street;
        next.event_location_zip = initialAddons.deliveryAddress.zip || f.event_location_zip;
        next.event_location_city = initialAddons.deliveryAddress.city || f.event_location_city;
        // Veranstaltungsort weicht von Rechnungsadresse ab, sonst würde die
        // Form `customer_street` beim Rendern über die hydrierten Felder kopieren.
        next.event_location_same_as_billing = false;
        if (initialAddons.deliveryPriceOverride != null) {
          next.delivery_price_override = String(initialAddons.deliveryPriceOverride);
        }
      }
      if (initialAddons.hasSetup) {
        next.service_bundle = true;
        if (!next.bundle_mode || next.bundle_mode === 'none') {
          next.bundle_mode = 'both';
        }
      } else if (initialAddons.cleaningChoice) {
        next.cleaning_option = initialAddons.cleaningChoice;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAddons]);

  // KI-Chat → Buchungsseite: Kundendaten + Leistungsauswahl einmalig aus dem Tab-Speicher übernehmen (keine URL-Parameter).
  useEffect(() => {
    if (adminMode || editBooking) return;
    try {
      const raw = sessionStorage.getItem('kidsevent_ai_handover');
      if (!raw) return;
      const h = JSON.parse(raw);
      if (!h?.form || Date.now() - (h.ts || 0) > 30 * 60 * 1000) return;
      setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(h.form).filter(([, v]) => v !== '' && v != null)) }));
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill date if it arrives after mount (cart hydrated late)
  useEffect(() => {
    if (initialDate && !form.event_date_from) {
      setForm((f) => ({
        ...f,
        event_date_from: initialDate,
        event_date_to: initialDateTo || initialDate,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDate, initialDateTo]);

  // Fetch availability for every product whenever a date range is set
  useEffect(() => {
    if (!form.event_date_from) return;
    if (!allProducts.length) return;
    const sessionId = ensureCartSessionId();
    const excludeParam = editBooking?.id ? `&exclude_booking_id=${encodeURIComponent(editBooking.id)}` : '';
    const sidParam = `?session_id=${encodeURIComponent(sessionId)}${excludeParam}`;
    allProducts.forEach((p) => {
      setAvailability((prev) => {
        if (prev[p.id]) return prev;
        axios.get(`${API}/products/${p.id}/availability${sidParam}`)
          .then((res) => setAvailability((s) => ({ ...s, [p.id]: res.data.blocked_ranges || [] })))
          .catch(() => {});
        return prev;
      });
    });
  }, [allProducts, form.event_date_from, form.event_date_to, editBooking?.id]);

  // ============== Auto-Fill Rückgabe: 24h nach Abholung (bei 1-Tages-Miete)
  // bzw. Anmiettag + rental_duration Tage bei Mehrtages-Miete ==============
  // Sobald Kunde Abholtag + Zeitfenster gewählt hat, wird Rückgabetag/-zeit
  // vorgeschlagen (nur wenn Rückgabe noch nicht manuell vom Kunden gesetzt).
  // Der Kunde kann die Rückgabe jederzeit selbst überschreiben – das Auto-Fill
  // greift NICHT nochmal, solange der Kunde selbst editiert hat.
  //
  // Phase 123 – Same-Day Return Fix: Wenn ein Rückgabedatum bereits explizit
  // vorliegt (aus Cart via ProductDetail-Auswahl ODER aus editBooking), wird
  // Auto-Fill deaktiviert. So kann ein Kunde ein Same-Day-Rental (Anmiet =
  // Rückgabe-Tag) im Frontend vorwählen, ohne dass wir den Rückgabetag auf
  // +1 Tag verschieben.
  const [returnManuallyChanged, setReturnManuallyChanged] = useState(
    Boolean(editBooking?.event_date_to || initialDateTo)
  );
  // Helper: erstelle YYYY-MM-DD String aus einem lokalen Datum (ohne UTC-Shift)
  const _toISODate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  useEffect(() => {
    if (!form.event_date_from || items.length === 0) return;
    if (returnManuallyChanged) return;
    // Max. Miettage aus den ausgewählten Produkten
    const maxDays = Math.max(1, ...items.map((it) => Number(it.rental_duration) || 1));
    // Rückgabe-Datum = Anmiettag + maxDays (24h-basiert: Fr morgens + 3 Tage
    // = Mo morgens = 3× 24 h Miete).
    const d = new Date(form.event_date_from + 'T12:00:00');
    d.setDate(d.getDate() + maxDays);
    const suggestedDate = _toISODate(d);
    // Rückgabe-Zeit = gleiches Zeitfenster wie Abholung
    const suggestedTime = form.preferred_time_window || '';
    if (form.event_date_to !== suggestedDate) updateField('event_date_to', suggestedDate);
    if (suggestedTime && form.return_time_window !== suggestedTime) updateField('return_time_window', suggestedTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.event_date_from, form.preferred_time_window, items, returnManuallyChanged]);

  // Umgekehrte Richtung: sobald der Kunde einen Zeitraum wählt (Anmiettag +
  // Rückgabe-Datum), wird die Miet-Dauer jedes Produkts automatisch aus dem
  // Zeitraum PLUS Zeitfenster abgeleitet — der Kunde kann sie nicht mehr per
  // Dropdown ändern. Nur im Public/Customer-Mode aktiv.
  useEffect(() => {
    if (adminMode) return;
    if (!form.event_date_from || !form.event_date_to) return;
    if (items.length === 0) return;
    // Basis: Datumsdifferenz in 24 h-Perioden
    const baseDays = computeDaysFromRange(form.event_date_from, form.event_date_to);
    // Zusatz: wenn Zeitfenster die 24 h-Perioden überschreiten (z. B. 9-10 Uhr
    // Abholung, 15-16 Uhr Rückgabe 3 Tage später = 78 h > 72 h) → einen Tag
    // hochstufen. So bekommt der Kunde automatisch den nächst teureren Preis
    // ohne manuell umzustellen.
    let effDays = baseDays;
    const startH = parseSlotHour(form.preferred_time_window);
    const endH = parseSlotHour(form.return_time_window || form.preferred_time_window);
    if (startH != null && endH != null) {
      const startMs = new Date(form.event_date_from + 'T00:00:00').getTime() + startH * 3600_000;
      const endMs = new Date(form.event_date_to + 'T00:00:00').getTime() + endH * 3600_000;
      const actualHours = (endMs - startMs) / 3600_000;
      const neededDays = Math.ceil(actualHours / 24);
      if (neededDays > effDays) effDays = neededDays;
    }
    const next = items.map((it) => {
      if (!it.product) return it;
      const desired = snapDurationForProduct(it.product, effDays);
      return desired === it.rental_duration ? it : { ...it, rental_duration: desired };
    });
    // Nur setzen, wenn sich mindestens ein Item geändert hat (verhindert Endlos-Renderloop).
    const changed = next.some((it, i) => it !== items[i]);
    if (changed) setItems(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.event_date_from, form.event_date_to, form.preferred_time_window, form.return_time_window, items.length, adminMode]);

  // Zeitfenster-Overrun-Warnung: berechnet ob Rückgabe mehr als (24h × rental_duration)
  // nach der Abholung liegt. Multi-Select Zeitfenster: erstes Slot als Referenz.
  const parseSlotHour = (window) => {
    if (!window) return null;
    const first = String(window).split(',')[0].trim();
    const m = first.match(/(\d{1,2})/);
    return m ? parseInt(m[1], 10) : null;
  };
  const timeWindowWarning = useMemo(() => {
    if (!form.event_date_from || !form.event_date_to || !form.preferred_time_window) return null;
    // Basis-Zeitraum (24 h-basiert): reine Datumsdifferenz.
    const baseDays = computeDaysFromRange(form.event_date_from, form.event_date_to);
    const actualMax = items.length > 0 ? Math.max(1, ...items.map((it) => Number(it.rental_duration) || 1)) : 1;
    const start = new Date(form.event_date_from + 'T00:00:00');
    const startH = parseSlotHour(form.preferred_time_window);
    const end = new Date(form.event_date_to + 'T00:00:00');
    const endH = parseSlotHour(form.return_time_window || form.preferred_time_window);
    if (startH == null || endH == null) return null;
    const startMs = start.getTime() + startH * 3600_000;
    const endMs = end.getTime() + endH * 3600_000;
    const actualHours = (endMs - startMs) / 3600_000;
    // Hinweis, wenn das Zeitfenster den Basis-Zeitraum überschreitet — auch
    // wenn die Miet-Dauer bereits automatisch hochgestuft wurde. So versteht
    // der Kunde, warum sein Preis angepasst wurde.
    const baseHours = baseDays * 24;
    if (actualHours > baseHours + 0.01) {
      return `Hinweis: Deine gewählte Rückgabezeit liegt ca. ${Math.round(actualHours)} Std. nach Abholung. Wir haben den Mietpreis automatisch auf ${actualMax} Tage (${actualMax * 24} Std.) angepasst.`;
    }
    return null;
  }, [form.event_date_from, form.event_date_to, form.preferred_time_window, form.return_time_window, items]);


  // Datum + mind. ein Produkt gewählt sind → andere sehen sie 30 Min als "reserviert".
  const [holdExpiresAt, setHoldExpiresAt] = useState(null);
  const [holdRemaining, setHoldRemaining] = useState(0);

  // Kalender-Sperren: Alle Blocked-Ranges der aktuell im Warenkorb gewählten
  // Produkte holen und vereinen. So sieht der Kunde auf einen Blick, welche
  // Tage bereits ausgebucht sind — er kann sie nicht mehr anklicken.
  const [blockedRanges, setBlockedRanges] = useState([]);
  useEffect(() => {
    if (adminMode) { setBlockedRanges([]); return undefined; }
    const productIds = Array.from(new Set(items.map((it) => it.product?.id).filter(Boolean)));
    if (productIds.length === 0) { setBlockedRanges([]); return undefined; }
    let alive = true;
    (async () => {
      try {
        const sid = (typeof ensureCartSessionId === 'function') ? ensureCartSessionId() : '';
        const excludeParam = editBooking?.id ? `&exclude_booking_id=${encodeURIComponent(editBooking.id)}` : '';
        const results = await Promise.all(productIds.map((pid) => axios
          .get(`${API}/products/${pid}/availability?session_id=${encodeURIComponent(sid)}${excludeParam}`)
          .then((r) => r.data?.blocked_ranges || [])
          .catch(() => [])));
        if (!alive) return;
        // Manuelle Sperren + gebuchte Termine über ALLE gewählten Produkte
        // vereinen. Kein Dedup nötig, DatePicker prüft nur „liegt im Bereich".
        const merged = results.flat().filter((r) => r && r.from);
        setBlockedRanges(merged);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, adminMode, editBooking?.id]);
  useEffect(() => {
    // Kein Cart-Hold im Admin- oder Edit-Modus
    if (adminMode || editBooking) return;
    if (!form.event_date_from || items.length === 0) {
      setHoldExpiresAt(null);
      try { localStorage.removeItem('cartHoldExpiresAt'); window.dispatchEvent(new Event('cartHoldUpdated')); } catch {}
      return;
    }
    // Phase 122Z — Signatur-Check identisch zu CartContext: unveränderter Warenkorb
    // darf KEINEN neuen 30-Min-Hold auslösen, selbst wenn der Kunde 1-2 Tage später
    // die Mietanfrage-Seite erneut öffnet. Ein neuer Hold entsteht nur, wenn sich
    // Produkte, Mengen oder das Datum tatsächlich geändert haben.
    const dateFrom = form.event_date_from;
    const dateTo = form.event_date_to || form.event_date_from;
    const holdItems = items
      .filter((it) => it.product)
      .map((it) => ({ product_id: it.product.id, quantity: it.quantity || 1 }));
    // WICHTIG: identisches Signatur-Format wie CartContext.js verwenden,
    // damit beide Effekte denselben localStorage-Schlüssel teilen.
    const signature = JSON.stringify(
      holdItems
        .map((it) => ({ p: it.product_id, q: it.quantity || 1, f: dateFrom, t: dateTo }))
        .sort((a, b) => (a.p + a.f).localeCompare(b.p + b.f))
    );
    const prevSignature = localStorage.getItem('cartHoldSignature');
    const prevExpiry = localStorage.getItem('cartHoldExpiresAt');
    if (signature === prevSignature) {
      // Warenkorb unverändert → keinen neuen Hold anlegen. Bestehenden (evtl.
      // abgelaufenen) Ablauf nur weiterverwenden, nicht auffrischen.
      const stillValid = prevExpiry && new Date(prevExpiry) > new Date();
      setHoldExpiresAt(stillValid ? prevExpiry : null);
      return;
    }
    const sessionId = ensureCartSessionId();
    const t = setTimeout(() => {
      axios.post(`${API}/cart-holds`, {
        session_id: sessionId,
        date_from: dateFrom,
        date_to: dateTo,
        items: holdItems,
      })
        .then((res) => {
          const exp = res.data?.expires_at || null;
          setHoldExpiresAt(exp);
          try {
            if (exp) {
              localStorage.setItem('cartHoldExpiresAt', exp);
              localStorage.setItem('cartHoldSignature', signature);
            } else {
              localStorage.removeItem('cartHoldExpiresAt');
            }
            window.dispatchEvent(new Event('cartHoldUpdated'));
          } catch {}
        })
        .catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [items, form.event_date_from, form.event_date_to, adminMode, editBooking]);

  // Countdown-Ticker für die Reservierung
  useEffect(() => {
    if (!holdExpiresAt) { setHoldRemaining(0); return; }
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(holdExpiresAt).getTime() - Date.now()) / 1000));
      setHoldRemaining(diff);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [holdExpiresAt]);

  const rangesOverlap = (aFrom, aTo, bFrom, bTo) => {
    const af = new Date(aFrom).setHours(0, 0, 0, 0);
    const at = new Date(aTo || aFrom).setHours(23, 59, 59, 999);
    const bf = new Date(bFrom).setHours(0, 0, 0, 0);
    const bt = new Date(bTo || bFrom).setHours(23, 59, 59, 999);
    return af <= bt && bf <= at;
  };

  // Per-product unavailability for the currently chosen date range
  const unavailableIds = useMemo(() => {
    const set = new Set();
    if (!form.event_date_from) return set;
    for (const p of allProducts) {
      const ranges = availability[p.id] || [];
      for (const r of ranges) {
        if (rangesOverlap(form.event_date_from, form.event_date_to, r.from, r.to)) {
          set.add(p.id);
          break;
        }
      }
    }
    return set;
  }, [allProducts, availability, form.event_date_from, form.event_date_to]);

  // Produkte, die NUR durch eine fremde 30-Min-Reservierung blockiert sind
  // (temporär „gerade reserviert") – im Gegensatz zu dauerhaft ausgebucht.
  const heldByOthersIds = useMemo(() => {
    const set = new Set();
    if (!form.event_date_from) return set;
    for (const p of allProducts) {
      const ranges = availability[p.id] || [];
      let held = false; let booked = false;
      for (const r of ranges) {
        if (rangesOverlap(form.event_date_from, form.event_date_to, r.from, r.to)) {
          if (r.source === 'hold') held = true;
          else booked = true;
        }
      }
      if (held && !booked) set.add(p.id);
    }
    return set;
  }, [allProducts, availability, form.event_date_from, form.event_date_to]);

  const conflicts = useMemo(() => {
    if (!form.event_date_from) return [];
    const out = [];
    for (const it of items) {
      if (!it.product) continue;
      const ranges = availability[it.product.id] || [];
      for (const r of ranges) {
        if (rangesOverlap(form.event_date_from, form.event_date_to, r.from, r.to)) {
          out.push({ id: it.product.id, name: it.product.name, from: r.from, to: r.to });
        }
      }
    }
    return out;
  }, [items, availability, form.event_date_from, form.event_date_to]);

  const [waitlistJoined, setWaitlistJoined] = useState({});
  const joinWaitlist = async (c) => {
    if (!form.customer_email) {
      toast.error('Bitte gib zuerst Deine E-Mail-Adresse ein – dorthin benachrichtigen wir Dich.');
      return;
    }
    try {
      await axios.post(`${API}/waitlist`, {
        product_id: c.id,
        date_from: form.event_date_from,
        date_to: form.event_date_to || form.event_date_from,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone || '',
      });
      setWaitlistJoined((s) => ({ ...s, [c.id]: true }));
      toast.success('Du stehst auf der Warteliste! Wir melden uns per E-Mail, sobald der Termin frei wird.');
    } catch {
      toast.error('Konnte Dich nicht auf die Warteliste setzen. Bitte später erneut versuchen.');
    }
  };

  const productsTotal = useMemo(() => {
    return items.reduce((sum, it) => {
      if (it.product?.price_on_request) return sum;
      const unit = computeUnitPrice(it.product, it.rental_duration, form.event_date_from, form.event_date_to, it.with_supervision, it.price_option);
      return sum + unit * (it.quantity || 1);
    }, 0);
  }, [items, form.event_date_from, form.event_date_to]);

  const hasPriceOnRequest = useMemo(
    () => items.some((it) => !!it.product?.price_on_request),
    [items]
  );

  // --- Zubehör (Phase 77) ---
  const accessoriesTotal = useMemo(
    () => (accessories || []).reduce((sum, a) => sum + (Number(a.accessory.price) || 0) * a.quantity, 0),
    [accessories]
  );

  // Kategorien der aktuell ausgewählten Produkte → passendes Zubehör im Formular
  const selectedCategoryIds = useMemo(() => {
    const s = new Set();
    items.forEach((it) => (it.product?.categories || []).forEach((c) => s.add(c)));
    return Array.from(s);
  }, [items]);

  // Auto-select inquiry intent if any "Preis auf Anfrage" item is in cart (no binding possible)
  useEffect(() => {
    if (hasPriceOnRequest && form.booking_intent !== 'inquiry') {
      setForm((f) => ({ ...f, booking_intent: 'inquiry' }));
    }
  }, [hasPriceOnRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  const setupItems = useMemo(
    () => items.filter((it) => it.product?.requires_setup),
    [items]
  );
  // Phase 122C / 125.9 — Items, deren Auf-/Abbau bei Betreuung inklusive ist,
  // werden bei aktiver Betreuung DIESES Produkts (per-Item, nicht global) NICHT
  // ins Bundle einbezogen. Beispiel: Rodeo mit Betreuung → Aufbau ist im
  // 990 €-Preis enthalten und darf nicht zusätzlich als 125 €-Bundle-Aufpreis
  // berechnet werden.
  const bundleEligibleItems = useMemo(
    () => setupItems.filter(
      (it) => !(it.with_supervision !== false && it.product?.setup_included_with_supervision),
    ),
    [setupItems],
  );
  // Phase 125.10 — Wenn ALLE Setup-Produkte ihren Aufbau bereits durch die
  // Betreuung inkludiert haben, service_bundle automatisch aktivieren (rein
  // optisch — Preis bleibt 0, weil kein Item ins Bundle einfließt). So sieht
  // der Kunde in der Kostenübersicht sofort „Auf- und Abbau: inklusive".
  const allSetupCoveredByBetreuung = useMemo(
    () => setupItems.length > 0 && setupItems.every(
      (it) => it.with_supervision !== false && it.product?.setup_included_with_supervision,
    ),
    [setupItems],
  );
  useEffect(() => {
    if (allSetupCoveredByBetreuung && !form.service_bundle) {
      setForm((f) => ({ ...f, service_bundle: true }));
    }
  }, [allSetupCoveredByBetreuung, form.service_bundle]);
  const setupUnits = useMemo(
    () => bundleEligibleItems.reduce((sum, it) => sum + (it.quantity || 1), 0),
    [bundleEligibleItems]
  );

  // --- Bundle (Auf-/Abbau + Endreinigung) ---
  // Per-Item-Logik (spiegelt Backend): jeder Artikel kann eine eigene
  // `bundle_price_override` / `bundle_price_bulk_override` haben. Sonst gilt
  // die globale Pauschale. Bulk-Schwelle bezieht sich auf die Gesamtanzahl.
  const bundleThreshold = Number(pricing.bundle_threshold || 3);
  const globalBundleUnit = Number(pricing.bundle_price || 75);
  const globalBundleBulkUnit = Number(pricing.bundle_price_bulk || 50);
  const bundleIsBulk = setupUnits >= bundleThreshold;
  const itemBundleUnitPrice = (product) => {
    const ov = bundleIsBulk ? product?.bundle_price_bulk_override : product?.bundle_price_override;
    return (ov != null && ov !== '') ? Number(ov) : (bundleIsBulk ? globalBundleBulkUnit : globalBundleUnit);
  };
  // Phase 122S — Admin kann Auf-/Abbau teilen (nur Aufbau / nur Abbau / beides).
  // Der Standardpreis geht davon aus, dass BEIDES enthalten ist → bei
  // „nur Aufbau" oder „nur Abbau" wird der Preis halbiert.
  const effectiveBundleMode = adminMode
    ? (form.bundle_mode || 'none')
    : (form.service_bundle ? 'both' : 'none');
  const bundleModeMultiplier = { both: 1.0, setup_only: 0.5, teardown_only: 0.5, none: 0 }[effectiveBundleMode] ?? 0;
  const bundleAutoPrice = effectiveBundleMode !== 'none'
    ? Math.round(bundleEligibleItems.reduce((sum, it) => sum + itemBundleUnitPrice(it.product) * (it.quantity || 1) * bundleModeMultiplier, 0) * 100) / 100
    : 0;
  // Admin-Override hat Vorrang (auch 0 € gültig, um den Service kostenfrei zu machen).
  const bundleOverride = adminMode ? form.bundle_price_override_manual : null;
  // Phase 122S — Individuelle Aufbau/Abbau-Preise summieren sich zum Bundle-Preis,
  // ausser Admin setzt zusätzlich den Gesamt-Override.
  let bundleFromParts = null;
  if (adminMode && effectiveBundleMode !== 'none') {
    const s = (form.bundle_setup_price !== null && form.bundle_setup_price !== '') ? Number(form.bundle_setup_price) : null;
    const t = (form.bundle_teardown_price !== null && form.bundle_teardown_price !== '') ? Number(form.bundle_teardown_price) : null;
    if (effectiveBundleMode === 'setup_only' && s !== null) bundleFromParts = s;
    else if (effectiveBundleMode === 'teardown_only' && t !== null) bundleFromParts = t;
    else if (effectiveBundleMode === 'both' && (s !== null || t !== null))
      bundleFromParts = (s ?? bundleAutoPrice / 2) + (t ?? bundleAutoPrice / 2);
  }
  const bundlePrice = (bundleOverride !== null && bundleOverride !== '' && bundleOverride !== undefined)
    ? Number(bundleOverride) || 0
    : (bundleFromParts !== null ? Math.round(bundleFromParts * 100) / 100 : bundleAutoPrice);
  // Anzeige: einheitlicher Stückpreis, falls alle Setup-Artikel gleich teuer sind; sonst gemischt.
  const bundleUnitPrices = setupItems.map((it) => itemBundleUnitPrice(it.product));
  const bundleUniformUnit = (bundleUnitPrices.length > 0 && bundleUnitPrices.every((p) => p === bundleUnitPrices[0]))
    ? bundleUnitPrices[0]
    : null;

  // --- Separate Endreinigung (nur wenn kein Bundle) ---
  // Phase 122w — pro Produkt konfigurierbarer Reinigungspreis.
  // `cleaning_price_override` auf dem Produkt (auch 0 erlaubt) vor globalem Default.
  const cleaningDefaultUnit = Number(pricing.cleaning_price || 20);
  const cleaningBreakdown = items.map((it) => {
    const p = it.product || {};
    const ov = p.cleaning_price_override;
    const unit = (ov !== undefined && ov !== null && ov !== '') ? Number(ov) : cleaningDefaultUnit;
    const qty = Math.max(1, parseInt(it.quantity || 1, 10));
    return { name: p.name || '', unit, qty, sub: unit * qty };
  });
  const cleaningPriceAuto = (effectiveBundleMode === 'none' && form.cleaning_option === 'kidsevent')
    ? Math.round(cleaningBreakdown.reduce((s, b) => s + b.sub, 0) * 100) / 100
    : 0;
  // Admin-Override: manueller Gesamtpreis für Endreinigung (auch 0 möglich)
  const cleaningOverrideRaw = form.cleaning_price_total_override;
  const cleaningOverrideActive = adminMode
    && effectiveBundleMode === 'none'
    && form.cleaning_option === 'kidsevent'
    && cleaningOverrideRaw !== undefined
    && cleaningOverrideRaw !== null
    && String(cleaningOverrideRaw).trim() !== '';
  const cleaningPrice = cleaningOverrideActive
    ? Math.max(0, Math.round(Number(cleaningOverrideRaw) * 100) / 100)
    : cleaningPriceAuto;

  // --- Betreuung ---
  // Per-product extra-hour price override: when a supervision product (Bullriding, Bungee,
  // Eisenbahn…) is in the cart, supervision is charged with THAT product's per-hour rate
  // (z.B. 100 €/h) statt der generischen 35 €/h – unabhängig von „mit"/„ohne" Betreuung.
  // --- Betreuung ---
  // Phase 122D — per-Item Zusatzstunden + globaler Block nur für Items ohne
  // eigenen Stundensatz (typisch: Hüpfburgen). Rodeo/Bungee zahlen ihre
  // eigenen Extra-Stunden separat.
  const globalBetreuungPerHour = Number(pricing.betreuung_per_hour || 35);
  const EXTRA_HUEPFBURG_PER_HOUR = Number(pricing.betreuung_extra_huepfburg_per_hour ?? 5);
  const HUEPFBURGEN_PER_STAFF = Math.max(1, Number(pricing.betreuung_max_huepfburg_per_staff || 3));

  // Phase 122E — Betreuungs-Produkte (Rodeo, Bungee, Eisenbahn) mit eigenem Stundenpreis.
  // Die zusätzlichen Stunden werden im Zusatzservice-Block eingegeben (nicht mehr am Produkt).
  const supItemLines = items
    .map((it, idx) => ({ it, idx }))
    .filter(({ it }) => it.product?.requires_supervision && it.with_supervision !== false && Number(it.product?.supervision_extra_hour_price) > 0)
    .map(({ it, idx }) => {
      const defaultH = Number(it.product?.supervision_hours || 0);
      const selectedH = Number(it.supervision_hours ?? defaultH);
      const extraH = Math.max(0, selectedH - defaultH);
      const qty = Math.max(1, Number(it.quantity || 1));
      const hourly = Number(it.product?.supervision_extra_hour_price || 0);
      return { idx, product_name: it.product?.name || '', selectedH, defaultH, extraH, hourly, qty, extra: extraH * hourly * qty };
    });
  const perItemSupExtra = supItemLines.reduce((s, l) => s + l.extra, 0);

  // Phase 122E — Hüpfburgen-Betreuung (nur echte Hüpfburgen mit `requires_setup`).
  //   1. Hüpfburg → 35 €/h (Betreuer 1)
  //   2./3. Hüpfburg → +5 €/h (selber Betreuer)
  //   4. Hüpfburg → +35 €/h (Betreuer 2 startet) …usw.
  // Formel: Betreuer = ceil(qty/3); Extra = qty - Betreuer;
  //         Stundensatz = Betreuer × 35 + Extra × 5
  const huepfburgLines = items
    .map((it, idx) => ({ it, idx }))
    .filter(({ it }) => it.product && it.product.requires_setup && !it.product.requires_supervision && !it.product.price_on_request)
    .map(({ it, idx }) => ({ idx, product_name: it.product?.name || '', qty: Math.max(1, Number(it.quantity || 1)) }));
  const huepfburgTotalQty = huepfburgLines.reduce((s, l) => s + l.qty, 0);
  const huepfburgStaffCount = huepfburgTotalQty > 0 ? Math.ceil(huepfburgTotalQty / HUEPFBURGEN_PER_STAFF) : 0;
  const huepfburgExtraCount = huepfburgTotalQty > 0 ? (huepfburgTotalQty - huepfburgStaffCount) : 0;
  const huepfburgRatePerHour = huepfburgStaffCount * globalBetreuungPerHour + huepfburgExtraCount * EXTRA_HUEPFBURG_PER_HOUR;
  const huepfburgHours = Math.max(0, Number(form.betreuung_hours || 0));
  const huepfburgBetreuungPrice = (form.service_betreuung && huepfburgHours > 0 && huepfburgTotalQty > 0)
    ? huepfburgRatePerHour * huepfburgHours
    : 0;
  const derivedServiceBetreuung = (!!form.service_betreuung && huepfburgHours > 0 && huepfburgTotalQty > 0) || perItemSupExtra > 0;
  // Für Anzeige/Kompatibilität
  const betreuungPerHour = globalBetreuungPerHour;
  const betreuungPriceAuto = Math.round((huepfburgBetreuungPrice + perItemSupExtra) * 100) / 100;
  // Phase 122A — Admin-Override: freier Betreuungspreis (überschreibt die
  // automatische Berechnung). Leer/undefined → auto.
  const betreuungOverrideRaw = form.betreuung_price_override;
  const betreuungOverrideActive = betreuungOverrideRaw !== undefined && betreuungOverrideRaw !== null && String(betreuungOverrideRaw).trim() !== '';
  const betreuungPrice = betreuungOverrideActive
    ? Math.max(0, Number(betreuungOverrideRaw))
    : betreuungPriceAuto;

  // --- Kinderschminken ---
  // Phase 128.16 — Admin-Override für Sonderpreis; fällt zurück auf Shop-Preis.
  const ksOverrideRaw = form.kinderschminken_price_override;
  const ksOverrideActive = ksOverrideRaw !== undefined && ksOverrideRaw !== null && String(ksOverrideRaw).trim() !== '';
  const kinderschminkenPrice = form.service_kinderschminken
    ? (ksOverrideActive ? Math.max(0, Number(ksOverrideRaw)) : Number(pricing.kinderschminken_price || 0))
    : 0;

  // --- Delivery / Pickup options ---
  const deliveryOptions = [
    { v: 'pickup_lankow', l: 'Selbstabholung in Schwerin-Lankow', price: Number(pricing.pickup_lankow_price || 0) },
    { v: 'pickup_anhaenger', l: 'Selbstabholung im Mietanhänger (nach Verfügbarkeit)', price: Number(pricing.pickup_anhaenger_price || 20) },
    { v: 'delivery_schwerin', l: 'Lieferservice innerhalb Schwerin', price: Number(pricing.delivery_schwerin_price || 25) },
    { v: 'delivery_other', l: 'Lieferservice nach Entfernung (km)', price: null },
  ];
  const selectedDelivery = deliveryOptions.find((d) => d.v === form.delivery_option);

  // --- Live Distance Matrix lookup for "delivery_other" ---
  const [distanceData, setDistanceData] = useState({ km: null, loading: false, error: null });
  const perKm = Number(pricing.price_per_km || 1.30);
  const tripMult = Number(pricing.round_trip_multiplier || 2);
  useEffect(() => {
    if (form.delivery_option !== 'delivery_other') {
      setDistanceData({ km: null, loading: false, error: null });
      return;
    }
    const street = (form.event_location_street || '').trim();
    const zip = (form.event_location_zip || '').trim();
    const city = (form.event_location_city || '').trim();
    if (!street || !zip || !city) {
      setDistanceData({ km: null, loading: false, error: null });
      return;
    }
    const dest = `${street}, ${zip} ${city}`;
    setDistanceData((s) => ({ ...s, loading: true, error: null }));
    const t = setTimeout(() => {
      axios.post(`${API}/distance`, { destination_address: dest })
        .then((res) => {
          const km = Number(res.data?.distance_km || 0);
          setDistanceData({ km, loading: false, error: km > 0 ? null : 'Adresse nicht gefunden – bitte prüfen' });
        })
        .catch((err) => {
          const detail = err?.response?.data?.detail || '';
          const userMsg = detail.includes('REQUEST_DENIED') || detail.includes('legacy API')
            ? 'Lieferkosten-Berechnung derzeit nicht verfügbar – wir teilen Dir den Preis im Angebot mit.'
            : 'Entfernung konnte nicht ermittelt werden';
          setDistanceData({ km: null, loading: false, error: userMsg });
        });
    }, 500); // debounce 500ms
    return () => clearTimeout(t);
  }, [form.delivery_option, form.event_location_street, form.event_location_zip, form.event_location_city]);

  let deliveryPrice = 0;
  if (selectedDelivery && selectedDelivery.price !== null) {
    deliveryPrice = selectedDelivery.price;
  } else if (form.delivery_option === 'delivery_other' && distanceData.km != null && distanceData.km > 0) {
    // Auf ganze Euro runden (keine Cent-Beträge), dann Mindestbetrag (z.B. innerhalb Schwerin → 25 €)
    const minDelivery = Number(pricing.delivery_min_price || 25);
    deliveryPrice = Math.max(Math.round(distanceData.km * perKm * tripMult), minDelivery);
  }
  // Rabatt: Wenn Betreuung gewählt → Lieferkosten halbieren
  if (derivedServiceBetreuung && deliveryPrice > 0) {
    deliveryPrice = Math.round(deliveryPrice * Number(pricing.betreuung_delivery_discount || 0.5));
    // Mindestbetrag gilt immer für entfernungsbasierte Lieferung
    if (form.delivery_option === 'delivery_other') {
      deliveryPrice = Math.max(deliveryPrice, Number(pricing.delivery_min_price || 25));
    }
  }
  // Admin-Override: manueller Lieferkosten-Betrag (auch 0 möglich)
  const deliveryOverrideRaw = form.delivery_price_override;
  const deliveryOverrideActive = adminMode && deliveryOverrideRaw !== undefined && deliveryOverrideRaw !== null && String(deliveryOverrideRaw).trim() !== '';
  const deliveryPriceAuto = deliveryPrice;
  if (deliveryOverrideActive) {
    deliveryPrice = Math.max(0, Number(deliveryOverrideRaw));
  }
  const deliveryNote = (form.delivery_option === 'delivery_other')
    ? (distanceData.loading
        ? 'Entfernung wird berechnet …'
        : distanceData.error
          ? distanceData.error
          : distanceData.km != null && distanceData.km > 0
            ? `Entfernung: ${distanceData.km.toFixed(1)} km · ${perKm.toFixed(2).replace('.', ',')} €/km × ${tripMult} Strecken (mind. ${Number(pricing.delivery_min_price || 25).toFixed(2).replace('.', ',')} €)`
            : 'Bitte Lieferadresse oben ausfüllen für eine Vorab-Schätzung.')
    : null;

  const subtotalBeforeBonus = productsTotal + accessoriesTotal + bundlePrice + cleaningPrice + betreuungPrice + kinderschminkenPrice + deliveryPrice;
  // Admin-only: Live-Summe der Custom-Positionen für die Vorschau
  const customItemsPreview = adminMode
    ? (form.custom_items || []).map((ci) => {
        const label = (ci.label || '').trim();
        if (!label) return null;
        if (ci.kind === 'custom_product') {
          const qty = Number(ci.quantity) || 0;
          const days = Number(ci.rental_duration) || 0;
          const unit = Number(ci.unit_price) || 0;
          // Preis/Stk ist bereits die Gesamt-Position pro Stück (nicht pro Tag).
          // Tage werden nur informativ mitgeführt.
          return {
            label,
            amount: Math.round(qty * unit * 100) / 100,
            isProduct: true,
            qty,
            days,
          };
        }
        return { label, amount: Number(ci.amount) || 0, isProduct: false };
      }).filter(Boolean)
    : [];
  const customItemsTotal = customItemsPreview.reduce((s, ci) => s + (Number(ci.amount) || 0), 0);
  // Phase 128.85 — Fun-Food-Aufpreis (Upgrades + Extras) muss AUCH im Live-Total
  // eingerechnet werden. Beim Speichern wandern die Positionen ohnehin via
  // funfoodToCustomItems() in payload.custom_items.
  const funfoodPreviewTotal = funfoodToCustomItems(form.funfood_selections, items).reduce(
    (s, ci) => s + (Number(ci.amount) || 0),
    0,
  );
  const subtotalWithCustom = subtotalBeforeBonus + customItemsTotal + funfoodPreviewTotal;
  // Bonus discount — only if the customer has actually collected enough stamps.
  // Admin-Edit: NIE die eigene Admin-Sammelkarte auf eine Kundenbuchung anwenden.
  const bonusDiscount = adminMode ? 0 : Math.min(
    Number(bonusCard?.available_discount_eur || 0),
    Math.max(0, Math.round(subtotalWithCustom * 100) / 100),
  );
  const totalAuto = Math.max(0, Math.round((subtotalWithCustom - bonusDiscount) * 100) / 100);
  // Admin-Override: manueller Gesamtpreis (überschreibt alles außer Rabatt-Anzeige)
  const totalOverrideRaw = form.total_price_override;
  const totalOverrideActive = adminMode && totalOverrideRaw !== undefined && totalOverrideRaw !== null && String(totalOverrideRaw).trim() !== '';
  const total = totalOverrideActive ? Math.max(0, Number(totalOverrideRaw)) : totalAuto;

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [awareChoice, setAwareChoice] = useState('');
  const [awareOther, setAwareOther] = useState('');
  const handleAwareChoice = (choice) => {
    setAwareChoice(choice);
    if (choice === 'Sonstiges') {
      updateField('aware_through', awareOther ? `Sonstiges: ${awareOther}` : 'Sonstiges');
    } else {
      updateField('aware_through', choice);
    }
  };
  const handleAwareOther = (text) => {
    setAwareOther(text);
    updateField('aware_through', text ? `Sonstiges: ${text}` : 'Sonstiges');
  };

  const toggleContactMethod = (m) => {
    setForm((f) => ({
      ...f,
      contact_methods: f.contact_methods.includes(m)
        ? f.contact_methods.filter((x) => x !== m)
        : [...f.contact_methods, m],
    }));
  };

  const togglePaymentOption = (m) => {
    setForm((f) => ({
      ...f,
      payment_options: f.payment_options.includes(m)
        ? f.payment_options.filter((x) => x !== m)
        : [...f.payment_options, m],
    }));
  };

  // Phase 122X — Im Admin-Modus (manuelle Buchung / Edit) NIEMALS den globalen
  // Kunden-Warenkorb schreiben. Sonst leaken die vom Admin gewählten Produkte
  // in `localStorage.cart` und tauchen später im echten Kunden-Warenkorb auf.
  // Snapshot/Restore war ein Fallback — dieser Skip ist die eigentliche Lösung.
  const publicCartWrite = !adminMode;

  const addItem = (product) => {
    if (!product) return;
    // Each product only once, qty fixed to 1
    if (items.find((it) => it.product?.id === product.id)) return;
    // Miet-Dauer aus dem gewählten Zeitraum berechnen (falls Datum gewählt),
    // sonst kleinste Staffel/1 Tag. So bekommt jedes Produkt beim Hinzufügen
    // automatisch die richtige Anzahl Tage.
    const days = form.event_date_from
      ? computeDaysFromRange(form.event_date_from, form.event_date_to)
      : null;
    const duration = days ? snapDurationForProduct(product, days) : defaultDuration(product);
    const eventDate = form.event_date_from || '';
    const eventDateTo = form.event_date_to || eventDate;
    setItems([...items, { product, rental_duration: duration, quantity: 1, eventDate, eventDateTo }]);
    if (publicCartWrite) {
      // Auch in den globalen Warenkorb legen (persistiert über Navigation hinweg)
      try { addToCart(product, duration, eventDate, eventDateTo, { quantity: 1 }); } catch { /* noop */ }
    }
  };
  const toggleItem = (product) => {
    const exists = items.find((it) => it.product?.id === product.id);
    if (exists) {
      setItems(items.filter((it) => it.product?.id !== product.id));
      if (publicCartWrite) {
        try { removeAllByProduct(product.id); } catch { /* noop */ }
      }
    } else addItem(product);
  };
  const updateItemDuration = (idx, dur) => {
    setItems(items.map((it, i) => i === idx ? { ...it, rental_duration: dur } : it));
    const it = items[idx];
    if (it?.product && publicCartWrite) {
      try { addToCart(it.product, dur, it.eventDate || form.event_date_from || '', it.eventDateTo || form.event_date_to || '', { quantity: it.quantity || 1, withSupervision: it.with_supervision }); } catch { /* noop */ }
    }
  };
  const updateItemSupervision = (idx, withSup) => setItems(items.map((it, i) => i === idx ? { ...it, with_supervision: withSup } : it));
  const removeItem = (idx) => {
    const it = items[idx];
    setItems(items.filter((_, i) => i !== idx));
    if (it?.product?.id && publicCartWrite) {
      try { removeAllByProduct(it.product.id); } catch { /* noop */ }
    }
  };

  // Products already added in the form (used to filter "weitere Produkte" list)
  const addedProductIds = new Set(items.filter((it) => it.product).map((it) => it.product.id));
  const additionalProducts = allProducts.filter((p) => !addedProductIds.has(p.id));

  // Phase 90 — Feld-Labels + Anker-Selektoren für die Missing-Fields-Panel.
  const FIELD_META = {
    customer_name:       { label: 'Dein Name',                        anchor: '[data-testid="bf-name"]' },
    customer_email:      { label: 'E-Mail-Adresse',                   anchor: '[data-testid="bf-email"]' },
    customer_phone:      { label: 'Telefonnummer',                    anchor: '[data-testid="bf-phone"]' },
    customer_street:     { label: 'Deine Straße',                     anchor: '[data-testid="bf-street"]' },
    customer_zip:        { label: 'Deine PLZ',                        anchor: '[data-testid="bf-zip"]' },
    customer_city:       { label: 'Deine Stadt',                      anchor: '[data-testid="bf-city"]' },
    event_date_from:     { label: 'Mietbeginn (Datum)',               anchor: '[data-testid="bf-from"]' },
    event_date_to:       { label: 'Mietende (Datum)',                 anchor: '[data-testid="bf-to"]' },
    event_location_zip:  { label: 'PLZ des Veranstaltungsorts',       anchor: '[data-testid="bf-loc-zip"]' },
    event_location_city: { label: 'Stadt des Veranstaltungsorts',     anchor: '[data-testid="bf-loc-city"]' },
    ground_type:         { label: 'Untergrund-Art',                   anchor: '[data-testid="bf-ground"]' },
    event_type:          { label: 'Veranstaltungsart',                anchor: '[data-testid="bf-event-type"]' },
    delivery_option:     { label: 'Lieferung oder Selbstabholung',    anchor: '[data-testid="bf-delivery-option-section"]' },
    cleaning_option:     { label: 'Reinigungs-Option',                anchor: '[data-testid="bf-cleaning-section"]' },
    aware_through:       { label: 'Wie bist du auf uns aufmerksam geworden', anchor: '[data-testid="bf-aware-through"]' },
    notes:               { label: 'Weitere Informationen',            anchor: '[data-testid="bf-notes"]' },
    items:               { label: 'Mindestens ein Produkt auswählen', anchor: '[data-testid="bf-products-section"]' },
    betreuung_hours:     { label: 'Stundenanzahl der Betreuung',      anchor: '[data-testid="bf-betreuung-hours"]' },
    contact_methods:     { label: 'Kontaktweg auswählen (Telefon/E-Mail/WhatsApp)', anchor: '[data-testid="bf-contact-methods"]' },
    privacy_accepted:    { label: 'Datenschutz-Bestätigung',          anchor: '[data-testid="bf-privacy-label"]' },
    booking_intent:      { label: 'Wie du fortfahren möchtest (verbindlich buchen oder unverbindlich anfragen)', anchor: '[data-testid="bf-intent-section"]' },
  };

  const validate = () => {
    // Sammelt ALLE fehlenden Pflichtfelder, damit dem Kunden klar wird was zu tun ist.
    const missing = [];
    const lockedRequired = ['customer_name', 'customer_email', 'event_date_from', 'event_date_to', 'delivery_option'];
    const configurable = ['customer_phone', 'customer_street', 'customer_zip', 'customer_city',
      'ground_type', 'event_type', 'aware_through', 'notes'];
    const required = [...lockedRequired, ...configurable.filter(isReq)];
    if (deliveryByUs) required.push('event_location_zip', 'event_location_city');
    for (const key of required) {
      if (!form[key] || !String(form[key]).trim()) missing.push(key);
    }
    if (items.length === 0) {
      // Phase 122S — im Admin-Modus reichen auch reine Custom-Items
      // (z.B. „Tischkicker" ohne Katalog-Produkt) als Buchungsinhalt.
      const hasCustom = adminMode && (form.custom_items || []).some(
        (ci) => (ci.label || '').trim()
      );
      if (!hasCustom) missing.push('items');
    }
    if (effectiveBundleMode === 'none' && !form.cleaning_option && !offerMode) missing.push('cleaning_option');
    if (form.service_betreuung && (!form.betreuung_hours || form.betreuung_hours <= 0)) missing.push('betreuung_hours');
    if (!offerMode && (!form.contact_methods || form.contact_methods.length === 0)) missing.push('contact_methods');
    if (!adminMode && !form.privacy_accepted) missing.push('privacy_accepted');
    if (!adminMode && !form.booking_intent) missing.push('booking_intent');

    // E-Mail Format separat (nicht "fehlend" sondern "ungültig")
    const invalidFormat = [];
    if (form.customer_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customer_email)) {
      invalidFormat.push({ key: 'customer_email', label: 'E-Mail-Adresse (ungültiges Format)' });
    }

    // Toast-Errors (keine „fehlenden Felder", sondern echte Regelverstöße)
    if (missing.length === 0 && invalidFormat.length === 0) {
      if (notYetBookable) {
        toast.error(`Reservierungen für „${notYetBookable.name}" sind erst ab dem ${new Date(notYetBookable.bookable_from).toLocaleDateString('de-DE')} möglich.`);
        return { ok: false, missing: [] };
      }
      if (minRentalDaysRequired > 1 && form.event_date_from && form.event_date_to) {
        const a = new Date(form.event_date_from);
        const b = new Date(form.event_date_to);
        const days = Math.floor((b - a) / (1000 * 60 * 60 * 24)) + 1;
        if (days < minRentalDaysRequired) {
          toast.error(`Mindestmietdauer ${minRentalDaysRequired} Tage erforderlich an diesem Datum`);
          return { ok: false, missing: [] };
        }
      }
      if (form.event_date_to && form.event_date_from && new Date(form.event_date_to) < new Date(form.event_date_from)) {
        toast.error('Mietende darf nicht vor dem Mietbeginn liegen');
        return { ok: false, missing: [] };
      }
      if (conflicts.length > 0 && !adminMode) {
        // Phase 122Y — Admin darf trotz Konflikt buchen (z. B. selbst-blockierter
        // Zeitraum). Öffentliche Kunden werden weiter geblockt.
        toast.error('Mindestens ein Produkt ist im gewählten Zeitraum nicht verfügbar');
        return { ok: false, missing: [] };
      }
      return { ok: true, missing: [] };
    }

    // Missing/Invalid-Felder als strukturierte Liste zurückgeben
    const list = [
      ...missing.map((k) => ({ key: k, label: FIELD_META[k]?.label || k, anchor: FIELD_META[k]?.anchor })),
      ...invalidFormat.map((f) => ({ ...f, anchor: FIELD_META[f.key]?.anchor })),
    ];
    return { ok: false, missing: list };
  };

  const scrollToAnchor = (anchor) => {
    if (!anchor) return;
    const el = document.querySelector(anchor);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Roter Puls-Effekt für 1.5 s
    el.classList.add('ring-2', 'ring-red-500', 'ring-offset-2');
    setTimeout(() => el.classList.remove('ring-2', 'ring-red-500', 'ring-offset-2'), 1500);
    // Fokus setzen (falls Input)
    setTimeout(() => { try { el.focus?.(); } catch { /* ignore */ } }, 400);
  };

  const submit = async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    const saveAsDraft = !!opts.draft;
    // Phase 128.17 — Wenn eine Buchung mit bereits gesendetem/unterschriebenem
    // Vertrag bearbeitet wird, VOR dem Speichern nachfragen, was passieren soll.
    const contractLocked = !!(editBooking && (
      editBooking.customer_signed_at ||
      ['contract_sent', 'contract_signed'].includes(editBooking.status)
    ));
    if (
      !saveAsDraft && contractLocked && !opts.contractDecisionMade
    ) {
      setContractDecisionPending({
        onDecide: (preserve) => {
          setContractDecisionPending(null);
          // Setze temporär die Entscheidung im Form-State und rufe submit erneut
          setForm((f) => ({ ...f, _preserve_contract_decision: preserve }));
          setTimeout(() => submit(null, { ...opts, contractDecisionMade: true }), 0);
        },
        onCancel: () => setContractDecisionPending(null),
      });
      return;
    }
    // Phase 122S — Entwurf: minimale Validierung (nur Kundenname), Rest darf fehlen.
    if (saveAsDraft) {
      if (!adminMode || editBooking) return; // nur bei manueller Neuanlage im Admin
      const name = (form.customer_name || '').trim();
      if (!name) {
        setMissingFields([{ key: 'customer_name', label: 'Kundenname', anchor: '[data-testid="bf-customer-name"]' }]);
        toast.error('Bitte mindestens den Kundennamen ausfüllen');
        setTimeout(() => scrollToAnchor('[data-testid="bf-customer-name"]'), 50);
        return;
      }
      setMissingFields([]);
    } else {
      const result = validate();
      if (!result.ok) {
        // Zeige das Missing-Panel oben am Button; scrollen zum ersten fehlenden Feld.
        setMissingFields(result.missing);
        if (result.missing.length > 0) {
          toast.error(`Bitte ${result.missing.length} fehlende ${result.missing.length === 1 ? 'Angabe' : 'Angaben'} ergänzen`);
          // erst Panel rendern lassen, dann scrollen
          setTimeout(() => {
            if (missingPanelRef.current) {
              missingPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            // Nach kurzem Delay zum ersten fehlenden Feld springen
            setTimeout(() => scrollToAnchor(result.missing[0].anchor), 500);
          }, 50);
        }
        return;
      }
      setMissingFields([]);
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        hold_session_id: ensureCartSessionId(),
        // Phase 122S — Admin-Modus: bundle_mode + Preis-Override senden. Public-
        // Flow bleibt kompatibel: service_bundle=true → bundle_mode implicit 'both'.
        service_bundle: effectiveBundleMode !== 'none',
        bundle_mode: adminMode ? (form.bundle_mode || 'none') : (form.service_bundle ? 'both' : 'none'),
        bundle_price_override_manual: (adminMode && form.bundle_price_override_manual !== null
          && form.bundle_price_override_manual !== '' && form.bundle_price_override_manual !== undefined)
          ? Number(form.bundle_price_override_manual)
          : null,
        bundle_setup_price: (adminMode && form.bundle_setup_price !== null && form.bundle_setup_price !== '')
          ? Number(form.bundle_setup_price) : null,
        bundle_teardown_price: (adminMode && form.bundle_teardown_price !== null && form.bundle_teardown_price !== '')
          ? Number(form.bundle_teardown_price) : null,
        // Phase 122E — service_betreuung + betreuung_hours werden global aus dem
        // Zusatzservice-Block übernommen. Backend berechnet den Preis mit
        // Staffelpreis (35 €/h + 5 €/h pro weiterer Hüpfburg, ab der 4. neuer Betreuer).
        service_betreuung: !!form.service_betreuung,
        betreuung_hours: form.service_betreuung ? Number(form.betreuung_hours || 0) : 0,
        betreuung_price_override: (form.betreuung_price_override === '' || form.betreuung_price_override == null)
          ? null : Number(form.betreuung_price_override),
        // Phase 128.16 — Kinderschminken-Preis-Override mitsenden
        kinderschminken_price_override: (form.kinderschminken_price_override === '' || form.kinderschminken_price_override == null)
          ? null : Number(form.kinderschminken_price_override),
        kinderschminken_notes: (form.kinderschminken_notes || '').trim(),
        // If bundle selected, server will ignore cleaning_option; we send null for clarity
        cleaning_option: (effectiveBundleMode !== 'none') ? null : form.cleaning_option,
        items: items.filter((it) => it.product).map((it) => ({
          product_id: it.product.id,
          product_name: it.product.name,
          rental_duration: it.rental_duration,
          quantity: it.quantity || 1,
          unit_price: 0,
          subtotal: 0,
          with_supervision: it.with_supervision,
          // Phase 122D — per-Item Betreuungsstunden (Default = product.supervision_hours)
          supervision_hours: it.supervision_hours !== undefined && it.supervision_hours !== null && it.supervision_hours !== ''
            ? Number(it.supervision_hours)
            : null,
          // Phase 133.86 — Gewählte Preisvariante (Standard = null)
          price_option: it.price_option || null,
        })),
        accessories: (accessories || []).map((a) => ({
          accessory_id: a.accessory.id,
          name: a.accessory.name,
          quantity: a.quantity,
          unit_price: 0,
          subtotal: 0,
        })),
        bonus_discount_request_eur: bonusDiscount > 0 ? bonusDiscount : undefined,
        // Admin-only Overrides
        delivery_price_override: adminMode && form.delivery_price_override !== '' && form.delivery_price_override != null
          ? Number(form.delivery_price_override)
          : null,
        total_price_override: adminMode && form.total_price_override !== '' && form.total_price_override != null
          ? Number(form.total_price_override)
          : null,
        // Phase 125.13 — Admin-Override für die gesamte Endreinigung (Kulanz).
        cleaning_price_total_override: adminMode && form.cleaning_price_total_override !== '' && form.cleaning_price_total_override != null
          ? Number(form.cleaning_price_total_override)
          : null,
        // Phase 125.14 — Admin: Buchung direkt als bezahlt anlegen
        already_paid: adminMode ? !!form.already_paid : undefined,
        already_paid_method: adminMode && form.already_paid ? (form.already_paid_method || 'bar') : undefined,
        // Admin-only: freie Positionen / eigene Produkte / Rabatte
        custom_items: adminMode
          ? (form.custom_items || [])
              .filter((c) => (c.label || '').trim())
              .map((c) => (c.kind === 'custom_product'
                ? {
                    label: c.label,
                    kind: 'custom_product',
                    quantity: Number(c.quantity) || 0,
                    rental_duration: Number(c.rental_duration) || 0,
                    unit_price: Number(c.unit_price) || 0,
                  }
                : {
                    label: c.label,
                    kind: 'custom_extra',
                    amount: Number(c.amount) || 0,
                  }))
          : [],
      };
      // Phase 128.82 — Fun-Food-Auswahl in `custom_items` mergen (Aufpreis
      // Upgrades + Extras), damit Preis, Rechnung und Statistik automatisch stimmen.
      const _ffItems = funfoodToCustomItems(form.funfood_selections, items);
      if (_ffItems.length > 0) {
        payload.custom_items = [...(payload.custom_items || []), ..._ffItems];
      }
      const offerPayload = offerMode ? {
        ...payload,
        intro_text: offerFields.intro_text || '',
        offer_notes: offerFields.offer_notes || '',
        valid_days: Number(offerFields.valid_days) || 14,
        // Phase 128.53 — Entwurf vs. Final: "Als Entwurf speichern" behält draft,
        // primärer Button schließt das Angebot als versandbereit ab.
        finalize: !saveAsDraft,
      } : null;
      const res = offerMode
        ? (offerInitial?.id
            ? await axios.patch(
                `${API}/admin/offers/${offerInitial.id}/from-booking-payload`,
                offerPayload,
                { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } },
              )
            : await axios.post(
                `${API}/admin/offers/from-booking-payload`,
                offerPayload,
                { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } },
              ))
        : editBooking
        ? await axios.put(
            `${API}/admin/bookings/${editBooking.id}/edit?allow_locked=${form.allow_locked ? 'true' : 'false'}`,
            {
              ...payload,
              // Alle relevanten Felder für den Edit-Endpoint aufbereiten
              notify_customer: !!form.notify_customer,
              admin_note: form.admin_note || undefined,
              // Phase 128.17 — Entscheidung des Admins zum bestehenden Vertrag mitsenden
              preserve_contract: form._preserve_contract_decision ?? null,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}`,
              },
            },
          )
        : adminModeProp
        ? await axios.post(
            `${API}/admin/bookings?notify_customer=${saveAsDraft ? 'false' : (form.notify_customer ? 'true' : 'false')}${saveAsDraft ? '&draft=true' : ''}`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}`,
              },
            },
          )
        : await axios.post(`${API}/bookings`, payload, { withCredentials: true });
      if (offerMode) {
        toast.success(offerInitial?.id ? 'Angebot aktualisiert' : 'Angebot angelegt');
      } else if (editBooking) {
        toast.success(form.notify_customer ? 'Buchung aktualisiert – Kunde informiert' : 'Buchung aktualisiert');
      } else if (adminMode) {
        if (saveAsDraft) {
          toast.success('Entwurf gespeichert — später bearbeiten & bestätigen');
        } else {
          toast.success(form.notify_customer ? 'Buchung angelegt – Bestätigungs-Mail versendet' : 'Buchung angelegt (keine Kunden-Mail)');
        }
      } else {
        toast.success(form.booking_intent === 'binding' ? 'Buchung erfolgreich – wir melden uns mit dem Vertrag!' : 'Anfrage erfolgreich – wir melden uns in Kürze!');
        clearCart();
        // Phase 128.8 — Ref VOR removeItem setzen, damit der Save-Effect (der
        // durch das nachfolgende setForm/setCurrentStep ausgelöst wird) nicht
        // sofort wieder einen frischen Entwurf schreibt.
        wizardSubmittedRef.current = true;
        // Wizard-Draft nach erfolgreicher Absendung löschen
        try { window.localStorage.removeItem(WIZARD_DRAFT_KEY); } catch { /* noop */ }
        try { sessionStorage.removeItem('kidsevent_ai_handover'); } catch { /* noop */ }
        setCurrentStep(0);
      }
      // Reset nur im öffentlichen/manuellen Anlege-Modus — im Edit-Modus schließt
      // die Dialog-Komponente das Formular direkt, ein Reset würde nur
      // zwischenzeitliche Renders mit leerem State provozieren.
      // Phase 128.52 — Im Offer-Edit-Modus (offerInitial gesetzt) NICHT resetten.
      if (!editBooking && !(offerMode && offerInitial?.id)) {
        setForm(initialForm);
        setItems([]);
      }
      if (!adminMode) {
        // Cache the result for the confirmation page (sessionStorage survives the navigation)
        try { sessionStorage.setItem('lastBookingResult', JSON.stringify(res.data)); } catch { /* noop */ }
      }
      onSuccess?.(offerMode ? (res.data?.offer || res.data) : (editBooking ? (res.data?.booking || res.data) : res.data));
      // Smart redirect to the confirmation page (nur öffentliches Formular)
      if (!adminMode && !offerMode && res.data?.id) {
        navigate(`/buchungsbestaetigung/${res.data.id}`);
      }
    } catch (err) {
      // Backend kann bei 409 (Verfügbarkeitskonflikt) ein Dict {message, conflicts}
      // liefern — dieses darf NICHT direkt als React-Child gerendert werden
      // (sonst weiße Seite). Wir extrahieren die Message + eine kurze Konflikt-Liste.
      let detail = err?.response?.data?.detail;
      let msg = 'Fehler beim Senden – bitte versuche es erneut';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (detail && typeof detail === 'object') {
        msg = detail.message || msg;
        if (Array.isArray(detail.conflicts) && detail.conflicts.length > 0) {
          const first = detail.conflicts[0] || {};
          const name = first.name || first.product_id || 'Produkt';
          const range = first.from ? `${first.from}${first.to && first.to !== first.from ? '–' + first.to : ''}` : '';
          msg += ` — z.B. ${name}${range ? ' (' + range + ')' : ''}`;
        }
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 128.52 — Live-Payload-Emitter fuer Offer-Live-Preview.
  // Baut die gleiche Payload-Struktur wie im Submit-Handler zusammen und
  // sendet sie debounced an den Parent (OfferBookingDialog), der daraus
  // die PDF-Vorschau rendert.
  const onPayloadChangeRef = React.useRef(onPayloadChange);
  useEffect(() => { onPayloadChangeRef.current = onPayloadChange; }, [onPayloadChange]);
  useEffect(() => {
    if (!onPayloadChangeRef.current) return () => {};
    const t = setTimeout(() => {
      const payload = {
        ...form,
        service_bundle: effectiveBundleMode !== 'none',
        bundle_mode: form.bundle_mode || 'none',
        bundle_price_override_manual: form.bundle_price_override_manual !== null
          && form.bundle_price_override_manual !== ''
          && form.bundle_price_override_manual !== undefined
          ? Number(form.bundle_price_override_manual) : null,
        bundle_setup_price: form.bundle_setup_price !== null && form.bundle_setup_price !== ''
          ? Number(form.bundle_setup_price) : null,
        bundle_teardown_price: form.bundle_teardown_price !== null && form.bundle_teardown_price !== ''
          ? Number(form.bundle_teardown_price) : null,
        service_betreuung: !!form.service_betreuung,
        betreuung_hours: form.service_betreuung ? Number(form.betreuung_hours || 0) : 0,
        betreuung_price_override: (form.betreuung_price_override === '' || form.betreuung_price_override == null)
          ? null : Number(form.betreuung_price_override),
        kinderschminken_price_override: (form.kinderschminken_price_override === '' || form.kinderschminken_price_override == null)
          ? null : Number(form.kinderschminken_price_override),
        kinderschminken_notes: (form.kinderschminken_notes || '').trim(),
        cleaning_option: (effectiveBundleMode !== 'none') ? null : form.cleaning_option,
        items: items.filter((it) => it.product).map((it) => ({
          product_id: it.product.id,
          product_name: it.product.name,
          rental_duration: it.rental_duration,
          quantity: it.quantity || 1,
          unit_price: 0,
          subtotal: 0,
          with_supervision: it.with_supervision,
          supervision_hours: it.supervision_hours !== undefined && it.supervision_hours !== null && it.supervision_hours !== ''
            ? Number(it.supervision_hours) : null,
        })),
        accessories: (accessories || []).map((a) => ({
          accessory_id: a.accessory.id,
          name: a.accessory.name,
          quantity: a.quantity,
          unit_price: 0,
          subtotal: 0,
        })),
        delivery_price_override: form.delivery_price_override !== '' && form.delivery_price_override != null
          ? Number(form.delivery_price_override) : null,
        total_price_override: form.total_price_override !== '' && form.total_price_override != null
          ? Number(form.total_price_override) : null,
        cleaning_price_total_override: form.cleaning_price_total_override !== '' && form.cleaning_price_total_override != null
          ? Number(form.cleaning_price_total_override) : null,
        custom_items: (form.custom_items || [])
          .filter((c) => (c.label || '').trim())
          .map((c) => (c.kind === 'custom_product'
            ? { label: c.label, kind: 'custom_product', quantity: Number(c.quantity) || 0, rental_duration: Number(c.rental_duration) || 0, unit_price: Number(c.unit_price) || 0 }
            : { label: c.label, kind: 'custom_extra', amount: Number(c.amount) || 0 })),
        intro_text: offerFields.intro_text || '',
        offer_notes: offerFields.offer_notes || '',
        valid_days: Number(offerFields.valid_days) || 14,
      };
      // Phase 128.82 — Fun-Food-Auswahl in Preview-Payload einbeziehen, damit
      // Preisvorschau live inkl. Upgrade/Extras aktualisiert.
      const _ffPrev = funfoodToCustomItems(form.funfood_selections, items);
      if (_ffPrev.length > 0) {
        payload.custom_items = [...(payload.custom_items || []), ..._ffPrev];
      }
      onPayloadChangeRef.current?.(payload);
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, items, accessories, offerFields, effectiveBundleMode]);

  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid="booking-form">
      {/* Phase 128.17 — Mietvertrags-Entscheidungs-Dialog beim Speichern einer Buchung mit signiertem/versendetem Vertrag */}
      {contractDecisionPending && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4" data-testid="contract-decision-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-neutral-900 flex items-center gap-2">
              <Info className="h-5 w-5 text-amber-500" /> Was soll mit dem bestehenden Mietvertrag passieren?
            </h3>
            <p className="text-sm text-neutral-600">
              Diese Buchung hat bereits einen <b>versendeten oder unterschriebenen Mietvertrag</b>. Deine Änderungen betreffen möglicherweise Angaben, die im Vertrag stehen.
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => contractDecisionPending.onDecide(true)}
                className="w-full text-left px-4 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                data-testid="contract-decision-keep"
              >
                <div className="font-bold text-emerald-800">✓ Vertrag beibehalten</div>
                <div className="text-xs text-emerald-700 mt-1">Buchung wird gespeichert, Vertrag &amp; Unterschrift bleiben unverändert. Empfohlen bei kleinen Korrekturen (z. B. Telefonnummer, interne Notiz).</div>
              </button>
              <button
                type="button"
                onClick={() => contractDecisionPending.onDecide(false)}
                className="w-full text-left px-4 py-3 rounded-xl border-2 border-amber-500 bg-amber-50 hover:bg-amber-100 transition-colors"
                data-testid="contract-decision-new"
              >
                <div className="font-bold text-amber-800">↻ Neuen Vertrag erstellen</div>
                <div className="text-xs text-amber-700 mt-1">Der alte, unterschriebene Vertrag wird als PDF-Snapshot in der Buchungshistorie archiviert. Ein neuer Vertrag muss versendet und unterschrieben werden.</div>
              </button>
              <button
                type="button"
                onClick={() => contractDecisionPending.onCancel?.()}
                className="w-full text-center px-4 py-2 text-sm text-neutral-500 hover:text-neutral-800"
                data-testid="contract-decision-cancel"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ============ WIZARD STEPPER (Phase 126) ============ */}
      {wizardMode && (
        <div className="sticky top-0 z-20 -mx-5 md:-mx-8 -mt-5 md:-mt-8 mb-2 bg-white/95 backdrop-blur border-b border-sky-100 px-5 md:px-8 pt-4 pb-3 rounded-t-3xl" data-testid="wizard-stepper">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-neutral-500">Schritt <span className="font-semibold text-sky-700">{currentStep + 1}</span> von {WIZARD_STEPS.length}</div>
            <button
              type="button"
              onClick={toggleWizard}
              className="text-[11px] text-neutral-500 hover:text-sky-700 underline underline-offset-2"
              data-testid="wizard-toggle-classic"
            >
              Zum klassischen Formular wechseln
            </button>
          </div>
          <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${WIZARD_STEPS.length}, minmax(0, 1fr))` }}>
            {WIZARD_STEPS.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={() => jumpToStep(i)}
                disabled={i > currentStep}
                className={`h-1.5 rounded-full transition-colors ${
                  i < currentStep ? 'bg-emerald-500 cursor-pointer hover:bg-emerald-600' :
                  i === currentStep ? 'bg-sky-500' :
                  'bg-neutral-200'
                }`}
                aria-label={`Zu ${s.title} springen`}
                data-testid={`wizard-progress-${i}`}
              />
            ))}
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <div className="text-lg font-bold text-sky-900" data-testid="wizard-step-title">
              {currentStep + 1}. {WIZARD_STEPS[currentStep].title}
            </div>
            <div className="text-[11px] text-neutral-400 hidden md:flex gap-2">
              {WIZARD_STEPS.map((s, i) => (
                <span key={s.key} className={i === currentStep ? 'text-sky-700 font-semibold' : ''}>
                  {s.title}{i < WIZARD_STEPS.length - 1 ? ' ›' : ''}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Klassik-Modus: kleiner Hinweis mit Zurück-Toggle */}
      {wizardCapable && !useWizard && (
        <div className="flex items-center justify-between rounded-xl bg-sky-50 border border-sky-200 px-4 py-2.5 text-xs text-sky-800" data-testid="classic-mode-notice">
          <span>📋 Klassisches Formular — alles auf einer Seite</span>
          <button
            type="button"
            onClick={toggleWizard}
            className="font-semibold text-sky-700 hover:text-sky-900 underline underline-offset-2"
            data-testid="wizard-toggle-guided"
          >
            Zurück zur geführten Anfrage →
          </button>
        </div>
      )}

      {/* Kostenübersicht Mini-Sidebar (nur Mobile: kompakter Balken unter Stepper) */}
      {wizardMode && currentStep < 4 && (
        <div className="lg:hidden rounded-xl bg-sky-50 border border-sky-200 px-3 py-2 flex items-center justify-between text-xs" data-testid="wizard-cost-mobile">
          <div className="text-sky-800">
            <span className="font-semibold">{items.filter(i => i.product).length}</span> Produkte
            {form.event_date_from && <> · {form.event_date_from.split('-').reverse().slice(0,2).join('.')}.</>}
          </div>
          <div className="font-bold text-sky-900" data-testid="mobile-total">
            {total > 0 ? fmt(total) : '—'}
          </div>
        </div>
      )}

      {/* Draft-Restore-Prompt */}
      {wizardMode && draftRestorePrompt && (
        <div className="rounded-xl bg-amber-50 border border-amber-300 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="wizard-draft-prompt">
          <div className="flex-1 text-sm text-amber-900">
            💾 <strong>Entwurf gefunden</strong> — du hast diese Anfrage vor {Math.round((Date.now() - draftRestorePrompt.timestamp) / 3600000)} h begonnen. Fortfahren?
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={restoreDraft} className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold rounded-full px-4 py-1.5" data-testid="wizard-restore-draft">Fortsetzen</button>
            <button type="button" onClick={discardDraft} className="text-sm text-neutral-600 hover:text-neutral-900 underline underline-offset-2" data-testid="wizard-discard-draft">Neu beginnen</button>
          </div>
        </div>
      )}

      {!customerUser && !adminMode && (!wizardMode || currentStep === 0) && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-sky-50 border border-sky-200 px-3 py-2 text-xs md:text-sm text-sky-900" data-testid="booking-form-account-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Gift className="w-4 h-4 text-sky-700 shrink-0" />
            <span className="truncate">Mit Kundenkonto Buchungen verwalten &amp; Bonuspunkte sammeln</span>
          </div>
          <Link
            to="/login"
            className="shrink-0 text-xs font-semibold text-sky-700 hover:text-sky-900 underline underline-offset-2"
            data-testid="booking-form-account-link"
          >
            Konto erstellen →
          </Link>
        </div>
      )}
      {customerUser && !adminMode && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2" data-testid="booking-form-logged-in-hint">
          <Check className="h-4 w-4 shrink-0" /> Angemeldet als <strong>{customerUser.email}</strong> — Buchung wird Deinem Konto zugeordnet.
        </div>
      )}
      {customerUser && !adminMode && bonusDiscount > 0 && (
        <div
          className="rounded-xl bg-gradient-to-r from-yellow-200 to-yellow-100 border border-yellow-400 px-3 py-2 flex items-center gap-2.5"
          data-testid="bf-bonus-banner"
        >
          <div className="text-2xl shrink-0">🎫</div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-sky-900 leading-tight">
              Du sparst {fmt(bonusDiscount)} durch Deine Sammelkarte!
            </div>
            <div className="text-[11px] text-sky-800 leading-tight">
              {bonusCard?.stamps} von {bonusCard?.max_stamps} Stempeln – wird automatisch abgezogen.
            </div>
          </div>
        </div>
      )}
      {customerUser && !adminMode && !bonusDiscount && bonusCard?.next_reward && (
        <div className="rounded-2xl bg-sky-50 border border-sky-200 p-3 text-sm text-sky-800 flex items-center gap-2" data-testid="bf-bonus-progress">
          🎫 Noch {bonusCard.next_reward.stamps - (bonusCard.stamps || 0)} Stempel bis zu {fmt(bonusCard.next_reward.discount_eur)} Rabatt — diese Buchung bringt Dich näher dran!
        </div>
      )}

      {/* 1. Kontaktdaten */}
      {(!wizardMode || currentStep === 0) && (
      <Section title="1. Deine Kontaktdaten" required>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="bf-first-name">Vorname *</Label>
            <Input
              id="bf-first-name"
              data-testid="bf-first-name"
              value={(form.customer_name || '').split(/\s+/).slice(0, -1).join(' ') || ((form.customer_name || '').split(/\s+/).length === 1 ? (form.customer_name || '') : '')}
              onChange={(e) => {
                const first = e.target.value;
                const parts = (form.customer_name || '').trim().split(/\s+/).filter(Boolean);
                const last = parts.length > 1 ? parts[parts.length - 1] : '';
                updateField('customer_name', [first, last].filter(Boolean).join(' ').trim());
              }}
              placeholder="z. B. Max"
              autoComplete="given-name"
              required
            />
          </div>
          <div>
            <Label htmlFor="bf-last-name">Nachname *</Label>
            <Input
              id="bf-last-name"
              data-testid="bf-last-name"
              value={(() => {
                const parts = (form.customer_name || '').trim().split(/\s+/).filter(Boolean);
                return parts.length > 1 ? parts[parts.length - 1] : '';
              })()}
              onChange={(e) => {
                const last = e.target.value;
                const parts = (form.customer_name || '').trim().split(/\s+/).filter(Boolean);
                const first = parts.length > 1 ? parts.slice(0, -1).join(' ') : (parts[0] || '');
                updateField('customer_name', [first, last].filter(Boolean).join(' ').trim());
              }}
              placeholder="z. B. Mustermann"
              autoComplete="family-name"
              required
            />
          </div>
          {/* Hidden combined field to keep test/anchor selectors (bf-name) working */}
          <input type="hidden" data-testid="bf-name" value={form.customer_name || ''} readOnly />
          {/* Phase 133.84 — Firmenname nur bei Bedarf einblenden. Checkbox-Toggle
              „Buchung für Firma / Gewerbe"; wenn bereits ein Firmenname gesetzt
              ist (Edit / Hydration), bleibt der Toggle automatisch aktiv. */}
          <div className="md:col-span-2 space-y-2">
            <label className="inline-flex items-center gap-2 text-sm text-neutral-700 cursor-pointer select-none" data-testid="bf-company-toggle">
              <input
                type="checkbox"
                className="h-4 w-4 accent-sky-600"
                checked={companyEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  setCompanyEnabled(on);
                  if (!on) updateField('company_name', '');
                }}
              />
              <span>Buchung für Firma / Gewerbe</span>
            </label>
            {companyEnabled && (
              <div data-testid="bf-company-wrap">
                <Label htmlFor="bf-company" className="text-xs text-neutral-600">Firmenname</Label>
                <Input
                  id="bf-company"
                  data-testid="bf-company"
                  value={form.company_name}
                  onChange={(e) => updateField('company_name', e.target.value)}
                  placeholder="z. B. Mustermann GmbH"
                  className="mt-1"
                />
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="bf-email">E-Mail *</Label>
            <Input id="bf-email" type="email" data-testid="bf-email" value={form.customer_email} onChange={(e) => updateField('customer_email', e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="bf-phone">Telefon{star('customer_phone')}</Label>
            <Input id="bf-phone" data-testid="bf-phone" value={form.customer_phone} onChange={(e) => updateField('customer_phone', e.target.value)} required={isReq('customer_phone')} />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="bf-street">Straße & Hausnummer{star('customer_street')}</Label>
            <Input id="bf-street" data-testid="bf-street" value={form.customer_street} onChange={(e) => updateField('customer_street', e.target.value)} required={isReq('customer_street')} />
          </div>
          <div>
            <Label htmlFor="bf-zip">PLZ{star('customer_zip')}</Label>
            <Input id="bf-zip" data-testid="bf-zip" value={form.customer_zip} onChange={(e) => updateField('customer_zip', e.target.value)} required={isReq('customer_zip')} />
          </div>
          <div>
            <Label htmlFor="bf-city">Ort{star('customer_city')}</Label>
            <Input id="bf-city" data-testid="bf-city" value={form.customer_city} onChange={(e) => updateField('customer_city', e.target.value)} required={isReq('customer_city')} />
          </div>
        </div>
      </Section>
      )}

      {/* 2. Mietzeitraum & Ort */}
      {(!wizardMode || currentStep === 1) && (
      <>
      <Section title="2. Mietzeitraum & Veranstaltungsort" required>
        {matchedSpecialDates.length > 0 && (
          <div className={`rounded-2xl border-2 p-4 mb-4 shadow-sm ${notYetBookable ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`} data-testid="bf-special-date-banner">
            <div className="flex items-start gap-3">
              <div className="text-2xl">{notYetBookable ? '⛔' : '⚠️'}</div>
              <div className="flex-1">
                {notYetBookable ? (
                  <>
                    <div className="font-bold text-red-900 mb-1">
                      Reservierung noch nicht möglich
                    </div>
                    <div className="text-sm text-red-900">
                      Für „{notYetBookable.name}" können Reservierungen erst <strong>ab dem {new Date(notYetBookable.bookable_from).toLocaleDateString('de-DE')}</strong> entgegengenommen werden.
                      Bitte versuche es ab diesem Datum erneut.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-bold text-amber-900 mb-1">
                      Aktion „{matchedSpecialDates.map((s) => s.name).join(', ')}"
                    </div>
                    {matchedSpecialDates.map((sd) => (
                      <div key={sd.id} className="text-sm text-amber-900 mb-2">
                        {sd.banner_text || `Mindestmietdauer ${sd.min_rental_days} Tage${sd.force_cleaning ? ', Reinigung inklusive' : ''}${sd.only_self_pickup ? ', nur Selbstabholung' : ''}.`}
                      </div>
                    ))}
                    <ul className="text-xs text-amber-800 space-y-0.5 mt-2">
                      {minRentalDaysRequired > 1 && (
                        <li>• Mindestmietdauer: <strong>{minRentalDaysRequired} Tage</strong></li>
                      )}
                      {forceCleaning && <li>• Endreinigung ist verpflichtend und nicht abwählbar</li>}
                      {onlySelfPickup && <li>• Nur Selbstabholung möglich (keine Lieferung)</li>}
                      {deliveryOnRequest && !onlySelfPickup && <li>• Lieferung nur <strong>auf Anfrage / unter Vorbehalt</strong> – wir bestätigen die Verfügbarkeit separat</li>}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Abholung / Anmietung */}
          <div className="rounded-2xl border border-sky-100 bg-sky-50/40 p-3 space-y-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-sky-700">
              📦 Selbstabholung / Anlieferung · Anmiettag
            </div>
            <DatePicker
              testId="bf-from"
              value={form.event_date_from}
              onChange={(v) => {
                updateField('event_date_from', v);
                if (form.event_date_to && v && form.event_date_to < v) updateField('event_date_to', '');
              }}
              placeholder="Datum wählen"
              blockedRanges={blockedRanges}
              showLegend
            />
            <TimeWindowPicker value={form.preferred_time_window} onChange={(v) => updateField('preferred_time_window', v)} />
          </div>
          {/* Rückgabe */}
          <div className="rounded-2xl border border-sky-100 bg-sky-50/40 p-3 space-y-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-sky-700">
              🔁 Rückgabe / Abholung durch uns
            </div>
            <DatePicker
              testId="bf-to"
              value={form.event_date_to}
              onChange={(v) => { setReturnManuallyChanged(true); updateField('event_date_to', v); }}
              placeholder="Datum wählen"
              min={form.event_date_from}
              blockedRanges={blockedRanges}
              showLegend
            />
            <TimeWindowPicker value={form.return_time_window} onChange={(v) => { setReturnManuallyChanged(true); updateField('return_time_window', v); }} />
          </div>
          <p className="md:col-span-2 text-[11px] text-neutral-500 -mt-1">
            Spätester Rückgabetermin = Anmiettag + gebuchte Miettage (24h-basiert, z. B. Fr → Mo bei 3 Tagen, jeweils zur gleichen Uhrzeit). Zeitfenster sind Wünsche – wir bestätigen sie in der Auftragsbestätigung.
          </p>
          {timeWindowWarning && (
            <div className="md:col-span-2 rounded-2xl border-2 border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900" data-testid="bf-time-window-warning">
              ℹ️ {timeWindowWarning}
            </div>
          )}
          <div className="md:col-span-2 rounded-2xl border border-sky-200 bg-white shadow-sm overflow-hidden" data-testid="bf-location-card">
            <div className="flex items-center justify-between px-4 py-2.5 bg-sky-50 border-b border-sky-100">
              <div className="text-[11px] font-bold uppercase tracking-wider text-sky-700 flex items-center gap-1.5">
                📍 Veranstaltungsort / Lieferadresse
              </div>
              {deliveryByUs && (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 border border-amber-300 text-amber-800 rounded-full px-2 py-0.5">
                  Für Lieferung nötig
                </span>
              )}
            </div>
            <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* Option A: gleich wie Rechnung */}
              <button
                type="button"
                onClick={() => updateField('event_location_same_as_billing', true)}
                data-testid="bf-loc-choice-same"
                className={`text-left rounded-xl border-2 p-3 transition-colors ${form.event_location_same_as_billing ? 'border-sky-500 bg-sky-50/70' : 'border-neutral-200 bg-white hover:border-sky-200'}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.event_location_same_as_billing ? 'border-sky-500' : 'border-neutral-300'}`}>
                    {form.event_location_same_as_billing && <span className="w-2 h-2 bg-sky-500 rounded-full" />}
                  </span>
                  <span className="text-sm font-semibold text-neutral-900">Gleich wie Rechnungsadresse</span>
                </div>
                <div className="mt-1 pl-6 text-xs text-neutral-600 leading-snug">
                  Der Veranstaltungsort ist identisch mit der Rechnungsanschrift.
                </div>
                {form.event_location_same_as_billing && (form.customer_street || form.customer_zip || form.customer_city) && (
                  <div className="mt-2 pl-6 text-xs text-neutral-700 leading-snug" data-testid="bf-loc-same-note">
                    <b className="text-neutral-800">{[form.customer_street, [form.customer_zip, form.customer_city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</b>
                  </div>
                )}
              </button>
              {/* Option B: abweichende Adresse */}
              <button
                type="button"
                onClick={() => updateField('event_location_same_as_billing', false)}
                data-testid="bf-loc-choice-different"
                className={`text-left rounded-xl border-2 p-3 transition-colors ${!form.event_location_same_as_billing ? 'border-amber-500 bg-amber-50/70' : 'border-neutral-200 bg-white hover:border-amber-200'}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${!form.event_location_same_as_billing ? 'border-amber-500' : 'border-neutral-300'}`}>
                    {!form.event_location_same_as_billing && <span className="w-2 h-2 bg-amber-500 rounded-full" />}
                  </span>
                  <span className="text-sm font-semibold text-neutral-900">Abweichender Veranstaltungsort</span>
                </div>
                <div className={`mt-1 pl-6 text-xs leading-snug ${!form.event_location_same_as_billing ? 'text-amber-800' : 'text-neutral-500'}`}>
                  Wähle diese Option, wenn die Hüpfburg an einem anderen Ort aufgebaut wird – z. B. auf einem anderen Grundstück, im Park, in einer Halle, in einer Kita oder auf einem Festplatz.
                </div>
              </button>
            </div>
            {!form.event_location_same_as_billing && (
              <div className="px-4 pb-4 pt-1 space-y-3" data-testid="bf-loc-fields-card">
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                  <div className="sm:col-span-6">
                    <Label htmlFor="bf-loc-street" className="text-xs">Straße & Hausnummer <span className="text-neutral-400 font-normal">(optional)</span></Label>
                    <Input id="bf-loc-street" data-testid="bf-loc-street" value={form.event_location_street} onChange={(e) => updateField('event_location_street', e.target.value)} placeholder="z. B. Musterstraße 12" className="mt-1" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="bf-loc-zip" className="text-xs">PLZ{locStar('event_location_zip')}</Label>
                    <Input id="bf-loc-zip" data-testid="bf-loc-zip" value={form.event_location_zip} onChange={(e) => updateField('event_location_zip', e.target.value)} required={deliveryByUs} placeholder="19053" inputMode="numeric" className="mt-1" />
                  </div>
                  <div className="sm:col-span-4">
                    <Label htmlFor="bf-loc-city" className="text-xs">Ort{locStar('event_location_city')}</Label>
                    <Input id="bf-loc-city" data-testid="bf-loc-city" value={form.event_location_city} onChange={(e) => updateField('event_location_city', e.target.value)} required={deliveryByUs} placeholder="Schwerin" className="mt-1" />
                  </div>
                </div>
                {!deliveryByUs && (
                  <p className="text-[11px] text-neutral-500" data-testid="bf-loc-hint">
                    Bei Selbstabholung genügen PLZ und Ort — die Straße hilft uns nur, den Aufbauort für den Kontext zu kennen.
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <Label>Untergrund: Auf welchem Untergrund wird die Hüpfburg aufgebaut?{star('ground_type')}</Label>
            {/* Phase 133.85 — Untergrund als kompakte Icon-Karten statt Dropdown.
                "Sonstiges" blendet einen Freitext ein. Alle Werte landen weiter
                in form.ground_type (unverändertes Datenfeld). */}
            {(() => {
              const GROUND_PRESETS = [
                { v: 'Rasen / Wiese', Icon: Leaf },
                { v: 'Beton / Asphalt / Pflaster', Icon: Construction },
                { v: 'Schotter / Kies', Icon: Mountain },
                { v: 'Erde / Sand', Icon: Mountain },
                { v: 'Kunstrasen', Icon: Trees },
                { v: 'Hallenboden (Indoor)', Icon: Square },
              ];
              const presets = GROUND_PRESETS.map((g) => g.v);
              const isOther = !!(form.ground_type_other_active || (form.ground_type && !presets.includes(form.ground_type)));
              return (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-1" data-testid="bf-ground-cards">
                    {GROUND_PRESETS.map(({ v, Icon }) => {
                      const checked = !isOther && form.ground_type === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => { updateField('ground_type_other_active', false); updateField('ground_type', v); }}
                          aria-pressed={checked}
                          className={`relative flex items-center gap-2.5 rounded-xl px-3 py-2.5 border-2 transition-all text-left ${checked ? 'border-sky-600 bg-sky-50 shadow-sm' : 'border-neutral-200 bg-white hover:border-sky-300'}`}
                          data-testid={`bf-ground-${v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                        >
                          <Icon className={`h-5 w-5 shrink-0 ${checked ? 'text-sky-700' : 'text-neutral-500'}`} />
                          <span className={`text-sm font-semibold leading-tight ${checked ? 'text-sky-900' : 'text-neutral-800'}`}>{v}</span>
                          {checked && (
                            <Check className="h-3.5 w-3.5 text-sky-700 ml-auto shrink-0" />
                          )}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => { updateField('ground_type_other_active', true); updateField('ground_type', ''); }}
                      aria-pressed={isOther}
                      className={`relative flex items-center gap-2.5 rounded-xl px-3 py-2.5 border-2 transition-all text-left ${isOther ? 'border-sky-600 bg-sky-50 shadow-sm' : 'border-neutral-200 bg-white hover:border-sky-300'}`}
                      data-testid="bf-ground-sonstiges"
                    >
                      <MoreHorizontal className={`h-5 w-5 shrink-0 ${isOther ? 'text-sky-700' : 'text-neutral-500'}`} />
                      <span className={`text-sm font-semibold leading-tight ${isOther ? 'text-sky-900' : 'text-neutral-800'}`}>Sonstiges</span>
                      {isOther && (
                        <Check className="h-3.5 w-3.5 text-sky-700 ml-auto shrink-0" />
                      )}
                    </button>
                  </div>
                  {/* Verstecktes Select, damit Validierungs-Anker (bf-ground-select) weiter funktioniert */}
                  <select
                    id="bf-ground-select"
                    data-testid="bf-ground-select"
                    value={isOther ? '__other__' : (form.ground_type || '')}
                    onChange={() => {}}
                    required={isReq('ground_type')}
                    className="sr-only"
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <option value="" disabled>Bitte auswählen …</option>
                    {presets.map((v) => (<option key={v} value={v}>{v}</option>))}
                    <option value="__other__">Sonstiges</option>
                  </select>
                  {isOther && (
                    <Input
                      id="bf-ground"
                      data-testid="bf-ground"
                      value={presets.includes(form.ground_type) ? '' : (form.ground_type || '')}
                      onChange={(e) => updateField('ground_type', e.target.value)}
                      placeholder="z. B. Terrasse mit Rollrasen, Turnhalle, …"
                      required={isReq('ground_type')}
                      className="mt-2"
                    />
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </Section>

      {/* 3. Veranstaltungsart */}
      <Section title="3. Veranstaltungsart" required={isReq('event_type')} testId="bf-event-type">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {EVENT_TYPES.map((t) => {
            const checked = form.event_type === t.v;
            const Icon = t.v === 'private' ? Home
              : t.v === 'oeffentliche' ? Users
              : t.v === 'gewerbliche' ? Building2
              : MoreHorizontal;
            const shortLabel = t.v === 'private' ? 'Privat'
              : t.v === 'oeffentliche' ? 'Öffentlich'
              : t.v === 'gewerbliche' ? 'Gewerblich'
              : 'Sonstiges';
            return (
              <button
                key={t.v}
                type="button"
                onClick={() => updateField('event_type', t.v)}
                aria-pressed={checked}
                className={`relative flex flex-col items-center gap-1.5 rounded-xl px-2 py-3.5 border-2 transition-all text-center ${checked ? 'border-sky-600 bg-sky-50 shadow-sm' : 'border-neutral-200 bg-white hover:border-sky-300'}`}
                data-testid={`bf-event-${t.v}`}
              >
                <Icon className={`h-6 w-6 ${checked ? 'text-sky-700' : 'text-neutral-500'}`} />
                <span className={`text-sm font-semibold leading-tight ${checked ? 'text-sky-900' : 'text-neutral-800'}`}>{shortLabel}</span>
                {checked && (
                  <Check className="h-4 w-4 text-sky-700" style={{ position: 'absolute', top: 6, right: 6 }} />
                )}
              </button>
            );
          })}
        </div>
      </Section>
      </>
      )}

      {/* Schritt 3 (Wizard): Produkte + Zusatz-Service */}
      {(!wizardMode || currentStep === 2) && (
      <>
      {/* 4. Gewünschte Produkte — im Admin-Modus optional, wenn Custom Items genutzt werden */}
      <Section
        title="4. Gewünschte Produkte"
        required={!adminMode || !(form.custom_items || []).some((ci) => (ci.label || '').trim())}
        testId="bf-products-section"
      >
        {/* Multi-Select Dropdown */}
        <ProductMultiSelect
          allProducts={allProducts}
          selectedIds={Array.from(addedProductIds)}
          onToggle={toggleItem}
          unavailableIds={unavailableIds}
          heldIds={heldByOthersIds}
          waitlistJoined={waitlistJoined}
          onJoinWaitlist={(p) => joinWaitlist({ id: p.id, name: p.name })}
          hasDateChosen={Boolean(form.event_date_from)}
          categoryOrder={categoryOrder}
          adminMode={adminMode}
        />

        {items.length > 0 && (
          <div className="space-y-2 mt-4">
            {items.map((it, idx) => {
              // Phase 122M — im Edit-Modus können Items zunächst product=null haben, bis /api/products geladen ist.
              if (!it.product) return null;
              const onRequest = !!it.product?.price_on_request;
              const unit = computeUnitPrice(it.product, it.rental_duration, form.event_date_from, form.event_date_to, it.with_supervision, it.price_option);
              const p = it.product;
              const useWithout = p.requires_supervision && it.with_supervision === false && p.price_without_supervision != null;
              const tiersRaw = useWithout
                ? (Array.isArray(p.tiered_prices_without_supervision) ? p.tiered_prices_without_supervision : [])
                : (Array.isArray(p.tiered_prices) ? p.tiered_prices : []);
              const tieredOn = p.tiered_pricing_enabled && tiersRaw.length > 0;
              const extraOn = p.extra_day_enabled && p.price_per_extra_day != null && p.price_per_extra_day !== '';
              const tiers = tieredOn ? tiersRaw.map((t) => ({ days: parseInt(t.days, 10), price: parseFloat(t.price) })).filter((t) => t.days > 0 && !Number.isNaN(t.price)).sort((a, b) => a.days - b.days) : [];
              return (
                <div key={`${it.product.id}-${idx}`} className="flex items-center gap-3 p-3 rounded-xl bg-sky-50 border border-sky-100" data-testid={`bf-item-${idx}`}>
                  <img
                    src={resolveImageUrl(it.product.image_url, 96)}
                    alt={it.product.name}
                    loading="lazy"
                    className="h-14 w-14 rounded-lg object-cover shrink-0 bg-white border border-sky-100"
                    data-testid={`bf-item-thumb-${idx}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sky-900 truncate flex items-center gap-2">
                      {it.product.name}
                      {it.price_option?.name && (
                        <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold" data-testid={`bf-item-variant-${idx}`}>
                          {it.price_option.name}
                        </span>
                      )}
                      {it.product.requires_setup && <span className="text-[10px] bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-bold">HÜPFBURG</span>}
                      {onRequest && <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold">ANFRAGE</span>}
                    </div>
                    {!onRequest && (
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-sm">
                        {adminMode ? (
                          extraOn ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              value={it.rental_duration > 3 ? 'custom' : String(it.rental_duration)}
                              onChange={(e) => updateItemDuration(idx, e.target.value === 'custom' ? 4 : Number(e.target.value))}
                              className="h-8 rounded border border-sky-200 px-2"
                              data-testid={`bf-duration-select-${idx}`}
                            >
                              <option value="1">1 Tag ({fmt(computeUnitPrice(it.product, 1, form.event_date_from, form.event_date_to, it.with_supervision, it.price_option))})</option>
                              <option value="3">3 Tage / Wochenende ({fmt(computeUnitPrice(it.product, 3, form.event_date_from, form.event_date_to, it.with_supervision, it.price_option))})</option>
                              <option value="custom">Individuell (ab 4 Tage)</option>
                            </select>
                            {it.rental_duration > 3 && (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number" min={4} value={it.rental_duration}
                                  onChange={(e) => updateItemDuration(idx, Math.max(4, parseInt(e.target.value || '4', 10)))}
                                  className="h-8 w-20 rounded border border-sky-200 px-2"
                                  data-testid={`bf-duration-input-${idx}`}
                                />
                                <span className="text-neutral-600">Tage ({fmt(unit)})</span>
                              </div>
                            )}
                          </div>
                          ) : tieredOn ? (
                          <select value={it.rental_duration} onChange={(e) => updateItemDuration(idx, Number(e.target.value))} className="h-8 rounded border border-sky-200 px-2" data-testid={`bf-duration-select-${idx}`}>
                            {tiers.map((t) => (
                              <option key={t.days} value={t.days}>{t.days} Tag{t.days > 1 ? 'e' : ''} ({fmt(t.price)})</option>
                            ))}
                          </select>
                          ) : (
                          <select value={it.rental_duration} onChange={(e) => updateItemDuration(idx, Number(e.target.value))} className="h-8 rounded border border-sky-200 px-2">
                            <option value={1}>1 Tag ({fmt(computeUnitPrice(it.product, 1, form.event_date_from, form.event_date_to, it.with_supervision, it.price_option))})</option>
                            <option value={3}>3 Tage ({fmt(computeUnitPrice(it.product, 3, form.event_date_from, form.event_date_to, it.with_supervision, it.price_option))})</option>
                          </select>
                          )
                        ) : (
                          // Kundenansicht: Dauer wird aus dem gewählten Zeitraum berechnet
                          // und ist NICHT selbst wählbar. Preis wird automatisch neu berechnet.
                          <span
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white border border-sky-200 text-sky-900 font-semibold"
                            data-testid={`bf-duration-readonly-${idx}`}
                          >
                            {it.rental_duration} Tag{it.rental_duration === 1 ? '' : 'e'}
                            <span className="text-neutral-500 font-medium">({fmt(unit)})</span>
                          </span>
                        )}
                      </div>
                    )}
                    {!onRequest && p.requires_supervision && p.price_without_supervision != null && (
                      <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`bf-item-supervision-${idx}`}>
                        <button
                          type="button"
                          onClick={() => updateItemSupervision(idx, true)}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${it.with_supervision !== false ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-neutral-600 border-neutral-300 hover:border-emerald-400'}`}
                          data-testid={`bf-item-supervision-on-${idx}`}
                        >
                          Mit Betreuung{p.supervision_hours ? ` (${p.supervision_hours}h inkl.)` : ''}
                        </button>
                        <button
                          type="button"
                          onClick={() => updateItemSupervision(idx, false)}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${it.with_supervision === false ? 'bg-sky-500 text-white border-sky-500' : 'bg-white text-neutral-600 border-neutral-300 hover:border-sky-400'}`}
                          data-testid={`bf-item-supervision-off-${idx}`}
                        >
                          Ohne Betreuung
                        </button>
                        {/* Phase 122E — Zusatz-Stunden werden im Zusatzservice-Bereich neben der inklusiven Betreuung eingegeben */}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-sky-700">{onRequest ? 'Preis auf Anfrage' : fmt(unit)}</div>
                    <button type="button" onClick={() => removeItem(idx)} className="text-xs text-red-500 hover:underline">entfernen</button>
                  </div>
                </div>
              );
            })}
            {/* Phase 128.83 — Fun-Food-Zusammenfassung direkt unter dem jeweiligen Produkt */}
            {items.map((it, idx) => {
              const p = it.product;
              const sel = form.funfood_selections?.[p?.id];
              if (!p || !sel) return null;
              const upgActive = sel.upgrade && Number(sel.upgrade.price) > 0;
              const extras = Object.entries(sel.extras || {}).filter(([, q]) => Number(q) > 0);
              if (!upgActive && extras.length === 0) return null;
              const qtyItem = Number(it.quantity) || 1;
              const extrasCfg = Array.isArray(p.funfood_extras) ? p.funfood_extras : [];
              const upgAmount = upgActive ? Number(sel.upgrade.price) * qtyItem : 0;
              const extrasAmount = extras.reduce((sum, [label, q]) => {
                const cfg = extrasCfg.find((e) => e.label === label);
                return sum + Number(cfg?.price || 0) * Number(q);
              }, 0);
              const total = upgAmount + extrasAmount;
              return (
                <div key={`ff-sum-${p.id}-${idx}`} className="-mt-1 mb-1 ml-3 pl-3 border-l-2 border-pink-300 space-y-1 text-xs" data-testid={`bf-item-funfood-summary-${idx}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-pink-700 flex items-center gap-1.5">
                    🍭 Zutaten & Extras <span className="text-neutral-400 font-semibold">· {p.name}</span>
                  </div>
                  {upgActive && (
                    <div className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
                      <span className="text-amber-900 font-semibold truncate">
                        ⬆ {sel.upgrade.label}{sel.upgrade.choice ? ` · ${sel.upgrade.choice}` : ''}
                      </span>
                      <span className="text-amber-800 font-bold shrink-0">+{fmt(upgAmount)}</span>
                    </div>
                  )}
                  {extras.map(([label, q]) => {
                    const cfg = extrasCfg.find((e) => e.label === label);
                    const amount = Number(cfg?.price || 0) * Number(q);
                    return (
                      <div key={label} className="flex items-center justify-between gap-2 bg-sky-50 border border-sky-200 rounded-lg px-2 py-1">
                        <span className="text-sky-900 font-semibold truncate">＋ {q}× {label}</span>
                        <span className="text-sky-800 font-bold shrink-0">{fmt(amount)}</span>
                      </div>
                    );
                  })}
                  {total > 0 && (
                    <div className="text-right font-bold text-pink-800">Summe Extras: {fmt(total)}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* Reservierungs-Countdown – direkt beim Warenkorb sichtbar */}
        {holdExpiresAt && items.length > 0 && (
          <div
            className={`flex items-center gap-3 rounded-xl border p-3 mt-3 ${holdRemaining === 0 ? 'border-red-300 bg-red-50' : holdRemaining <= 300 ? 'border-red-300 bg-red-50 animate-pulse' : 'border-sky-200 bg-sky-50'}`}
            data-testid="bf-hold-countdown"
          >
            <Clock className={`h-5 w-5 flex-shrink-0 ${holdRemaining > 0 && holdRemaining > 300 ? 'text-sky-600' : 'text-red-600'}`} />
            <p className={`text-sm ${holdRemaining > 0 && holdRemaining > 300 ? 'text-sky-900' : 'text-red-900'}`}>
              {holdRemaining > 0 ? (
                <>Deine Auswahl ist noch <strong data-testid="bf-hold-timer">{String(Math.floor(holdRemaining / 60)).padStart(2, '0')}:{String(holdRemaining % 60).padStart(2, '0')} Min</strong> für Dich reserviert.{holdRemaining <= 300 && ' Bitte schließe Deine Anfrage bald ab.'}</>
              ) : (
                <>Deine Reservierung ist abgelaufen – ändere die Auswahl, um den Termin erneut zu reservieren.</>
              )}
            </p>
          </div>
        )}
      </Section>

      {/* 4b. Zubehör (Phase 77) — nur bei passendem ausgewähltem Produkt */}
      <AccessoriesPicker categoryIds={selectedCategoryIds} title="Passendes Zubehör (optional)" compact collapsible />

      {/* Phase 128.82 — 4c. Fun-Food-Zutaten & Extras (Zuckerwatte/Popcorn/Slush etc.) */}
      <FunFoodSelector
        items={items}
        selections={form.funfood_selections || {}}
        onChange={(next) => updateField('funfood_selections', next)}
      />

      {/* 5. Zusatz-Service */}
      <Section title="5. Zusatz-Service">
        <div className="space-y-3">
          {adminMode ? (
            /* Phase 122S — Admin: 4 Modi-Auswahl + freier Preis-Override */
            <div className="p-3 rounded-xl bg-white border border-sky-100 space-y-3" data-testid="bf-bundle-admin">
              <div className="font-semibold">Auf-/Abbau-Service (Admin)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { key: 'none', label: 'Kein Auf-/Abbau', hint: 'Kunde übernimmt selbst' },
                  { key: 'both', label: 'Auf- UND Abbau', hint: 'voller Preis' },
                  { key: 'setup_only', label: 'Nur Aufbau', hint: 'halber Preis' },
                  { key: 'teardown_only', label: 'Nur Abbau', hint: 'halber Preis' },
                ].map((opt) => (
                  <label
                    key={opt.key}
                    className={`flex items-start gap-2 p-2.5 rounded-lg border-2 cursor-pointer transition ${form.bundle_mode === opt.key ? 'border-sky-500 bg-sky-50' : 'border-neutral-200 hover:border-sky-300'}`}
                    data-testid={`bf-bundle-mode-${opt.key}`}
                  >
                    <input
                      type="radio"
                      name="bundle_mode"
                      className="mt-1 accent-sky-600"
                      checked={form.bundle_mode === opt.key}
                      onChange={() => updateField('bundle_mode', opt.key)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-neutral-900">{opt.label}</div>
                      <div className="text-xs text-neutral-500">{opt.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
              {form.bundle_mode !== 'none' && (
                <div className="pt-1 border-t border-neutral-100 space-y-2">
                  <div className="text-xs text-neutral-600">
                    Automatisch berechnet: <span className="font-semibold text-neutral-800">{fmt(bundleAutoPrice)}</span> gesamt
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {/* Preis Aufbau — nur bei „Auf UND Abbau" ODER „Nur Aufbau" */}
                    {(form.bundle_mode === 'both' || form.bundle_mode === 'setup_only') && (
                      <label className="block">
                        <span className="block text-xs text-neutral-600 mb-1">Preis Aufbau (€)</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.bundle_setup_price ?? ''}
                          placeholder={`Auto: ${fmt(form.bundle_mode === 'both' ? bundleAutoPrice / 2 : bundleAutoPrice)}`}
                          onChange={(e) => updateField('bundle_setup_price', e.target.value === '' ? null : e.target.value)}
                          className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                          data-testid="bf-bundle-setup-price"
                        />
                      </label>
                    )}
                    {/* Preis Abbau — nur bei „Auf UND Abbau" ODER „Nur Abbau" */}
                    {(form.bundle_mode === 'both' || form.bundle_mode === 'teardown_only') && (
                      <label className="block">
                        <span className="block text-xs text-neutral-600 mb-1">Preis Abbau (€)</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.bundle_teardown_price ?? ''}
                          placeholder={`Auto: ${fmt(form.bundle_mode === 'both' ? bundleAutoPrice / 2 : bundleAutoPrice)}`}
                          onChange={(e) => updateField('bundle_teardown_price', e.target.value === '' ? null : e.target.value)}
                          className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                          data-testid="bf-bundle-teardown-price"
                        />
                      </label>
                    )}
                  </div>
                  <label className="block pt-1">
                    <span className="block text-xs text-neutral-600 mb-1">Gesamtpreis überschreiben (optional — auch 0 € möglich)</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.bundle_price_override_manual ?? ''}
                      placeholder={`Auto: ${fmt(bundleAutoPrice)}`}
                      onChange={(e) => updateField('bundle_price_override_manual', e.target.value === '' ? null : e.target.value)}
                      className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                      data-testid="bf-bundle-override"
                    />
                    {(form.bundle_price_override_manual !== null && form.bundle_price_override_manual !== '') && (
                      <button
                        type="button"
                        onClick={() => updateField('bundle_price_override_manual', null)}
                        className="mt-1 text-xs text-sky-600 hover:underline"
                      >
                        Gesamt-Override entfernen (zurück auf Aufbau+Abbau-Summe)
                      </button>
                    )}
                  </label>
                </div>
              )}
              {bundlePrice > 0 && (
                <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
                  <span className="text-sm text-neutral-600">Endpreis Auf-/Abbau:</span>
                  <span className="font-bold text-sky-700 text-base">+{fmt(bundlePrice)}</span>
                </div>
              )}
            </div>
          ) : (() => {
            // Phase 125.10 — Wenn ALLE Setup-Produkte ihren Aufbau bereits über
            // die Betreuung inklusive haben (z. B. Rodeo mit Mit-Betreuung), soll
            // die Auf-/Abbau-Zeile NICHT als "gegen Aufpreis" erscheinen. Statt-
            // dessen: automatisch aktiv, gesperrt, Label „inklusive".
            const setupIncludedItems = setupItems.filter(
              (it) => it.with_supervision !== false && it.product?.setup_included_with_supervision,
            );
            const allSetupIncluded = setupItems.length > 0 && setupIncludedItems.length === setupItems.length;
            if (allSetupIncluded) {
              return (
                <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="bf-bundle-included">
                  <Checkbox checked disabled className="mt-1" data-testid="bf-bundle-locked" />
                  <div className="flex-1">
                    <div className="font-semibold text-emerald-900">Auf- und Abbau — inklusive</div>
                    <div className="text-sm text-emerald-800/80">
                      Enthalten im Betreuungspreis
                      {setupIncludedItems.length === 1
                        ? ` (${setupIncludedItems[0].product?.name})`
                        : ` (${setupIncludedItems.length} Produkte)`}
                    </div>
                  </div>
                  <div className="font-bold text-emerald-700 whitespace-nowrap">inklusive</div>
                </div>
              );
            }
            return (
              <div
                className={`flex items-start gap-3 rounded-xl border-2 p-3 transition-all ${form.service_bundle ? 'border-sky-500 bg-sky-50 shadow-sm' : 'border-neutral-200 bg-white hover:border-sky-300'}`}
                data-testid="bf-bundle-label"
              >
                <Switch
                  checked={form.service_bundle}
                  onCheckedChange={(v) => updateField('service_bundle', !!v)}
                  data-testid="bf-bundle"
                  aria-label="Auf- und Abbau hinzufügen"
                  className="mt-0.5 shrink-0"
                />
                <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${form.service_bundle ? 'bg-sky-100 text-sky-700' : 'bg-neutral-100 text-neutral-500'}`}>
                  <Wrench className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-bold text-neutral-900 leading-tight">Auf- und Abbau + Endreinigung</div>
                  <div className="text-[13px] text-neutral-600 mt-0.5 leading-snug">
                    Preise je nach Produkt, ab {bundleThreshold} Hüpfburgen günstiger
                    {bundleUniformUnit != null && (
                      <span className="ml-1 text-neutral-500">
                        · aktuell {fmt(bundleUniformUnit)} pro Hüpfburg{bundleIsBulk ? ' (Mengenrabatt)' : ''}
                      </span>
                    )}
                  </div>
                  {setupIncludedItems.length > 0 && bundleEligibleItems.length > 0 && (
                    <div className="mt-1 text-xs text-emerald-700 leading-snug">
                      ✓ {setupIncludedItems.length === 1 ? setupIncludedItems[0].product?.name : `${setupIncludedItems.length} Produkte`} inklusive Betreuung — nur die anderen kosten extra.
                    </div>
                  )}
                </div>
                {form.service_bundle && bundlePrice > 0 && (
                  <span className="text-[15px] font-bold text-sky-700 whitespace-nowrap self-start mt-0.5">+{fmt(bundlePrice)}</span>
                )}
              </div>
            );
          })()}

          {/* Phase 122E — Betreuung:
              1) Zeige die inklusiven Stunden (aus Betreuungs-Produkten wie Rodeo, Bungee) als Hinweis.
              2) Zeige pro Nicht-Betreuungs-Produkt (z.B. Hüpfburg) eine kleine Karte
                 „Betreuung dazubuchen — Stunden: [__] × 35 €/h".
              3) Zusätzlich (nur Admin): einen freien Betreuungs-Override-Betrag. */}
          {(() => {
            const supItemsWithExtra = items
              .map((it, idx) => ({ it, idx }))
              .filter(({ it }) => it.product?.requires_supervision && it.with_supervision !== false);
            const supItems = supItemsWithExtra.map(({ it }) => it);
            const includedHours = supItems.reduce(
              (acc, it) => acc + (Number(it.product?.supervision_hours) || 0) * (Number(it.quantity) || 1),
              0
            );
            if (supItems.length === 0) return null;
            const hasExtraCharge = supItemsWithExtra.some(({ it }) => Number(it.product?.supervision_extra_hour_price) > 0);
            return (
              <div
                className="p-3.5 rounded-xl bg-emerald-50 border-2 border-emerald-200 space-y-3"
                data-testid="bf-betreuung-included-hint"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    <Users className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-base text-emerald-900 leading-tight">
                      {includedHours > 0
                        ? `${includedHours} ${includedHours === 1 ? 'Stunde' : 'Stunden'} Betreuung inklusive`
                        : 'Betreuung inklusive'}
                    </div>
                    <div className="text-sm text-emerald-800/80 mt-0.5">
                      Diese Betreuungsstunden sind bereits im Produktpreis enthalten.
                    </div>
                  </div>
                </div>

                {/* Übersicht der inklusiven Produkte */}
                <div className="rounded-lg bg-white/70 border border-emerald-100 px-3 py-2">
                  <div className="text-xs uppercase tracking-wide text-emerald-800 font-semibold mb-1">Inklusive Betreuung</div>
                  <ul className="text-sm text-emerald-900 space-y-0.5">
                    {supItemsWithExtra.map(({ it, idx }) => {
                      const p = it.product;
                      const defaultH = Number(p?.supervision_hours || 0);
                      const h = defaultH * (Number(it.quantity) || 1);
                      return (
                        <li key={idx} className="flex justify-between gap-2">
                          <span className="truncate">• <b>{p?.name}</b>{Number(it.quantity) > 1 ? ` × ${it.quantity}` : ''}</span>
                          <span className="tabular-nums text-emerald-800 font-semibold">{h > 0 ? `${h} Std.` : 'inkl.'}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Zusatzstunden dazubuchen — pro Betreuungs-Produkt eigener Stundensatz */}
                {hasExtraCharge && (
                  <div className="rounded-lg bg-white border-2 border-emerald-200 px-3 py-3 space-y-2">
                    <div>
                      <div className="font-bold text-base text-emerald-900">
                        Zusätzliche Betreuungsstunden dazubuchen
                      </div>
                      <div className="text-sm text-neutral-600 mt-0.5">
                        Möchtest Du länger betreut werden? Gib pro Produkt die gewünschten
                        <b> zusätzlichen Stunden</b> ein (über die inklusiven Stunden hinaus).
                      </div>
                    </div>
                    <div className="space-y-2 pt-1">
                      {supItemsWithExtra.map(({ it, idx }) => {
                        const p = it.product;
                        const hourly = Number(p?.supervision_extra_hour_price || 0);
                        if (hourly <= 0) return null;
                        const defaultH = Number(p?.supervision_hours || 0);
                        const selectedH = Number(it.supervision_hours ?? defaultH);
                        const extraH = Math.max(0, selectedH - defaultH);
                        const qty = Math.max(1, Number(it.quantity || 1));
                        return (
                          <div
                            key={idx}
                            className="flex flex-wrap items-center gap-2 text-sm bg-emerald-50/60 rounded-md px-2.5 py-2"
                            data-testid={`bf-sup-extra-row-${idx}`}
                          >
                            <div className="flex-1 min-w-[160px]">
                              <div className="font-medium text-emerald-900">
                                {p?.name}{qty > 1 ? ` × ${qty}` : ''}
                              </div>
                              <div className="text-[11px] text-neutral-600">
                                Inklusive: {defaultH} Std. · Zusatzstunde {fmt(hourly)}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                              <Label className="text-xs">Zusatz-Std.:</Label>
                              <Input
                                type="number"
                                min={0}
                                step={0.5}
                                value={extraH}
                                onChange={(e) => {
                                  const v = Math.max(0, Number(e.target.value) || 0);
                                  setItems(items.map((x, i) => i === idx ? { ...x, supervision_hours: defaultH + v } : x));
                                }}
                                className="w-20 h-8"
                                data-testid={`bf-sup-extra-input-${idx}`}
                              />
                              <span className="text-xs text-neutral-600">
                                × {fmt(hourly)}/Std.
                              </span>
                              <span className="text-sm text-emerald-800 font-semibold min-w-[70px] text-right" data-testid={`bf-sup-extra-price-${idx}`}>
                                = {fmt(extraH * hourly * qty)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Phase 122E — Betreuung für Hüpfburg(en) dazubuchen — 1 Karte, Staffelpreis */}
          {huepfburgTotalQty > 0 && (
            <div
              className={`flex items-start gap-3 rounded-xl border-2 p-3 transition-all ${form.service_betreuung ? 'border-sky-500 bg-sky-50 shadow-sm' : 'border-neutral-200 bg-white hover:border-sky-300'}`}
              data-testid="bf-huepfburg-betreuung-card"
            >
              <Switch
                checked={!!form.service_betreuung}
                onCheckedChange={(v) => {
                  updateField('service_betreuung', !!v);
                  if (v && (!form.betreuung_hours || Number(form.betreuung_hours) <= 0)) {
                    updateField('betreuung_hours', 2);
                  }
                }}
                data-testid="bf-betreuung"
                aria-label="Betreuung hinzufügen"
                className="mt-0.5 shrink-0"
              />
              <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${form.service_betreuung ? 'bg-sky-100 text-sky-700' : 'bg-neutral-100 text-neutral-500'}`}>
                <Users className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-bold text-neutral-900 leading-tight">
                  Betreuung für {huepfburgTotalQty === 1 ? 'Hüpfburg' : 'Hüpfburgen'} dazubuchen
                </div>
                <div className="text-[13px] text-neutral-600 mt-0.5 leading-snug">
                  Erste Hüpfburg {fmt(globalBetreuungPerHour)}/Std., jede weitere +{fmt(EXTRA_HUEPFBURG_PER_HOUR)}/Std.
                </div>

                {form.service_betreuung && (
                  <>
                    <div className="text-xs text-neutral-500 mt-1.5 leading-snug">
                      Unser Personal betreut Deine {huepfburgTotalQty === 1 ? 'Hüpfburg' : 'Hüpfburgen'} während der Veranstaltung
                      (max. {HUEPFBURGEN_PER_STAFF} Hüpfburgen pro Betreuer).
                    </div>

                    {/* Übersicht: welche Hüpfburgen werden betreut */}
                    <div className="mt-2 rounded-lg bg-white/60 border border-sky-100 px-2.5 py-2" data-testid="bf-huepfburg-list">
                      <div className="text-[11px] uppercase tracking-wide text-neutral-500 font-semibold mb-1">Betreute Produkte</div>
                      <ul className="text-xs text-neutral-700 space-y-0.5">
                        {huepfburgLines.map((l, i) => (
                          <li key={i} className="flex justify-between gap-2">
                            <span className="truncate">• {l.product_name}</span>
                            <span className="tabular-nums text-neutral-500">× {l.qty}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-1.5 text-[11px] text-neutral-600">
                        Gesamt: <b>{huepfburgTotalQty} {huepfburgTotalQty === 1 ? 'Hüpfburg' : 'Hüpfburgen'}</b>
                        {' · '}
                        <b>{huepfburgStaffCount} Betreuer</b> benötigt
                        {' · '}
                        Stundensatz: <b>{fmt(huepfburgRatePerHour)}/Std.</b>
                      </div>
                    </div>

                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <Label className="text-xs">Stunden:</Label>
                      <Input
                        type="number"
                        min={0.5}
                        max={24}
                        step={0.5}
                        value={form.betreuung_hours || ''}
                        onChange={(e) => updateField('betreuung_hours', Number(e.target.value) || 0)}
                        className="w-20 h-8"
                        data-testid="bf-betreuung-hours"
                      />
                      <span className="text-xs text-neutral-600">
                        × {fmt(huepfburgRatePerHour)}/Std.
                      </span>
                      <span className="text-xs text-sky-700 font-semibold" data-testid="bf-huepfburg-betreuung-price">
                        = {fmt(huepfburgBetreuungPrice)}
                      </span>
                    </div>
                  </>
                )}
              </div>
              {!form.service_betreuung && (
                <div className="text-[13px] text-neutral-500 whitespace-nowrap self-start mt-0.5">
                  ab {fmt(globalBetreuungPerHour)}/Std.
                </div>
              )}
              {form.service_betreuung && huepfburgBetreuungPrice > 0 && (
                <span className="text-[15px] font-bold text-sky-700 whitespace-nowrap self-start mt-0.5">+{fmt(huepfburgBetreuungPrice)}</span>
              )}
            </div>
          )}

          {/* Admin-Override Betreuungspreis (frei wählbar, überschreibt Auto-Berechnung) */}
          {adminMode && (derivedServiceBetreuung || betreuungOverrideActive) && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex flex-wrap items-center gap-2" data-testid="bf-betreuung-override-block">
              <Label className="text-xs text-neutral-700 font-semibold">Admin-Override Betreuungspreis (€):</Label>
              <Input
                data-testid="bf-betreuung-override"
                type="number"
                step="0.01"
                min="0"
                placeholder={`auto = ${fmt(betreuungPriceAuto)}`}
                value={form.betreuung_price_override ?? ''}
                onChange={(e) => updateField('betreuung_price_override', e.target.value)}
                className="w-32 h-8 text-xs"
              />
              {betreuungOverrideActive && (
                <>
                  <span className="text-xs text-amber-800 font-semibold">Aktiv: {fmt(Number(betreuungOverrideRaw))}</span>
                  <button
                    type="button"
                    onClick={() => updateField('betreuung_price_override', '')}
                    className="text-[11px] text-red-600 underline underline-offset-2"
                  >zurücksetzen</button>
                </>
              )}
            </div>
          )}

          <div
            className={`flex items-start gap-3 rounded-xl border-2 p-3 transition-all ${form.service_kinderschminken ? 'border-sky-500 bg-sky-50 shadow-sm' : 'border-neutral-200 bg-white hover:border-sky-300'}`}
            data-testid="bf-schminken-label"
          >
            <Switch
              checked={form.service_kinderschminken}
              onCheckedChange={(v) => updateField('service_kinderschminken', !!v)}
              data-testid="bf-schminken"
              aria-label="Kinderschminken hinzufügen"
              className="mt-0.5 shrink-0"
            />
            <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${form.service_kinderschminken ? 'bg-sky-100 text-sky-700' : 'bg-neutral-100 text-neutral-500'}`}>
              <Palette className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-bold text-neutral-900 leading-tight">Kinderschminken</div>
              <div className="text-[13px] text-neutral-600 mt-0.5">
                {kinderschminkenPrice > 0 ? fmt(Number(pricing.kinderschminken_price || 0)) : 'Preis auf Anfrage'}
              </div>
              {/* Phase 128.16 — Admin-Override für Sonderpreis */}
              {adminMode && form.service_kinderschminken && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-neutral-600">Sonderpreis (€):</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-24 px-2 py-1 border border-neutral-300 rounded text-sm"
                    value={form.kinderschminken_price_override ?? ''}
                    onChange={(e) => updateField('kinderschminken_price_override', e.target.value === '' ? null : Number(e.target.value))}
                    placeholder={String(pricing.kinderschminken_price || 0)}
                    data-testid="bf-schminken-override"
                  />
                  {form.kinderschminken_price_override !== null && form.kinderschminken_price_override !== '' && form.kinderschminken_price_override !== undefined && (
                    <button
                      type="button"
                      className="text-xs text-neutral-500 underline"
                      onClick={() => updateField('kinderschminken_price_override', null)}
                    >
                      zurücksetzen
                    </button>
                  )}
                </div>
              )}
              {form.service_kinderschminken && (
                <div className="mt-2">
                  <Input
                    type="text"
                    value={form.kinderschminken_notes || ''}
                    onChange={(e) => updateField('kinderschminken_notes', e.target.value)}
                    className="text-sm"
                    placeholder="z.B. 3 Stunden · 2 Personen · bis 14 Uhr"
                    data-testid="bf-schminken-notes"
                  />
                </div>
              )}
            </div>
            {form.service_kinderschminken && kinderschminkenPrice > 0 && (
              <span className="text-[15px] font-bold text-sky-700 whitespace-nowrap self-start mt-1">+{fmt(kinderschminkenPrice)}</span>
            )}
          </div>

          {derivedServiceBetreuung && deliveryPrice > 0 && (
            <p className="text-xs text-emerald-700 px-1">✓ Mit Betreuung: 50 % Rabatt auf die Lieferkosten</p>
          )}
        </div>
      </Section>

      {/* Admin-only: Weitere Positionen / eigene Produkte / Rabatte */}
      {adminMode && (
        <Section title="Weitere Positionen / Rabatte (Admin)" testId="bf-custom-items-section">
          <div className="space-y-2">
            {(form.custom_items || []).length === 0 && (
              <p className="text-xs text-neutral-500">
                Keine zusätzlichen Positionen. Füge eine Position (z.B. eigenes Produkt oder Rabatt) hinzu.
              </p>
            )}
            {(form.custom_items || []).map((ci, i) => {
              const setCi = (patch) => {
                const arr = [...(form.custom_items || [])];
                arr[i] = { ...arr[i], ...patch };
                updateField('custom_items', arr);
              };
              const removeCi = () => {
                const arr = (form.custom_items || []).filter((_, idx) => idx !== i);
                updateField('custom_items', arr);
              };
              const kind = ci.kind || 'custom_extra';
              return (
                <div
                  key={i}
                  className="p-3 rounded-xl bg-white border border-sky-100 space-y-2"
                  data-testid={`bf-custom-item-${i}`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={kind}
                      onChange={(e) => setCi({ kind: e.target.value })}
                      className="h-8 rounded border border-sky-200 px-2 text-xs"
                      data-testid={`bf-custom-item-kind-${i}`}
                    >
                      <option value="custom_extra">Position / Rabatt (Betrag)</option>
                      <option value="custom_product">Eigenes Produkt (Menge × Tage × Preis)</option>
                    </select>
                    <button
                      type="button"
                      onClick={removeCi}
                      className="ml-auto text-xs text-red-600 hover:underline"
                      data-testid={`bf-custom-item-remove-${i}`}
                    >
                      entfernen
                    </button>
                  </div>
                  {kind === 'custom_product' ? (
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                      <div className="md:col-span-3">
                        <Label className="text-xs">Bezeichnung</Label>
                        <Input
                          value={ci.label || ''}
                          onChange={(e) => setCi({ label: e.target.value })}
                          className="mt-1 h-8 text-sm"
                          placeholder="z.B. Sonderproduktion Hüpfburg XY"
                          data-testid={`bf-custom-item-label-${i}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Menge</Label>
                        <Input
                          type="number"
                          min={1}
                          step={1}
                          value={ci.quantity || ''}
                          onChange={(e) => setCi({ quantity: e.target.value })}
                          className="mt-1 h-8 text-sm"
                          data-testid={`bf-custom-item-qty-${i}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Tage</Label>
                        <Input
                          type="number"
                          min={1}
                          step={1}
                          value={ci.rental_duration || ''}
                          onChange={(e) => setCi({ rental_duration: e.target.value })}
                          className="mt-1 h-8 text-sm"
                          data-testid={`bf-custom-item-days-${i}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Preis/Stk (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={ci.unit_price || ''}
                          onChange={(e) => setCi({ unit_price: e.target.value })}
                          className="mt-1 h-8 text-sm"
                          data-testid={`bf-custom-item-unit-${i}`}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                      <div className="md:col-span-4">
                        <Label className="text-xs">Bezeichnung</Label>
                        <Input
                          value={ci.label || ''}
                          onChange={(e) => setCi({ label: e.target.value })}
                          className="mt-1 h-8 text-sm"
                          placeholder="z.B. Rabatt Stammkunde, Sonderleistung …"
                          data-testid={`bf-custom-item-label-${i}`}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs">Betrag (€, negativ für Rabatt)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={ci.amount || ''}
                          onChange={(e) => setCi({ amount: e.target.value })}
                          className="mt-1 h-8 text-sm"
                          data-testid={`bf-custom-item-amount-${i}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => updateField('custom_items', [...(form.custom_items || []), { kind: 'custom_extra', label: '', amount: '' }])}
                className="px-3 py-1.5 rounded-lg border border-sky-300 bg-sky-50 text-sky-700 text-xs font-semibold hover:bg-sky-100"
                data-testid="bf-add-custom-extra"
              >
                + Position / Rabatt
              </button>
              <button
                type="button"
                onClick={() => updateField('custom_items', [...(form.custom_items || []), { kind: 'custom_product', label: '', quantity: 1, rental_duration: 1, unit_price: '' }])}
                className="px-3 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100"
                data-testid="bf-add-custom-product"
              >
                + Eigenes Produkt
              </button>
            </div>
          </div>
        </Section>
      )}

      {/* Phase 128.52 — Angebots-Optionen (nur im Offer-Modus) */}
      {offerMode && (
        <Section title="Angebots-Optionen" testId="bf-offer-fields-section">
          <div className="space-y-3">
            <div>
              <Label htmlFor="bf-offer-intro">Einleitungstext (erscheint oben im PDF)</Label>
              <textarea
                id="bf-offer-intro"
                value={offerFields.intro_text}
                onChange={(e) => setOfferFields((f) => ({ ...f, intro_text: e.target.value }))}
                className="w-full mt-1 p-2 border rounded-md text-sm min-h-[70px]"
                placeholder="Sehr geehrte Damen und Herren, gerne unterbreiten wir Ihnen folgendes Angebot..."
                data-testid="bf-offer-intro"
              />
            </div>
            <div>
              <Label htmlFor="bf-offer-notes">Schlussanmerkung (erscheint unten im PDF)</Label>
              <textarea
                id="bf-offer-notes"
                value={offerFields.offer_notes}
                onChange={(e) => setOfferFields((f) => ({ ...f, offer_notes: e.target.value }))}
                className="w-full mt-1 p-2 border rounded-md text-sm min-h-[60px]"
                placeholder="Wir freuen uns auf Ihre Rückmeldung!"
                data-testid="bf-offer-notes"
              />
            </div>
            <div className="max-w-xs">
              <Label htmlFor="bf-offer-valid">Gültig für (Tage)</Label>
              <Input
                id="bf-offer-valid"
                type="number"
                min={1}
                max={365}
                value={offerFields.valid_days}
                onChange={(e) => setOfferFields((f) => ({ ...f, valid_days: Number(e.target.value) || 14 }))}
                data-testid="bf-offer-valid-days"
              />
            </div>
          </div>
        </Section>
      )}
      </>
      )}

      {/* Schritt 4 (Wizard): Lieferung + Reinigung + Details + Zahlung */}
      {(!wizardMode || currentStep === 3) && (
      <>
      {/* 6. Lieferung */}
      <Section title="6. Lieferung oder Selbstabholung?" required testId="bf-delivery-option-section">
        {/* Phase 133.83 — Lieferoptionen als kompakte 2-Spalten-Karten mit
            Icon, Label und rechtsbündigem Preis. Mobile wechselt zu 1 Spalte. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {deliveryOptions.map((d) => {
            const checked = form.delivery_option === d.v;
            const Icon = d.v.startsWith('pickup') ? Package
              : d.v === 'delivery_schwerin' ? Truck
              : MapPin;
            const priceLabel = d.v === 'delivery_other'
              ? 'Preis nach km'
              : (d.price > 0 ? `+${fmt(d.price)}` : 'kostenlos');
            return (
              <button
                type="button"
                key={d.v}
                onClick={() => updateField('delivery_option', d.v)}
                aria-pressed={checked}
                className={`flex items-start gap-3 rounded-xl px-3 py-2.5 border-2 text-left transition-all ${checked ? 'border-sky-600 bg-sky-50 shadow-sm' : 'border-neutral-200 bg-white hover:border-sky-300'}`}
                data-testid={`bf-delivery-${d.v}`}
              >
                <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${checked ? 'text-sky-700' : 'text-neutral-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-[15px] font-semibold leading-tight ${checked ? 'text-sky-900' : 'text-neutral-900'}`}>{d.l}</div>
                  {deliveryOnRequest && d.v.startsWith('delivery') && (
                    <div className="text-xs font-bold uppercase text-orange-700 mt-0.5">Auf Anfrage</div>
                  )}
                </div>
                <div className={`text-[15px] font-bold shrink-0 ${checked ? 'text-sky-700' : 'text-neutral-700'}`}>{priceLabel}</div>
              </button>
            );
          })}
        </div>
        {deliveryOnRequest && (form.delivery_option || '').startsWith('delivery') && (
          <div className="rounded-xl border border-orange-300 bg-orange-50 px-3 py-2 text-xs text-orange-900" data-testid="bf-delivery-on-request-note">
            <strong>Lieferung nur auf Anfrage.</strong> An diesem Termin können wir die Lieferung nicht garantieren – wir bestätigen separat.
          </div>
        )}
        {deliveryNote && (
          <p className="text-xs text-sky-700 px-1">{deliveryNote}</p>
        )}
          {/* Admin-Override: Lieferkosten manuell setzen (überschreibt Auto-Berechnung) */}
          {adminMode && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex flex-wrap items-center gap-2 mt-2" data-testid="bf-delivery-override-block">
              <Label className="text-xs text-neutral-700 font-semibold">Admin-Override Lieferkosten (€):</Label>
              <Input
                data-testid="bf-delivery-override"
                type="number"
                step="0.01"
                min="0"
                placeholder={`auto = ${fmt(deliveryPriceAuto)}`}
                value={form.delivery_price_override ?? ''}
                onChange={(e) => updateField('delivery_price_override', e.target.value)}
                className="w-32 h-8 text-xs"
              />
              {deliveryOverrideActive && (
                <>
                  <span className="text-xs text-amber-800 font-semibold">
                    Aktiv: {fmt(Number(form.delivery_price_override))}
                  </span>
                  <button
                    type="button"
                    onClick={() => updateField('delivery_price_override', '')}
                    className="text-[11px] text-red-600 underline underline-offset-2"
                    data-testid="bf-delivery-override-reset"
                  >zurücksetzen</button>
                </>
              )}
              {!deliveryOverrideActive && (
                <span className="text-[11px] text-neutral-500">leer = automatisch berechnet</span>
              )}
            </div>
          )}
      </Section>

      {/* 7. Reinigung (nur wenn kein Bundle) */}
      {effectiveBundleMode === 'none' && (
        <Section title="7. Reinigung" required testId="bf-cleaning-section">
          {/* Phase 133.83 — Zwei kompakte Karten statt großer bunter Flächen.
              Empfohlene Option bleibt leicht grün hervorgehoben. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <label
              className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${form.cleaning_option === 'kidsevent' ? 'border-emerald-500 bg-emerald-50 shadow-sm' : 'border-neutral-200 bg-white hover:border-emerald-300'}`}
              data-testid="bf-cleaning-kidsevent-label"
            >
              <input
                type="radio"
                name="cleaning"
                checked={form.cleaning_option === 'kidsevent'}
                onChange={() => updateField('cleaning_option', 'kidsevent')}
                className="mt-0.5 h-4 w-4 accent-emerald-600 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold leading-tight flex items-center flex-wrap gap-1.5">
                  <span>Endreinigung durch uns</span>
                  <span className="text-[10px] font-bold uppercase bg-emerald-600 text-white px-1.5 py-0.5 rounded-full">Empfohlen</span>
                  {form.cleaning_option === 'kidsevent' && cleaningPrice > 0 && (
                    <span className="text-[15px] font-bold text-emerald-700 ml-auto">+{fmt(cleaningPrice)}</span>
                  )}
                </div>
                <div className="text-[13px] text-neutral-600 mt-0.5 leading-snug">
                  Normale Verschmutzung inklusive. Bei nasser Rückgabe können zusätzliche Trocknungskosten entstehen.
                </div>
              </div>
            </label>
            <label
              className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-colors ${forceCleaning ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${form.cleaning_option === 'eigenreinigung' ? 'border-amber-500 bg-amber-50 shadow-sm' : 'border-neutral-200 bg-white hover:border-amber-300'}`}
              data-testid="bf-cleaning-eigen-label"
              title={forceCleaning ? 'An diesem besonderen Termin ist die Endreinigung verpflichtend' : undefined}
            >
              <input
                type="radio"
                name="cleaning"
                checked={form.cleaning_option === 'eigenreinigung'}
                disabled={forceCleaning}
                onChange={() => updateField('cleaning_option', 'eigenreinigung')}
                className="mt-0.5 h-4 w-4 accent-amber-500 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold leading-tight">Eigenreinigung</div>
                <div className="text-[13px] text-neutral-600 mt-0.5 leading-snug">Sauber und trocken zurückgeben. Bei starker Verschmutzung können Reinigungskosten nachberechnet werden.</div>
              </div>
            </label>
          </div>
          {/* Phase 125.13 — Admin-Override für die gesamte Endreinigung
              (nur im Admin-Modus): Kulanz / Sonderpreis pauschal. */}
          {adminMode && form.cleaning_option === 'kidsevent' && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex flex-wrap items-center gap-2" data-testid="bf-cleaning-override-block">
              <Label className="text-xs text-neutral-700 font-semibold">Admin-Override Endreinigung (€):</Label>
              <Input
                data-testid="bf-cleaning-override"
                type="number"
                step="0.01"
                min="0"
                placeholder={`auto = ${fmt(cleaningPriceAuto)}`}
                value={form.cleaning_price_total_override ?? ''}
                onChange={(e) => updateField('cleaning_price_total_override', e.target.value)}
                className="w-32 h-8 text-xs"
              />
              {form.cleaning_price_total_override !== '' && form.cleaning_price_total_override != null && (
                <>
                  <span className="text-xs text-amber-800 font-semibold">
                    Aktiv: {fmt(Number(form.cleaning_price_total_override))}
                  </span>
                  <button
                    type="button"
                    onClick={() => updateField('cleaning_price_total_override', '')}
                    className="text-[11px] text-red-600 underline underline-offset-2"
                    data-testid="bf-cleaning-override-reset"
                  >zurücksetzen</button>
                </>
              )}
              {(form.cleaning_price_total_override === '' || form.cleaning_price_total_override == null) && (
                <span className="text-[11px] text-neutral-500">leer = automatisch (pro Produkt)</span>
              )}
            </div>
          )}
        </Section>
      )}

      {/* 9. Weitere Angaben — kombiniert: Aufmerksam-geworden-Karten, Nachricht, Kontaktweg-Karten */}
      <Section title="8. Weitere Angaben" required={isReq('aware_through') || true} testId="bf-aware-through">
        {/* 9a. Wie bist du auf uns aufmerksam geworden — Icon-Karten */}
        <div>
          <Label className="text-sm font-semibold text-neutral-700 mb-1.5 block">
            Wie bist du auf uns aufmerksam geworden?{isReq('aware_through') && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {(() => {
            const AWARE_CARDS = [
              { v: 'Google', Icon: Megaphone },
              { v: 'Facebook/Instagram/Social Media', Icon: Facebook },
              { v: 'Empfehlung', Icon: UserCheck },
              { v: 'Werbung auf Veranstaltungen', Icon: Megaphone },
              { v: 'Kita/Schule', Icon: School },
              { v: 'Bestandskunde', Icon: Check },
              { v: 'Flyer', Icon: Newspaper },
              { v: 'Anhänger-Werbung', Icon: Truck },
              { v: 'Sonstiges', Icon: MoreHorizontal },
            ];
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="bf-aware-cards">
                {AWARE_CARDS.map(({ v, Icon }) => {
                  const checked = awareChoice === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => handleAwareChoice(v)}
                      aria-pressed={checked}
                      className={`relative flex items-center gap-2.5 rounded-xl px-3 py-2.5 border-2 transition-all text-left ${checked ? 'border-sky-600 bg-sky-50 shadow-sm' : 'border-neutral-200 bg-white hover:border-sky-300'}`}
                      data-testid={`bf-aware-${v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                    >
                      <Icon className={`h-5 w-5 shrink-0 ${checked ? 'text-sky-700' : 'text-neutral-500'}`} />
                      <span className={`text-sm font-semibold leading-tight ${checked ? 'text-sky-900' : 'text-neutral-800'}`}>{v}</span>
                      {checked && (
                        <Check className="h-3.5 w-3.5 text-sky-700 ml-auto shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })()}
          {/* Verstecktes Select für bestehende Validierungs-Hooks (bf-aware) */}
          <select
            data-testid="bf-aware"
            value={awareChoice}
            onChange={(e) => handleAwareChoice(e.target.value)}
            required={isReq('aware_through')}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
          >
            <option value="">Bitte auswählen …</option>
            {AWARE_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
          </select>
          {awareChoice === 'Sonstiges' && (
            <Input
              data-testid="bf-aware-other"
              className="mt-2"
              value={awareOther}
              onChange={(e) => handleAwareOther(e.target.value)}
              placeholder="Bitte kurz beschreiben …"
            />
          )}
        </div>

        {/* 9b. Nachricht / Wünsche */}
        <div>
          <Label className="text-sm font-semibold text-neutral-700 mb-1 block">
            Nachricht / Wünsche{isReq('notes') && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Textarea
            data-testid="bf-notes"
            rows={3}
            value={form.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            placeholder="Besondere Wünsche, Fragen, Hinweise …"
          />
        </div>

        {/* 9c. Kontaktaufnahme — Icon-Karten (Mehrfachauswahl) */}
        <div data-testid="bf-contact-methods">
          <Label className="text-sm font-semibold text-neutral-700 mb-1.5 block">
            Kontakt bevorzugt<span className="text-red-500 ml-1">*</span>
          </Label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, testId: 'bf-contact-whatsapp-label', color: 'emerald' },
              { v: 'email', label: 'E-Mail', Icon: MailIcon, testId: 'bf-contact-email-label', color: 'sky' },
            ].map(({ v, label, Icon, testId, color }) => {
              const checked = form.contact_methods.includes(v);
              const palette = color === 'emerald'
                ? { offBorder: 'border-emerald-200', offBg: 'bg-emerald-50/50', offIcon: 'text-emerald-500', offText: 'text-emerald-800', hover: 'hover:border-emerald-400 hover:bg-emerald-50', onBorder: 'border-emerald-600', onBg: 'bg-emerald-100', onIcon: 'text-emerald-700', onText: 'text-emerald-900', onCheck: 'text-emerald-700' }
                : { offBorder: 'border-sky-200',     offBg: 'bg-sky-50/50',     offIcon: 'text-sky-500',     offText: 'text-sky-800',     hover: 'hover:border-sky-400 hover:bg-sky-50',         onBorder: 'border-sky-600',     onBg: 'bg-sky-100',     onIcon: 'text-sky-700',     onText: 'text-sky-900',     onCheck: 'text-sky-700' };
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => toggleContactMethod(v)}
                  aria-pressed={checked}
                  className={`relative flex items-center gap-2.5 rounded-xl px-3 py-2.5 border-2 transition-all text-left ${checked ? `${palette.onBorder} ${palette.onBg} shadow-sm` : `${palette.offBorder} ${palette.offBg} ${palette.hover}`}`}
                  data-testid={testId}
                >
                  <Icon className={`h-5 w-5 shrink-0 ${checked ? palette.onIcon : palette.offIcon}`} />
                  <span className={`text-sm font-semibold leading-tight ${checked ? palette.onText : palette.offText}`}>{label}</span>
                  {checked && (
                    <Check className={`h-4 w-4 ml-auto shrink-0 ${palette.onCheck}`} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </Section>

      {/* 11. Zahlung */}
      <Section title="9. Zahlungsart (optional)">
        <p className="text-sm text-neutral-500">Mehrfachauswahl möglich — wir stimmen die konkrete Zahlart dann im Angebot ab.</p>
        {allowedPaymentOptions && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="bf-payment-restriction">
            <strong>Hinweis:</strong> An diesem Termin sind nur folgende Zahlungsarten möglich:{' '}
            {allowedPaymentOptions
              .map((v) => (PAYMENT_OPTION_CARDS.find((c) => c.v === v) || {}).label || v)
              .join(', ')}.
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {PAYMENT_OPTION_CARDS.map((p) => {
            const Icon = p.icon === 'paypal' ? Wallet
              : p.icon === 'bank' ? Landmark
              : p.icon === 'cash' ? Banknote
              : p.icon === 'card' ? CreditCard
              : FileText;
            const checked = form.payment_options.includes(p.v);
            const disabled = !!(allowedPaymentOptions && !allowedPaymentOptions.includes(p.v));
            return (
              <button
                type="button"
                key={p.v}
                onClick={() => { if (!disabled) togglePaymentOption(p.v); }}
                aria-pressed={checked}
                disabled={disabled}
                title={disabled ? 'An diesem Termin nicht möglich' : undefined}
                className={`relative flex flex-col items-center gap-1.5 rounded-xl px-2 py-3.5 border-2 transition-all text-center ${
                  disabled
                    ? 'border-neutral-200 bg-neutral-100 opacity-50 cursor-not-allowed'
                    : checked
                    ? 'border-sky-600 bg-sky-50 shadow-sm'
                    : 'border-neutral-200 bg-white hover:border-sky-300'
                }`}
                data-testid={`bf-pay-${p.v}`}
              >
                <Icon className={`h-6 w-6 ${checked ? 'text-sky-700' : 'text-neutral-500'}`} />
                <span className={`text-sm font-semibold leading-tight ${checked ? 'text-sky-900' : 'text-neutral-800'}`}>{p.label}</span>
                <span className="text-xs text-neutral-500 leading-tight">{p.sub}</span>
                {checked && <Check className="h-4 w-4 text-sky-700" style={{ position: 'absolute', top: 6, right: 6 }} />}
                {disabled && (
                  <span className="absolute top-1 right-1 text-[9px] font-bold uppercase bg-neutral-300 text-neutral-700 rounded px-1 py-0.5">
                    nicht möglich
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Section>
      </>
      )}

      {/* Schritt 5 (Wizard): Zusammenfassung + Absenden */}
      {(!wizardMode || currentStep === 4) && (
      <>
      {/* Conflict warning */}
      {conflicts.length > 0 && (
        <section className={`border-2 rounded-2xl p-4 ${adminMode ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`} data-testid="bf-conflicts">
          <h3 className={`font-bold mb-2 ${adminMode ? 'text-amber-800' : 'text-red-700'}`}>⚠️ {adminMode ? 'Konflikt-Hinweis (Admin darf trotzdem buchen)' : 'Termin-Konflikt'}</h3>
          <p className={`text-sm mb-3 ${adminMode ? 'text-amber-800' : 'text-red-700'}`}>
            {adminMode
              ? 'Folgende Produkte sind im gewählten Zeitraum eigentlich blockiert. Du kannst die Buchung als Admin trotzdem anlegen:'
              : 'Folgende Produkte sind im gewählten Zeitraum bereits ausgebucht oder gerade reserviert. Setze Dich auf die Warteliste – wir benachrichtigen Dich per E-Mail, sobald der Termin wieder frei wird:'}
          </p>
          <ul className="space-y-2">
            {conflicts.map((c, i) => (
              <li key={i} className={`flex items-center justify-between gap-3 bg-white border rounded-xl px-3 py-2 ${adminMode ? 'border-amber-200' : 'border-red-200'}`}>
                <span className={`text-sm ${adminMode ? 'text-amber-900' : 'text-red-800'}`}>
                  {c.name} <span className={adminMode ? 'text-amber-600' : 'text-red-500'}>({c.from}{c.to && c.to !== c.from ? ` – ${c.to}` : ''})</span>
                </span>
                {adminMode ? null : waitlistJoined[c.id] ? (
                  <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1 whitespace-nowrap" data-testid={`bf-waitlist-joined-${c.id}`}>
                    <Check className="h-4 w-4" /> Auf Warteliste
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => joinWaitlist(c)}
                    data-testid={`bf-waitlist-btn-${c.id}`}
                    className="text-xs font-semibold text-sky-700 border border-sky-300 hover:bg-sky-50 rounded-full px-3 py-1.5 whitespace-nowrap transition-colors"
                  >
                    Auf Warteliste setzen
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Summary */}
      <section className="bg-sky-50 rounded-2xl p-4 md:p-5 border border-sky-200" data-testid="bf-summary">
        <h3 className="text-base md:text-lg font-bold text-sky-900 mb-2 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 font-bold text-xs flex items-center justify-center">10</span>
          Kostenübersicht
        </h3>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span>Produkte</span><span className="font-medium" data-testid="bf-summary-products">{fmt(productsTotal)}</span></div>
          {/* Phase 125.9 — Aufschlüsselung: JEDES gewählte Produkt einzeln
              anzeigen, damit auch „Preis auf Anfrage"-Items sichtbar sind. */}
          {items.length > 0 && (
            <div className="pl-4 -mt-1 space-y-0.5 text-xs text-neutral-500" data-testid="bf-summary-products-list">
              {items.map((it, i) => {
                if (!it.product) return null;
                const p = it.product;
                const q = Math.max(1, parseInt(it.quantity || 1, 10));
                const dur = Number(it.rental_duration || 1);
                const onReq = !!p.price_on_request;
                const unit = onReq ? 0 : computeUnitPrice(p, dur, form.event_date_from, form.event_date_to, it.with_supervision, it.price_option);
                const sub = unit * q;
                // Phase 128.83 — Fun-Food-Zeilen direkt unter dem Produkt in der Kostenübersicht
                const sel = form.funfood_selections?.[p.id];
                const ffLines = [];
                const hasUpCfg = Array.isArray(p.funfood_upgrades) && p.funfood_upgrades.length > 0;
                if (sel || hasUpCfg) {
                  // Immer eine Zutat-Zeile anzeigen, wenn das Produkt Upgrades hat.
                  // Bei „Normal (inklusive)" zeigen wir „inklusive" statt eines Preises.
                  if (sel && sel.upgrade) {
                    const upAmount = Number(sel.upgrade.price || 0) * q;
                    ffLines.push({
                      key: `up-${p.id}`,
                      label: `⬆ ${sel.upgrade.label}${sel.upgrade.choice ? ` · ${sel.upgrade.choice}` : ''}`,
                      display: upAmount > 0 ? fmt(upAmount) : 'inklusive',
                    });
                  } else if (hasUpCfg) {
                    ffLines.push({
                      key: `up-none-${p.id}`,
                      label: '↺ Zutaten: Normal',
                      display: 'inklusive',
                    });
                  }
                  const extrasCfg = Array.isArray(p.funfood_extras) ? p.funfood_extras : [];
                  for (const [label, qty] of Object.entries(sel?.extras || {})) {
                    const qq = Number(qty) || 0;
                    if (qq <= 0) continue;
                    const cfg = extrasCfg.find((e) => e.label === label);
                    const price = Number(cfg?.price || 0);
                    const amount = price * qq;
                    ffLines.push({
                      key: `ex-${p.id}-${label}`,
                      label: `＋ ${qq}× ${label}`,
                      display: amount > 0 ? fmt(amount) : 'inklusive',
                    });
                  }
                }
                return (
                  <React.Fragment key={`prod-${i}`}>
                    <div className="flex justify-between" data-testid={`bf-summary-product-${i}`}>
                      <span className="truncate">
                        • {p.name}{q > 1 ? ` × ${q}` : ''}{dur > 1 ? ` (${dur} Tage)` : ''}
                      </span>
                      <span>{onReq ? <em className="text-amber-600 not-italic">Preis auf Anfrage</em> : fmt(sub)}</span>
                    </div>
                    {ffLines.length > 0 && (
                      <div className="pl-4 space-y-0.5 text-pink-800" data-testid={`bf-summary-product-funfood-${i}`}>
                        {ffLines.map((l) => (
                          <div key={l.key} className="flex justify-between">
                            <span className="truncate">↳ {l.label}</span>
                            <span className="font-semibold">{l.display}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {accessoriesTotal > 0 && (
            <div className="flex justify-between"><span>Zubehör</span><span className="font-medium" data-testid="bf-summary-accessories">{fmt(accessoriesTotal)}</span></div>
          )}
          {bundlePrice > 0 && (
            <div className="flex justify-between">
              <span>Auf-/Abbau + Endreinigung {bundleUniformUnit != null ? `(${setupUnits}× ${fmt(bundleUniformUnit)})` : `(${setupUnits} Stück)`}</span>
              <span className="font-medium">{fmt(bundlePrice)}</span>
            </div>
          )}
          {cleaningPrice > 0 && (
            <>
              <div className="flex justify-between">
                <span>Endreinigung gesamt{cleaningOverrideActive ? ' (Admin-Override)' : ''}</span>
                <span className="font-medium">{fmt(cleaningPrice)}</span>
              </div>
              {!cleaningOverrideActive && cleaningBreakdown.length > 1 && (
                <div className="pl-4 -mt-1 space-y-0.5 text-xs text-neutral-500">
                  {cleaningBreakdown.map((b, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="truncate">• {b.name}{b.qty > 1 ? ` × ${b.qty}` : ''}</span>
                      <span>{fmt(b.sub)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {betreuungPrice > 0 && (
            <>
              <div className="flex justify-between">
                <span>Betreuungsservice</span>
                <span className="font-medium">{fmt(betreuungPrice)}</span>
              </div>
              {(supItemLines.length > 0 || huepfburgBetreuungPrice > 0) && (
                <div className="pl-4 -mt-1 space-y-0.5 text-xs text-neutral-500">
                  {huepfburgBetreuungPrice > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="truncate">
                          • Hüpfburg-Betreuung · {huepfburgHours}h × {fmt(huepfburgRatePerHour)}/Std.
                          {huepfburgTotalQty > 1 ? ` (${huepfburgStaffCount} Betreuer für ${huepfburgTotalQty} Hüpfburgen)` : ''}
                        </span>
                        <span>{fmt(huepfburgBetreuungPrice)}</span>
                      </div>
                      {huepfburgLines.length > 0 && (
                        <div className="pl-4 text-[11px] text-neutral-400" data-testid="bf-summary-huepfburg-list">
                          Für: {huepfburgLines.map((l) => `${l.product_name}${l.qty > 1 ? ` × ${l.qty}` : ''}`).join(', ')}
                        </div>
                      )}
                    </>
                  )}
                  {supItemLines.filter((l) => l.extra > 0).map((l, i) => (
                    <div key={`sup-${i}`} className="flex justify-between">
                      <span className="truncate">• {l.product_name} · {l.extraH}h zusätzlich × {fmt(l.hourly)}{l.qty > 1 ? ` × ${l.qty}` : ''}</span>
                      <span>{fmt(l.extra)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {kinderschminkenPrice > 0 && (
            <div className="flex justify-between"><span>Kinderschminken</span><span className="font-medium">{fmt(kinderschminkenPrice)}</span></div>
          )}
          {deliveryPrice > 0 && (
            <div className="flex justify-between"><span>{selectedDelivery?.l}{derivedServiceBetreuung ? ' (–50% mit Betreuung)' : ''}</span><span className="font-medium">{fmt(deliveryPrice)}</span></div>
          )}
          {/* Phase 122S — Custom Items (Admin) als eigene Zeilen im Kostenüberblick */}
          {customItemsPreview.map((ci, i) => (
            <div key={`ci-${i}`} className="flex justify-between" data-testid={`bf-summary-custom-${i}`}>
              <span className="truncate">
                {ci.isProduct
                  ? `${ci.qty || 1}× ${ci.label}${ci.days > 1 ? ` (${ci.days} Tage)` : ''}`
                  : ci.label}
              </span>
              <span className="font-medium">{fmt(ci.amount)}</span>
            </div>
          ))}
          {bonusDiscount > 0 && (
            <div className="flex justify-between text-emerald-700" data-testid="bf-bonus-discount-line">
              <span className="font-semibold flex items-center gap-1.5">
                🎫 Sammelkarten-Rabatt
                <span className="text-xs font-normal text-emerald-600/80">({bonusCard?.stamps}/5 Stempel)</span>
              </span>
              <span className="font-bold">– {fmt(bonusDiscount)}</span>
            </div>
          )}
        </div>
        <div className="border-t-2 border-sky-300 mt-3 pt-2.5 flex justify-between items-center">
          <span className="font-bold text-base text-sky-900">Gesamt</span>
          <span className="font-bold text-xl md:text-2xl text-sky-700" data-testid="bf-summary-total">{fmt(total)}</span>
        </div>
        {!adminMode && <p className="text-[11px] text-neutral-500 mt-1.5" data-testid="bf-summary-intent-note">{form.booking_intent === 'binding'
          ? 'Verbindliche Buchung – nach dem Absenden ist Deine Buchung bestätigt (Verfügbarkeit wird beim Absenden final geprüft).'
          : 'Unverbindliche Anfrage – wir senden Dir ein verbindliches Angebot per E-Mail.'}</p>}
      </section>

      {/* Datenschutz — im Admin-Modus ausgeblendet */}
      {!adminMode && (
        <label className="flex items-start gap-3 p-3 rounded-xl bg-white border border-sky-100 cursor-pointer" data-testid="bf-privacy-label">
          <Checkbox checked={form.privacy_accepted} onCheckedChange={(v) => updateField('privacy_accepted', !!v)} className="mt-0.5" data-testid="bf-privacy" />
          <span className="text-sm text-neutral-700">
            Ich habe die <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-sky-600 underline">Datenschutzbestimmungen</a> zur Kenntnis genommen. *
          </span>
        </label>
      )}

      {/* Kundenkonto anlegen (optional, nur für Gäste) */}
      {!customerUser && (
        <label className="flex items-start gap-2 p-2.5 rounded-xl bg-white border border-sky-100 cursor-pointer hover:bg-sky-50/50" data-testid="bf-create-account-label">
          <Checkbox checked={form.create_account} onCheckedChange={(v) => updateField('create_account', !!v)} className="mt-0.5" data-testid="bf-create-account" />
          <span className="text-xs text-neutral-700">
            <strong className="text-sm">Kundenkonto anlegen</strong>
            <span className="block text-[11px] text-neutral-500 leading-snug">Speichert Buchungen, Bonuspunkte und ermöglicht Direktnachrichten.</span>
          </span>
        </label>
      )}

      {/* Buchungs-Intent: Verbindlich vs. Unverbindlich — im Admin-Modus ausgeblendet */}
      {!adminMode && (
      <div className="space-y-2" data-testid="bf-intent-section">
        <div className="text-[15px] font-semibold text-neutral-900">Wie möchtest du fortfahren? *</div>
        {hasPriceOnRequest && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="bf-inquiry-only-notice">
            Mindestens ein Produkt hat „Preis auf Anfrage" – nur unverbindliche Anfragen möglich.
          </div>
        )}
        <div className={`grid gap-2 ${hasPriceOnRequest ? '' : 'sm:grid-cols-2'}`}>
          {!hasPriceOnRequest && (
          <button
            type="button"
            onClick={() => updateField('booking_intent', 'binding')}
            className={`text-left p-3 rounded-xl border-2 transition ${
              form.booking_intent === 'binding'
                ? 'border-emerald-600 bg-emerald-100 shadow-sm'
                : 'border-emerald-200 bg-emerald-50/50 hover:border-emerald-400 hover:bg-emerald-50'
            }`}
            data-testid="bf-intent-binding"
          >
            <div className="flex items-center gap-1.5 mb-0.5">
              <Check className={`h-4 w-4 ${form.booking_intent === 'binding' ? 'text-emerald-700' : 'text-emerald-500'}`} />
              <span className={`font-bold text-[15px] ${form.booking_intent === 'binding' ? 'text-emerald-900' : 'text-emerald-800'}`}>Jetzt verbindlich buchen</span>
            </div>
            <p className="text-[13px] text-emerald-900/80 leading-snug">
              Du erhältst Buchungsbestätigung und Mietvertrag per E-Mail.
            </p>
          </button>
          )}
          <button
            type="button"
            onClick={() => updateField('booking_intent', 'inquiry')}
            className={`text-left p-3 rounded-xl border-2 transition ${
              form.booking_intent === 'inquiry'
                ? 'border-amber-500 bg-amber-100 shadow-sm'
                : 'border-amber-200 bg-amber-50/50 hover:border-amber-400 hover:bg-amber-50'
            }`}
            data-testid="bf-intent-inquiry"
          >
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className={`text-base leading-none ${form.booking_intent === 'inquiry' ? 'text-amber-700' : 'text-amber-500'}`}>💬</span>
              <span className={`font-bold text-[15px] ${form.booking_intent === 'inquiry' ? 'text-amber-900' : 'text-amber-800'}`}>Unverbindlich anfragen</span>
            </div>
            <p className="text-[13px] text-amber-900/80 leading-snug">
              Wir melden uns, klären offene Fragen und senden ein Angebot.
            </p>
          </button>
        </div>
      </div>
      )}

      {/* Empfehlungs-Code (klein & schlicht, am Ende, optional aufklappbar) */}
      {!adminMode && (
      <div className="text-center" data-testid="bf-referral-block">
        {!showReferralInput ? (
          <button
            type="button"
            onClick={() => setShowReferralInput(true)}
            className="text-xs text-neutral-500 hover:text-sky-700 underline underline-offset-4"
            data-testid="bf-referral-toggle"
          >
            Empfehlungs-Code eingeben?
          </button>
        ) : (
          <div className="max-w-sm mx-auto text-left">
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="text-xs font-semibold text-neutral-700 flex items-center gap-1">
                🎁 Empfehlungs-Code
              </label>
              <button
                type="button"
                onClick={() => { setShowReferralInput(false); updateField('referral_code', ''); }}
                className="text-[11px] text-neutral-400 hover:text-neutral-700"
                data-testid="bf-referral-collapse"
              >
                ausblenden
              </button>
            </div>
            <input
              type="text"
              value={form.referral_code || ''}
              onChange={(e) => updateField('referral_code', e.target.value.toUpperCase())}
              placeholder="KIDS-XXXXXX"
              maxLength={16}
              className="w-full text-sm rounded-xl border border-neutral-300 bg-white px-3 py-2 font-mono uppercase tracking-wider focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none"
              data-testid="bf-referral-input"
            />
            {referralValidation && form.referral_code && (
              referralValidation.valid ? (
                <div className="mt-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5" data-testid="bf-referral-valid">
                  ✓ Gültig — Du sparst <strong>{fmt(referralValidation.discount_eur)}</strong>
                </div>
              ) : (
                <div className="mt-1.5 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5" data-testid="bf-referral-invalid">
                  {referralValidation.reason || 'Code ungültig'}
                </div>
              )
            )}
          </div>
        )}
      </div>
      )}

      {/* Unverbindlichkeits-Hinweis + Tipp — nur bei "Unverbindliche Anfrage", ganz unten (nicht im Admin) */}
      {!adminMode && form.booking_intent === 'inquiry' && (
        <section
          className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-2.5"
          data-testid="bf-availability-disclaimer"
        >
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900 leading-relaxed">
              <strong>Hinweis:</strong> Eine unverbindliche Anfrage reserviert Deinen Wunschtermin noch nicht.
              Bis wir Deine Anfrage bestätigt haben und alles geklärt ist, kann der Termin von einem anderen
              Kunden verbindlich gebucht werden. Erst mit unserer Buchungsbestätigung ist Dein Termin
              verbindlich reserviert.
            </p>
          </div>
          {!hasPriceOnRequest && (
            <div className="flex items-start gap-3 border-t border-amber-200 pt-2.5">
              <span className="text-base leading-none mt-0.5">💡</span>
              <p className="text-sm text-amber-900 leading-relaxed">
                <strong>Tipp:</strong> Du möchtest auf Nummer sicher gehen? Wähle oben{' '}
                <button
                  type="button"
                  onClick={() => updateField('booking_intent', 'binding')}
                  className="font-bold text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
                  data-testid="bf-switch-to-binding"
                >
                  „Verbindlich buchen"
                </button>
                . So ist Dein Wunschtermin und Produkt sofort für Dich reserviert.
              </p>
            </div>
          )}
        </section>
      )}

      {/* Admin-only: Kunden per E-Mail informieren? */}
      {adminMode && (
        <label className="flex items-start gap-3 p-3 rounded-xl bg-sky-50 border-2 border-sky-200 cursor-pointer" data-testid="bf-notify-customer-label">
          <Checkbox
            checked={!!form.notify_customer}
            onCheckedChange={(v) => updateField('notify_customer', !!v)}
            className="mt-0.5"
            data-testid="bf-notify-customer"
          />
          <span className="text-sm text-neutral-800">
            <strong>Kunden per E-Mail benachrichtigen</strong>
            <span className="block text-xs text-neutral-600 mt-0.5">
              {form.notify_customer
                ? `Der Kunde erhält direkt nach dem Anlegen eine Buchungsbestätigung an ${form.customer_email || 'die hinterlegte E-Mail-Adresse'}.`
                : 'Es wird KEINE E-Mail an den Kunden versendet (nur intern erfasst).'}
            </span>
          </span>
        </label>
      )}

      {/* Phase 125.14 — Admin-only: Buchung sofort als bezahlt markieren */}
      {adminMode && !editBooking && (
        <div className="p-3 rounded-xl bg-emerald-50 border-2 border-emerald-200" data-testid="bf-already-paid-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={!!form.already_paid}
              onCheckedChange={(v) => updateField('already_paid', !!v)}
              className="mt-0.5"
              data-testid="bf-already-paid"
            />
            <span className="text-sm text-neutral-800 flex-1">
              <strong>Kunde hat bereits bezahlt</strong>
              <span className="block text-xs text-neutral-600 mt-0.5">
                Buchung wird sofort als bezahlt markiert (Ampel grün, keine offene Rechnung).
              </span>
            </span>
          </label>
          {form.already_paid && (
            <div className="mt-2 pl-8 flex flex-wrap items-center gap-2" data-testid="bf-already-paid-method">
              <Label className="text-xs text-neutral-700 font-semibold">Zahlungsart:</Label>
              {[
                { key: 'bar', label: 'Bar' },
                { key: 'überweisung', label: 'Überweisung' },
                { key: 'paypal', label: 'PayPal' },
                { key: 'rechnung', label: 'Rechnung (extern beglichen)' },
              ].map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => updateField('already_paid_method', m.key)}
                  className={`text-xs px-3 py-1 rounded-full font-semibold border ${
                    form.already_paid_method === m.key
                      ? 'bg-emerald-600 text-white border-emerald-700'
                      : 'bg-white text-neutral-700 border-neutral-300 hover:bg-emerald-50'
                  }`}
                  data-testid={`bf-already-paid-method-${m.key}`}
                >{m.label}</button>
              ))}
            </div>
          )}
        </div>
      )}

      <Button type="submit" disabled={submitting || !!notYetBookable} className="w-full bg-yellow-400 hover:bg-yellow-500 text-sky-900 font-bold rounded-full py-6 text-base lift-on-hover disabled:opacity-60 disabled:cursor-not-allowed" data-testid="bf-submit">
        {submitting ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Wird gesendet...</> : <><Check className="h-5 w-5 mr-2" /> {editBooking ? 'Änderungen speichern' : offerMode ? (offerInitial?.id ? 'Angebot final speichern' : 'Angebot final speichern') : adminMode ? 'Buchung anlegen' : (form.booking_intent === 'binding' ? 'Verbindlich buchen' : form.booking_intent === 'inquiry' ? 'Unverbindlich anfragen' : 'Anfrage absenden')}</>}
      </Button>

      {/* Phase 122S / 128.53 — „Als Entwurf speichern" für Admin-Neuanlage UND Angebots-Modus. */}
      {((adminModeProp && !editBooking && !offerMode) || offerMode) && (
        <Button
          type="button"
          onClick={(e) => submit(e, { draft: true })}
          disabled={submitting}
          variant="outline"
          className="w-full mt-2 border-2 border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 rounded-full py-5 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="bf-save-draft"
        >
          Als Entwurf speichern (später weiter bearbeiten)
        </Button>
      )}

      {/* Phase 90 — Missing-Fields Panel: erscheint nur nach fehlgeschlagenem Submit, mit Sprung-Links */}
      {missingFields.length > 0 && (
        <div
          ref={missingPanelRef}
          className="rounded-2xl border-2 border-red-300 bg-red-50 p-4 space-y-2 shadow-lg"
          role="alert"
          data-testid="bf-missing-panel"
        >
          <div className="flex items-start gap-2">
            <span className="text-xl leading-none">⚠️</span>
            <div className="flex-1">
              <div className="font-bold text-red-800 text-sm">
                Bitte ergänze noch {missingFields.length === 1 ? 'diese Angabe' : `diese ${missingFields.length} Angaben`}:
              </div>
              <ul className="mt-2 space-y-1.5">
                {missingFields.map((f, i) => (
                  <li key={f.key} className="flex items-start gap-2">
                    <span className="text-red-600 font-bold text-sm shrink-0">{i + 1}.</span>
                    <button
                      type="button"
                      onClick={() => scrollToAnchor(f.anchor)}
                      className="text-left text-sm text-red-800 hover:text-red-900 underline underline-offset-2 decoration-red-400 hover:decoration-red-700"
                      data-testid={`bf-missing-item-${f.key}`}
                    >
                      {f.label} <span className="text-[11px] text-red-500 font-semibold">→ zum Feld springen</span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-[11px] text-red-700">
                Klicke auf einen Eintrag, um direkt zum Feld zu springen.
              </div>
            </div>
          </div>
        </div>
      )}
      </>
      )}

      {/* ============ WIZARD NAV FOOTER ============ */}
      {wizardMode && (
        <div className="sticky bottom-0 -mx-5 md:-mx-8 -mb-5 md:-mb-8 bg-white/95 backdrop-blur border-t border-sky-100 px-5 md:px-8 py-3 rounded-b-3xl flex items-center gap-3 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.1)]" data-testid="wizard-nav-footer">
          <button
            type="button"
            onClick={goPrev}
            disabled={currentStep === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="wizard-prev"
          >
            ← Zurück
          </button>
          <div className="flex-1 text-xs text-neutral-500 text-center hidden sm:block" data-testid={stepError ? 'wizard-step-error' : 'wizard-step-hint'}>
            {stepError || (currentStep < WIZARD_STEPS.length - 1 ? `Weiter zu „${WIZARD_STEPS[currentStep + 1].title}"` : 'Prüfe deine Angaben und sende die Anfrage ab')}
          </div>
          {currentStep < WIZARD_STEPS.length - 1 ? (
            <button
              type="button"
              onClick={goNext}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white shadow-sm"
              data-testid="wizard-next"
            >
              Weiter →
            </button>
          ) : (
            <span className="text-xs text-neutral-500 hidden sm:inline">↓ scroll für Absenden</span>
          )}
        </div>
      )}
    </form>
  );
};

export default BookingForm;
